import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { rehabFinancingOnPurchaseToggle } from '../lib/rehabFinancingInheritance';
import { financingSeedFromAnalysis } from '../lib/financingFromDocument';
import { impliedPurchaseLtv, effectiveDownPayment } from '../lib/purchaseLtv';
import { rehabRequestFields } from '../lib/rehabPayload';
import { dealsAPI, analysisAPI, investmentCriteriaAPI, shareAPI, recentAnalysesAPI, analyticsAPI, compsAPI, subscriptionAPI } from '../api';
import { markGuestRecentAsSaved, updateGuestRecentState } from '../utils/guestRecentAnalyses';
import { buildRerunRequest } from '../utils/rerunRequest';
import { 
  buildCanonicalState, 
  serializeForShare, 
  serializeForPDF,
  serializeForEmail,
  logCanonicalChecksum,
  getCanonicalDecision 
} from '../utils/canonicalStateSerializer';
import Header from '../components/Header';
import ComparableManager from '../components/ComparableManager';
import DataCoverageWarning from '../components/DataCoverageWarning';
import InvestmentCriteriaOnboarding from '../components/InvestmentCriteriaOnboarding';
import InvestmentCriteriaSettings from '../components/InvestmentCriteriaSettings';
import ShareModal from '../components/ShareModal';
import PDFPreviewModal from '../components/PDFPreviewModal';
import AuthPrompt from '../components/AuthPrompt';
import DealClassificationCard from '../components/DealClassificationCard';
import ClassificationCriteriaPanel from '../components/ClassificationCriteriaPanel';
import UsageDisplay from '../components/UsageDisplay';
import FeedbackPrompt from '../components/FeedbackPrompt';
import UpgradePrompt from '../components/UpgradePrompt';
import CollapsibleSection from '../components/CollapsibleSection';
import MinimalDecisionView from '../components/MinimalDecisionView';  // V5.0: Minimal Decision View
import UserCompOutlierAlert from '../components/UserCompOutlierAlert';  // F: a comp that does not fit, named under the verdict
import UserCompSizeNote from '../components/UserCompSizeNote';           // F: a comp much bigger/smaller — a note, not a penalty
import RecommendedHint from '../components/RecommendedHint';
import { financialTextColor, financialCardColor } from '../utils/financialColor';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChanges';
// ARVExplanationPanel removed - functionality consolidated into ComparableManager
import { FormattedCurrencyInput, FormattedNumberInput } from '../components/FormattedInput';
import { parseFormattedNumber } from '../utils/formatNumber';
import { computeLiveRental } from '../utils/rentalMath';
import { arvSourceNote } from '../utils/arvSourceNote';
import { arvAlarmReason, arvAlarmSeverity, arvAlarmContent, arvNotConfirmedMark } from '../utils/arvAlarm';
import { exclusionText, orderExcluded, splitExcluded, EXCLUDED_ROWS_SHOWN } from '../utils/exclusionReason';
import { TrendingUp, DollarSign, Home as HouseIcon, Save, ArrowRight, ArrowLeft, AlertTriangle, CheckCircle2, TrendingDown, Share2, MapPin, Calendar, Ruler, Settings2, RefreshCw, ChevronDown, ChevronUp, Wrench, FileText, Download, Loader2, Mail, X, Send, HelpCircle, Lightbulb, Info, ShieldCheck, Edit3, RotateCcw, Building2, AlertCircle, Target, Link, Plus, Minus, Check, XCircle, CheckCircle, Eye, BarChart3, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_RENTAL_ORIGINATION_POINTS, DEFAULT_FLIP_ORIGINATION_POINTS } from '../utils/financingDefaults';

// Helper: Check if user has access to a feature
const canUseFeature = (user, feature) => {
  if (!user) return false;
  if (user.is_founder) return true;
  return user.features?.[feature] === true || user.features?.[feature] === 'limited';
};

/**
 * Format currency as whole dollars (no cents)
 * @param {number} value - The numeric value to format
 * @returns {string} Formatted string with commas, no decimal places
 */
const formatWholeDollars = (value) => {
  if (value === null || value === undefined) return '0';
  // PROBLEM 1: tolerate a formatted string ("1,500,000") — strip $/commas rather than
  // letting isNaN("1,500,000")===true collapse it to '0' or a plain Number() give NaN.
  const num = typeof value === 'string'
    ? parseFloat(value.replace(/[$,\s]/g, ''))
    : value;
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Math.round(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

// PER-MODE FINANCING: these are the ONLY inputs that belong to a specific financing mode
// (Cash keeps its own — empty/NA — and Financed keeps its own terms). Everything else
// (rehab, price, taxes, insurance, closing, holding, beds/baths/sqft, ARV, commission,
// target rule) is SHARED across both modes. Down payment mode/amount ride along per-mode.
// ARV_CONFIDENCE_ALARM_THRESHOLD and the whole decision of whether to warn now live in
// utils/arvAlarm.js, imported above — the rule is read from two places on this screen
// (the alarm box and the mark beside the ARV figure) and is tested there as arithmetic.

const FINANCING_KEYS = [
  'purchaseLTV', 'purchaseInterestRate', 'purchaseLoanPoints', 'purchaseLenderFees',
  'rehabFinancedPct', 'rehabInterestRate', 'rehabLoanPoints',
];
const pickFinancing = (a = {}) => FINANCING_KEYS.reduce((o, k) => { o[k] = a[k]; return o; }, {});

/**
 * Mobile-friendly numeric input with +/- buttons
 * Designed to work consistently across all devices including iPhone Safari
 */
const NumericInputWithButtons = ({ 
  value, 
  onChange, 
  min, 
  max, 
  step = 1, 
  label, 
  originalValue,
  testId,
  lang = 'en',
  suffix = '',
  formatValue = (v) => v
}) => {
  const currentValue = value ?? '';
  // PROBLEM 1: strip $/commas so a formatted value doesn't truncate at the first comma.
  const numericValue = parseFloat(String(currentValue).replace(/[$,\s]/g, '')) || 0;
  
  const handleIncrement = () => {
    const newValue = Math.min(numericValue + step, max);
    // Determine decimal places based on step value (0.01 -> 2, 0.5 -> 1, 1 -> 0)
    const decimalPlaces = step < 1 ? Math.max(1, -Math.floor(Math.log10(step))) : 0;
    onChange(decimalPlaces > 0 ? newValue.toFixed(decimalPlaces) : Math.round(newValue).toString());
  };
  
  const handleDecrement = () => {
    const newValue = Math.max(numericValue - step, min);
    // Determine decimal places based on step value (0.01 -> 2, 0.5 -> 1, 1 -> 0)
    const decimalPlaces = step < 1 ? Math.max(1, -Math.floor(Math.log10(step))) : 0;
    onChange(decimalPlaces > 0 ? newValue.toFixed(decimalPlaces) : Math.round(newValue).toString());
  };
  
  const handleInputChange = (e) => {
    onChange(e.target.value);
  };
  
  return (
    <div>
      <label className="text-sm text-muted-foreground mb-1.5 block flex items-center justify-between">
        <span>{label}</span>
        {originalValue !== undefined && originalValue !== null && (
          <span className="text-xs text-slate-400">
            ({lang === 'en' ? 'Orig' : 'Orig'}: {formatValue(originalValue)}{suffix})
          </span>
        )}
      </label>
      <div className="flex items-center gap-1">
        {/* Decrement Button */}
        <button
          type="button"
          onClick={handleDecrement}
          onTouchEnd={(e) => {
            e.preventDefault();
            handleDecrement();
          }}
          disabled={numericValue <= min}
          className="flex-shrink-0 w-10 h-10 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border-2 border-border bg-slate-50 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch-manipulation"
          data-testid={`${testId}-decrement`}
          aria-label={`Decrease ${label}`}
        >
          <Minus className="w-4 h-4 text-slate-600" />
        </button>
        
        {/* Input Field */}
        <input
          type="number"
          value={currentValue}
          onChange={handleInputChange}
          min={min}
          max={max}
          step={step}
          className="flex-1 min-w-0 px-2 py-2 sm:py-1.5 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm text-center"
          data-testid={testId}
          inputMode="numeric"
        />
        
        {/* Increment Button */}
        <button
          type="button"
          onClick={handleIncrement}
          onTouchEnd={(e) => {
            e.preventDefault();
            handleIncrement();
          }}
          disabled={numericValue >= max}
          className="flex-shrink-0 w-10 h-10 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border-2 border-border bg-slate-50 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch-manipulation"
          data-testid={`${testId}-increment`}
          aria-label={`Increase ${label}`}
        >
          <Plus className="w-4 h-4 text-slate-600" />
        </button>
      </div>
    </div>
  );
};

/**
 * Data Freshness Indicator Component
 * Shows when market data was last updated and provides manual refresh option
 * V5.0: Only show stale indicator if BOTH the analysis is old AND the cache is stale
 */
const DataFreshnessIndicator = ({ fetchedAt, isCached, onRefresh, lang, isRefreshing, analysisCreatedAt }) => {
  // V5.0: If the analysis was just created (within last 5 minutes), don't show "X hours ago"
  // This prevents confusing users who just ran a fresh analysis
  const isRecentAnalysis = () => {
    if (!analysisCreatedAt) return false;
    const created = new Date(analysisCreatedAt);
    const now = new Date();
    const diffMinutes = (now - created) / (1000 * 60);
    return diffMinutes < 5; // Analysis created within last 5 minutes is "recent"
  };
  
  // Calculate time since data was fetched
  const getTimeSince = () => {
    // V5.0: For recent analyses, show "Fresh" regardless of cache age
    if (isRecentAnalysis()) {
      return null; // Will display "Fresh"
    }
    
    if (!fetchedAt) return null;
    
    const fetched = new Date(fetchedAt);
    const now = new Date();
    const diffMs = now - fetched;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return lang === 'en' ? `${diffDays}d ago` : `hace ${diffDays}d`;
    } else if (diffHours > 0) {
      return lang === 'en' ? `${diffHours}h ago` : `hace ${diffHours}h`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes > 0) {
        return lang === 'en' ? `${diffMinutes}m ago` : `hace ${diffMinutes}m`;
      }
      return lang === 'en' ? 'Just now' : 'Ahora';
    }
  };
  
  const timeSince = getTimeSince();
  
  return (
    <>
      <span>•</span>
      <span 
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600 cursor-pointer hover:bg-slate-200 transition-colors group"
        data-testid="data-freshness-indicator"
        title={lang === 'en' 
          ? `Market data ${isCached ? 'from cache' : 'freshly fetched'}. Click to refresh.` 
          : `Datos de mercado ${isCached ? 'del caché' : 'recién obtenidos'}. Click para actualizar.`}
      >
        <Calendar className="w-3 h-3" />
        <span>
          {lang === 'en' ? 'Data: ' : 'Datos: '}
          {timeSince || (lang === 'en' ? 'Fresh' : 'Reciente')}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRefresh();
          }}
          disabled={isRefreshing}
          className="ml-1 p-0.5 rounded hover:bg-slate-300 transition-colors disabled:opacity-50"
          title={lang === 'en' ? 'Refresh market data' : 'Actualizar datos de mercado'}
          data-testid="refresh-market-data-btn"
        >
          <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : 'group-hover:text-blue-600'}`} />
        </button>
      </span>
    </>
  );
};

// G3 — ONE AUTHORITY FOR "IS THIS ARV RELIABLE". The document carries a record per mode
// (arv_by_mode.<mode>: arv, arv_details, verdict_reliable, comps...) and a top-level copy
// of the active one. Wherever the two can disagree — a document written before the copy
// existed, a write that landed out of order, a merge that kept a previous field — the
// per-mode record wins: it is what the engine computed FOR that mode. The header gate,
// the "needs renovated comps" panel and the tier note read this, never a top-level
// arv_status that may belong to another pool. 3305 West 33rd: User Only with
// verdict_reliable true and profit computed, and the header on "Add comps" because the
// top-level arv_details still said NO_RENOVATED_COMPS from the system pool.
export const withActiveModeVoice = (doc) => {
  if (!doc || typeof doc !== 'object') return doc;
  const mode = doc.active_arv_mode;
  const rec = mode && doc.arv_by_mode && doc.arv_by_mode[mode];
  if (!rec || typeof rec !== 'object') return doc;
  const out = { ...doc };
  for (const k of ['arv', 'arv_confidence', 'arv_confidence_score', 'arv_details', 'comps_used', 'comps_excluded',
                   'system_comps_used', 'user_comps_used', 'verdict_reliable']) {
    if (rec[k] !== undefined && rec[k] !== null) out[k] = rec[k];
  }
  if (Array.isArray(out.comps_used)) out.comparable_count = out.comps_used.length;
  const fin = rec.financials;
  if (fin && typeof fin === 'object') {
    for (const k of ['flip_analysis', 'rental_analysis', 'rental_classification', 'deal_decision', 'deal_classification',
                     'deal_score', 'deal_rating', 'deal_explanation', 'risk_factors', 'opportunities', 'rehab_estimate', 'rehab_breakdown']) {
      if (fin[k] !== undefined && fin[k] !== null) out[k] = fin[k];
    }
  }
  return out;
};

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { t, lang } = useTranslation();
  
  // V5.0: Get analysis_id from URL params - THIS IS REQUIRED
  const { analysisId: urlAnalysisId } = useParams();

  // PROBLEM 1 — EDITS PERSIST ACROSS NAVIGATION. Every in-progress edit (property
  // details, assumptions, financing, rehab, price) is saved to a per-analysis-id
  // sessionStorage key and restored when the user returns to the SAME deal — so a
  // navigate-away/return never reverts to the original. Keyed by analysis_id so it can
  // never bleed into a different deal, and a fresh run (new id) has no working state.
  const WORKING_KEY = urlAnalysisId && urlAnalysisId !== 'new' ? `chekdeal_working_${urlAnalysisId}` : null;
  const getWorkingState = () => {
    try {
      if (!WORKING_KEY) return null;
      const raw = sessionStorage.getItem(WORKING_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const _workingState = getWorkingState();

  // V5.0 STRICT FIX: Results NEVER auto-loads from sessionStorage
  // Results REQUIRES either:
  //   A) Valid analysis_id in URL, OR
  //   B) Explicit saved-analysis action (from History/Saved)
  // Otherwise: redirect to Analyzer
  
  const getInitialAnalysis = () => {
    // CASE 0: Return from "Edit My Criteria" — restore the FULL working deal (the user's
    // in-progress rehab/price/financing edits) instead of the stale nav state (CASE 1) or
    // a backend re-fetch of the original (CASE 2). Consumed once. The new criteria are
    // re-read from localStorage on mount and re-evaluated (see criteria-return effect).
    try {
      const criteriaReturn = sessionStorage.getItem('chekdeal_criteria_return_state');
      if (criteriaReturn) {
        const savedId = sessionStorage.getItem('chekdeal_criteria_return_id');
        const idMatches = !urlAnalysisId || urlAnalysisId === 'new' || !savedId
          || savedId === 'unsaved' || savedId === String(urlAnalysisId);
        if (idMatches) {
          sessionStorage.removeItem('chekdeal_criteria_return_state');
          sessionStorage.removeItem('chekdeal_criteria_return_id');
          const parsed = JSON.parse(criteriaReturn);
          parsed._fromSessionStorage = false;
          parsed._isFreshFromFlow = false;
          parsed._sourceType = 'criteria_return';
          parsed._criteriaReturn = true;  // triggers a re-evaluate against the new criteria
          console.log('[Results] Restored working deal from Edit My Criteria return');
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to restore criteria return state:', e);
    }

    // CASE 0.5: PROBLEM 1 — restore the user's edited working deal for THIS analysis id.
    // Takes precedence over the stale nav-state (CASE 1) and the backend re-fetch of the
    // original (CASE 2) so navigating away and back keeps every edit. Only fires when a
    // working state exists for this exact id (a fresh run has none).
    if (_workingState && _workingState.analysis) {
      const wId = _workingState.analysis.analysis_id || _workingState.analysis.id;
      if (String(wId) === String(urlAnalysisId)) {
        const wa = _workingState.analysis;
        wa._fromSessionStorage = false;
        wa._isFreshFromFlow = false;
        wa._sourceType = 'working_state_restore';
        console.log('[Results] Restored edited working deal from sessionStorage:', urlAnalysisId);
        return wa;
      }
    }

    // CASE 1: Fresh analysis from navigation state with URL analysis_id
    // This is the PRIMARY path for fresh Analyze flow
    if (urlAnalysisId && urlAnalysisId !== 'new' && location.state?.analysis) {
      const navAnalysis = location.state.analysis;
      const navId = navAnalysis.analysis_id || navAnalysis.id;

      if (navId === urlAnalysisId) {
        console.log('[Results] V5.0 FRESH: URL + navigation match:', urlAnalysisId);
        navAnalysis._isFreshFromFlow = true;
        navAnalysis._fromSessionStorage = false;
        navAnalysis._sourceType = 'fresh_analysis';
        return navAnalysis;
      }
    }
    
    // CASE 2: URL has analysis_id but no navigation state
    // This happens on page refresh or direct URL access
    // We'll fetch from backend in useEffect
    if (urlAnalysisId && urlAnalysisId !== 'new') {
      console.log('[Results] V5.0: URL has analysis_id, will fetch from backend:', urlAnalysisId);
      return null; // Return null, useEffect will fetch
    }
    
    // CASE 3: Explicit saved analysis from navigation (History/Saved page)
    if (location.state?.fromSavedAnalysis && location.state?.analysis) {
      console.log('[Results] V5.0: Explicit saved analysis loaded');
      const savedAnalysis = location.state.analysis;
      savedAnalysis._fromSessionStorage = false;
      savedAnalysis._isFreshFromFlow = false;
      savedAnalysis._sourceType = 'saved_analysis';
      return savedAnalysis;
    }
    
    // CASE 4: Return from Compare
    try {
      const compareReturnState = sessionStorage.getItem('chekdeal_compare_return_state');
      if (compareReturnState) {
        console.log('[Results] V5.0: Restored from Compare return');
        sessionStorage.removeItem('chekdeal_compare_return_state');
        const parsed = JSON.parse(compareReturnState);
        parsed._fromSessionStorage = false;
        parsed._sourceType = 'compare_return';
        return parsed;
      }
    } catch (e) {
      console.warn('Failed to restore from compare:', e);
    }
    
    // CASE 5: Legacy /results/new with navigation state (backwards compat)
    if (urlAnalysisId === 'new' && location.state?.analysis) {
      console.log('[Results] V5.0: Legacy /results/new with state');
      const analysis = location.state.analysis;
      analysis._isFreshFromFlow = true;
      analysis._fromSessionStorage = false;
      analysis._sourceType = 'legacy_new';
      return analysis;
    }
    
    // CASE 6: recover the last analysis from sessionStorage and MARK IT STALE.
    //
    // This is the "you left and came back" case, and it is the only one that sets
    // _fromSessionStorage = true — which is what renders the saved-session banner and
    // its Re-run button. Commit 7494769 rewrote this loader into the CASE chain above
    // and dropped this branch, so the flag was assigned `false` in every remaining
    // path and `true` in none. The banner and the button had been unreachable ever
    // since; restoring the branch is what makes them appear again.
    //
    // It cannot swallow a fresh analysis: CASE 1 returns on a URL id + nav state, and
    // CASE 2 returns null (deferring to the backend fetch) for ANY url id, so a real
    // analysis never falls through to here. Reaching this point means no id, no nav
    // state and no working state — nothing but what the last session left behind.
    try {
      const saved = sessionStorage.getItem('chekdeal_last_analysis');
      if (saved) {
        const parsed = JSON.parse(saved);
        const freshId = sessionStorage.getItem('chekdeal_fresh_analysis_id');
        const savedId = parsed.analysis_id || parsed.id;

        // Belt and braces: if this IS still the run the user just did, treat it as a
        // refresh rather than as stale, so they are not warned about their own work.
        if (freshId && savedId && String(freshId) === String(savedId) && parsed._isFreshFromFlow) {
          console.log('[Results] Page-refresh recovery of the current fresh analysis:', savedId);
          parsed._fromSessionStorage = false;
          parsed._sourceType = 'fresh_refresh';
          return parsed;
        }

        console.log('[Results] Recovered STALE analysis from sessionStorage:', savedId);
        parsed._fromSessionStorage = true;   // -> saved-session banner + Re-run button
        parsed._isFreshFromFlow = false;
        parsed._sourceType = 'stale_session';
        return parsed;
      }
    } catch (e) {
      console.warn('Failed to recover analysis from sessionStorage:', e);
    }

    // NO VALID SOURCE - DO NOT AUTO-LOAD FROM SESSIONSTORAGE
    // This prevents stale data from appearing
    console.log('[Results] V5.0: NO VALID SOURCE - will redirect to Analyzer');
    return null;
  };
  
  // PERSISTENCE FIX: Also recover assumptions from sessionStorage
  const getInitialAssumptions = (analysisData) => {
    try {
      const savedAssumptions = sessionStorage.getItem('chekdeal_last_assumptions');
      if (savedAssumptions) {
        const parsed = JSON.parse(savedAssumptions);
        // Only restore if same property address - prevents stale data from previous analysis
        const savedAddress = parsed._address || '';
        const currentAddress = analysisData?.address || analysisData?.property_address || '';
        if (savedAddress && currentAddress && savedAddress === currentAddress) {
          console.log('[Results] Recovered assumptions from sessionStorage (same property)');
          return parsed;
        } else {
          console.log('[Results] Skipping sessionStorage assumptions (different property)');
        }
      }
    } catch (e) {
      console.warn('Failed to recover assumptions from sessionStorage:', e);
    }
    return null; // Return null to use defaults
  };
  
  const originalAnalysis = getInitialAnalysis();
  const savedAssumptions = getInitialAssumptions(originalAnalysis);

  // The state to restore edits from: the per-tab working state (PROBLEM 1) OR the FULL
  // working state embedded in a SAVED deal (analysis_data._saved_working_state) when
  // reopening from the Saved list. Either restores assumptions / overrides / price / mode
  // exactly as last left/saved.
  const _restoredWork = _workingState || originalAnalysis?._saved_working_state || null;
  
  // V4.5: Track if showing persisted data that might be stale
  const [isFromSessionStorage, setIsFromSessionStorage] = useState(originalAnalysis?._fromSessionStorage || false);
  
  // Check if analysis came from PropertyConfirmation (has confirmed overrides baked in).
  //
  // BUG 5: `location.state` SURVIVES a back-navigation. The fresh-analysis navigate sets
  // fromConfirmation:true with replace:true, so that flag was still true after returning
  // from "Edit My Criteria" — and every `!fromConfirmation` guard below then threw away
  // the user's edited working state and re-seeded from the original confirmation inputs.
  // Edits appeared to survive only where they happened to be re-derivable from the
  // recalculated rental_analysis (taxes); anything read straight from location.state
  // (the acquisition fees) silently reverted to what was typed before the analysis ran.
  //
  // A working state exists for this analysis id only AFTER Results has already mounted
  // and captured the user's state, so its presence is exactly the signal that this is a
  // RE-entry rather than the original fresh run.
  const fromConfirmation = location.state?.fromConfirmation === true && !_restoredWork;
  const initialFinancingMode = location.state?.initialFinancingMode || null;
  // Same choice expressed in the rental vocabulary. The rental engine speaks
  // 'cash' | 'dscr'; the confirm-page toggle speaks 'cash' | 'financed'.
  const initialRentalFinancingType = initialFinancingMode
    ? (initialFinancingMode === 'cash' ? 'cash' : 'dscr')
    : null;
  
  const [analysis, setAnalysis] = useState(originalAnalysis);

  // A FROZEN EXAMPLE. The two samples on the Analyzer page arrive here marked
  // `is_sample` from GET /api/sample-deals/{kind} — a file, not an analysis: no owner,
  // no quota, no live data. Every handler below that would recompute (assumptions,
  // property overrides, comps, refresh, re-run) or persist (save, share, email, recent
  // history) starts with `sampleBlocked()`. Guarding the HANDLER, not the button, is
  // what makes this hold: whatever calls it, the request never leaves. If an action
  // slipped through, the example would go live again — RentCast on the owner's key and
  // the user's quota — which is the bug this exists to close.
  const isSample = analysis?.is_sample === true;
  const sampleBlocked = useCallback(() => {
    if (!isSample) return false;
    toast.info(lang === 'en'
      ? 'This is an example. Analyze a property of your own to edit, recalculate or save.'
      : 'Esto es un ejemplo. Analiza una propiedad tuya para editar, recalcular o guardar.');
    return true;
  }, [isSample, lang]);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [rerunningAnalysis, setRerunningAnalysis] = useState(false); // V4.5: For re-running stale analysis
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showAllExcluded, setShowAllExcluded] = useState(false);  // the set-aside comps list, 15 rows by default
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);  // V5.0: Full analysis hidden by default
  
  // Current ARV tracking - allows ARV mode switching to update deal analysis
  const [currentARV, setCurrentARV] = useState(originalAnalysis?.arv || 0);
  // USER ARV OVERRIDE. null = show OUR comp-derived estimate. A number = the user's own
  // figure (their appraisal, a negotiated price). Kept separate from currentARV so we can
  // always say which of the two is on screen — an estimate presented as ours when it is
  // the user's is the one thing here that could mislead a lender.
  // The user-typed ARV override is GONE. It bypassed the whole engine — every filter,
  // the comp funnel, the size adjustment, the livability floor — and produced a number
  // with no evidence behind it that then drove profit, margin, MAO and the verdict.
  // Adding comparables replaces it: the user supplies evidence, the engine processes it.
  //
  // The saved field is still READ so an older analysis carrying one opens without
  // throwing; it is never written and never displayed as the user's figure. Nothing in
  // the current data has one.
  const userArvOverride = null;
  const [currentARVConfidence, setCurrentARVConfidence] = useState(originalAnalysis?.arv_confidence || 'moderate');
  const [currentARVConfidenceScore, setCurrentARVConfidenceScore] = useState(originalAnalysis?.arv_confidence_score || 50);
  const [arvModeLabel, setArvModeLabel] = useState(null); // 'System Only', 'User Only', 'Blended', or null
  
  // Investment Criteria state
  const [investmentCriteria, setInvestmentCriteria] = useState(null);
  const [showCriteriaOnboarding, setShowCriteriaOnboarding] = useState(false);
  const [showCriteriaSettings, setShowCriteriaSettings] = useState(false);
  const [criteriaLoaded, setCriteriaLoaded] = useState(false);
  
  // Classification Criteria state (user-adjustable thresholds for deal classification)
  // P0-2 FIX: Include min_margin to align with backend decision engine
  const [classificationCriteria, setClassificationCriteria] = useState(() => {
    // Load from localStorage if available
    try {
      const saved = localStorage.getItem('chekdeal_classification_criteria');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure min_margin exists (backward compatibility)
        if (!parsed.min_margin) {
          parsed.min_margin = 10;
        }
        return parsed;
      }
    } catch (e) {
      // Ignore parse errors
    }
    return { min_profit: 20000, min_margin: 10, max_investment_pct_arv: 75, max_rehab_ratio: 1.0 };
  });
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Share Analysis modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [currentShareId, setCurrentShareId] = useState(null); // Track the share snapshot ID for email
  const [creatingShareSnapshot, setCreatingShareSnapshot] = useState(false);
  const [pendingShareAfterAuth, setPendingShareAfterAuth] = useState(false); // P0-1 FIX: Flag for deferred share snapshot
  const handleShareWithAuthRef = useRef(null); // P0-1 FIX: Ref to store handleShareWithAuth for deferred calls
  // Monotonic id for recalculations. Editing two fields in a row fires two overlapping
  // recalcs; without this the SLOWER (older) response lands last and silently reverts the
  // newer edit — e.g. typing taxes then insurance kept taxes and dropped insurance.
  const recalcSeqRef = useRef(0);
  // G2: the latest analysis for callbacks whose closure may be older than the state.
  // A mode switch calls recalculateAnalysis in the SAME tick as setAnalysis(adoptArvResult),
  // so `analysis` inside the callback is still the previous mode's — which is how the
  // recalc went out carrying the previous mode's arv_status and blocked a good ARV.
  const latestAnalysisRef = useRef(null);
  // recalculateAnalysis is defined below applyRentalDraft; go through a ref so the
  // helper always calls the current version without depending on declaration order.
  const recalculateAnalysisRef = useRef(null);
  const languageFixAppliedRef = useRef(false); // V2.3.0: Prevent multiple language fix calls
  
  // PDF Preview modal state
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  
  // Feature gating state
  const [showFeatureLockedModal, setShowFeatureLockedModal] = useState(false);
  const [lockedFeature, setLockedFeature] = useState(null);
  
  // Handler for locked features
  const handleLockedFeature = (featureName, displayName) => {
    setLockedFeature({ name: featureName, displayName });
    setShowFeatureLockedModal(true);
  };
  
  // Track if comps were modified since last ARV recalculation (for PDF warning)
  const [compsModifiedSinceRecalc, setCompsModifiedSinceRecalc] = useState(false);
  
  // Auth prompt for gated features
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Feedback prompt state (shows after 3+ analyses)
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  
  // CONVERSION: Upgrade prompt state
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptVariant, setUpgradePromptVariant] = useState('general');
  const [dealStats, setDealStats] = useState(null);
  
  // Explain deal state
  const [showExplanation, setShowExplanation] = useState(false);
  
  // Data refresh state
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  
  // Down payment mode toggle (LTV % vs manual $ amount)
  const [downPaymentMode, setDownPaymentMode] = useState(_restoredWork?.downPaymentMode || 'ltv'); // 'ltv' or 'manual'

  // Has the user set rehab financing DELIBERATELY, as opposed to inheriting it?
  //
  // Tracked as its own flag rather than inferred from the value, because
  // rehabFinancing === 'cash' means two different things: the default nobody has touched,
  // which should follow the purchase toggle, and a deliberate choice, which must not be
  // overwritten by it. The value cannot tell those apart; only this can.
  //
  // Restored with the rest of the saved work so a re-opened analysis does not silently
  // revert to inheriting.
  //
  // With no saved work, the document decides: a pair that differs (flip_analysis.
  // financing_structure) was chosen by someone, and the purchase toggle must not inherit
  // over it on the first click. See src/lib/financingFromDocument.js.
  const [rehabFinancingTouched, setRehabFinancingTouched] =
    useState(_restoredWork?.rehabFinancingTouched
      || (!fromConfirmation && financingSeedFromAnalysis(originalAnalysis).rehabFinancingTouched));
  const [manualDownPayment, setManualDownPayment] = useState(_restoredWork?.manualDownPayment ?? '');
  
  // Property details editing state
  const [showPropertyEdit, setShowPropertyEdit] = useState(false);
  // Property override state - initialized from analysis.property_snapshot for consistency
  // CRITICAL: If coming from PropertyConfirmation, the confirmed values are already in property_snapshot
  // We initialize propertyOverrides to match, so there's no dual-state confusion
  const [propertyOverrides, setPropertyOverrides] = useState(() => {
    // PROBLEM 1: restore the user's edited property details (beds/baths/sqft/...) on return.
    if (_restoredWork?.propertyOverrides) {
      return _workingState.propertyOverrides;
    }
    const ps = originalAnalysis?.property_snapshot;
    if (fromConfirmation && ps) {
      // Coming from confirmation - treat confirmed values as the current state
      return {
        beds: ps.beds || null,
        baths: ps.baths || null,
        sqft: ps.sqft || null,
        year_built: ps.year_built || null,
        lot_size: ps.lot_size || null,
        property_type: ps.property_type || null,
      };
    }
    // Normal flow - start with null (use original values)
    return {
      beds: null,
      baths: null,
      sqft: null,
      year_built: null,
      lot_size: null,
      property_type: null,
    };
  });
  const [hasPropertyOverrides, setHasPropertyOverrides] = useState(fromConfirmation || !!_restoredWork?.hasPropertyOverrides);
  // Whether those overrides have been RUN, as opposed to merely typed.
  //
  // The "User Adjusted" badge used to read hasPropertyOverrides, which flips on the
  // keystroke — so the screen claimed the adjustment was registered while the ARV beside
  // it was still the old one, and nothing said a button was waiting. Overrides restored
  // from a finished analysis were applied by definition, hence the same initialiser.
  const [overridesApplied, setOverridesApplied] = useState(fromConfirmation || !!_restoredWork?.hasPropertyOverrides);

  // Feature gaps the user can declare AFTER the analysis, not only on the confirmation
  // screen. Same two flags the engine already reads (FEATURE_GAP_ADJUSTMENTS), seeded
  // from whatever the analysis recorded so the boxes reflect what is currently applied.
  const _appliedGap = analysis?.arv_details?.feature_gap_adjustment?.applied || {};
  const [missingBasement, setMissingBasement] = useState(_appliedGap?.basement?.finish ?? null);
  const [missingGarage, setMissingGarage] = useState(_appliedGap?.garage?.spaces ?? null);
  const [gapRecalculating, setGapRecalculating] = useState(false);

  // Editable purchase price state (P0 - critical for user experience)
  const [editedPurchasePrice, setEditedPurchasePrice] = useState(_restoredWork?.editedPurchasePrice ?? null);
  const [isEditingPurchasePrice, setIsEditingPurchasePrice] = useState(false);
  
  // Get the effective purchase price (edited or original)
  const effectivePurchasePrice = editedPurchasePrice !== null ? editedPurchasePrice : (analysis?.purchase_price || 0);
  
  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Reopened from the Saved list (or restored a saved working state) => already saved.
  const [isSaved, setIsSaved] = useState(
    location.state?.fromSavedAnalysis === true || !!originalAnalysis?._saved_working_state
  );
  
  // Unsaved changes warning hook
  const warningMessage = lang === 'en' 
    ? 'You have unsaved changes. Save before leaving?' 
    : '¿Tienes cambios sin guardar. ¿Guardar antes de salir?';
  const { navigateWithWarning } = useUnsavedChangesWarning(hasUnsavedChanges, warningMessage);
  
  // Editable assumptions state
  // NOTE: PERSISTENCE FIX - Use savedAssumptions from sessionStorage if available
  // This preserves user-edited values (rent, taxes, insurance) across navigation
  // CRITICAL: If initialFinancingMode is passed from PropertyConfirmation, use it

  // Read a value the confirmation screen passed through, but ONLY on a genuinely fresh
  // confirmation run. location.state persists across back-navigation, so on a re-entry
  // it holds pre-analysis inputs that must not outrank what the user has since edited.
  const confirmationSeed = (key) => (fromConfirmation ? location.state?.[key] : undefined);

  const getDefaultAssumptions = () => ({
    rehabCondition: location.state?.rehabCondition
      || originalAnalysis?.rehab_breakdown?.condition
      || 'auto',
    customRehabPerSqft: location.state?.customRehabPerSqft
      ? location.state.customRehabPerSqft.toString()
      : '',
    customRehabTotal: location.state?.customRehabTotal
      ? location.state.customRehabTotal.toString()
      : (originalAnalysis?.rehab_estimate?.toString() || ''),
    customRehabInputMode: location.state?.customRehabInputMode || 'total', // 'total' or 'persqft'
    holdingPeriod: originalAnalysis?.assumptions?.holding_period || 9,
    loanInterest: originalAnalysis?.assumptions?.loan_interest || 12,
    loanPoints: originalAnalysis?.assumptions?.loan_points || 2,
    closingCostsPercent: originalAnalysis?.assumptions?.closing_costs_percent || 3,
    agentCommission: originalAnalysis?.assumptions?.agent_commission || 6,
    // MAO Rule: Read from arv_multiplier (decimal) and convert to percentage
    maoRule: originalAnalysis?.assumptions?.arv_multiplier 
      ? Math.round(originalAnalysis.assumptions.arv_multiplier * 100) 
      : 70, // Default 70% if not set
    // FLIP-specific costs (only used when strategy === 'flip')
    propertyTaxes: originalAnalysis?.assumptions?.property_taxes || '',
    insurance: originalAnalysis?.assumptions?.insurance || '',
    utilities: originalAnalysis?.assumptions?.utilities || '',
    otherHoldingCosts: originalAnalysis?.assumptions?.other_holding_costs || '',
    downPaymentPct: originalAnalysis?.assumptions?.down_payment_pct || 20,
    // Financing structure - Use initialFinancingMode from PropertyConfirmation if provided.
    // Otherwise the PAIR is the document's (flip_analysis.financing_structure — what the
    // engine priced the stored numbers with), not `assumptions`, which held the
    // confirmation screen's single mode for both legs. See src/lib/financingFromDocument.js.
    purchaseFinancing: initialFinancingMode || financingSeedFromAnalysis(originalAnalysis).assumptions.purchaseFinancing,
    purchaseLTV: originalAnalysis?.assumptions?.purchase_ltv || 80,
    purchaseInterestRate: originalAnalysis?.assumptions?.purchase_interest_rate || 11,
    purchaseLoanPoints: originalAnalysis?.assumptions?.purchase_loan_points || DEFAULT_FLIP_ORIGINATION_POINTS,
    purchaseLenderFees: originalAnalysis?.assumptions?.purchase_lender_fees || '',
    rehabFinancing: initialFinancingMode || financingSeedFromAnalysis(originalAnalysis).assumptions.rehabFinancing,
    rehabFinancedPct: originalAnalysis?.assumptions?.rehab_financed_pct || 100,
    rehabInterestRate: originalAnalysis?.assumptions?.rehab_interest_rate || 12,
    rehabLoanPoints: originalAnalysis?.assumptions?.rehab_loan_points || 0,
    // Deal Tolerance settings
    toleranceMode: originalAnalysis?.assumptions?.tolerance_mode || 'balanced',
    tolerancePricePct: originalAnalysis?.assumptions?.tolerance_price_pct || 1, // 1% default
    toleranceProfitDollars: originalAnalysis?.assumptions?.tolerance_profit_dollars || 2500, // $2,500 default
    // ========================================
    // RENTAL-SPECIFIC PARAMETERS (SEPARATE from Flip)
    // Initialize from rental_analysis to preserve actual values
    // ========================================
    // BUG 5 (read side): prefer the SAVED assumptions bundle over values re-derived from
    // rental_analysis. buildCanonicalState writes the user's edits into
    // originalAnalysis.assumptions; re-deriving from monthly_costs_breakdown instead
    // silently reverted them whenever the stored analysis predated the edit. Order is:
    // what the user entered up front (location.state) > what they saved > derived > default.
    // The stated order above begins with "what the user entered up front
    // (location.state)", but that term was missing here — unlike purchaseFinancing and
    // rehabFinancing above, which do start from initialFinancingMode. That asymmetry is
    // why flip honoured the confirm-page choice and rental silently ignored it.
    rentalFinancingType: initialRentalFinancingType
      || originalAnalysis?.assumptions?.rental_financing_type
      || originalAnalysis?.rental_analysis?.financing_type || 'dscr',
    rentalDownPaymentPct: originalAnalysis?.assumptions?.rental_down_payment_pct
      ?? (originalAnalysis?.rental_analysis?.rental_purchase_type === 'refinancing'
        ? 0
        : (originalAnalysis?.rental_analysis?.down_payment_pct ?? 25)),
    rentalInterestRate: originalAnalysis?.assumptions?.rental_interest_rate
      || originalAnalysis?.rental_analysis?.interest_rate || 7.5,
    rentalLoanTermYears: originalAnalysis?.assumptions?.rental_loan_term_years || 30,
    rentalLoanPoints: originalAnalysis?.assumptions?.rental_loan_points ?? DEFAULT_RENTAL_ORIGINATION_POINTS,
    rentalPropertyCondition: originalAnalysis?.assumptions?.rental_property_condition
      || originalAnalysis?.rental_analysis?.property_condition || 'turnkey',
    rentalPurchaseType: originalAnalysis?.assumptions?.rental_purchase_type
      || originalAnalysis?.rental_analysis?.rental_purchase_type || 'new_purchase',
    // Refinance inputs — must round-trip, or a recalc silently reverts the refi to a purchase
    rentalAppraisedValue: originalAnalysis?.assumptions?.appraised_value
      ?? originalAnalysis?.rental_analysis?.appraised_value ?? null,
    rentalExistingPayoff: originalAnalysis?.assumptions?.existing_payoff ?? 0,
    rentalRefiLtv: originalAnalysis?.assumptions?.refi_ltv ?? 75,
    // RENTAL OPERATING COSTS - saved edit first, else derived from monthly_costs_breakdown
    // These are ANNUAL amounts
    rentalAnnualTaxes: originalAnalysis?.assumptions?.rental_annual_taxes
      ?? (originalAnalysis?.rental_analysis?.monthly_costs_breakdown?.property_taxes || 0) * 12,
    rentalAnnualInsurance: originalAnalysis?.assumptions?.rental_annual_insurance
      ?? (originalAnalysis?.rental_analysis?.monthly_costs_breakdown?.insurance || 0) * 12,
    // Rental percentages for vacancy/maintenance/management
    rentalVacancyPct: originalAnalysis?.assumptions?.rental_vacancy_pct ?? 5.0,
    rentalMaintenancePct: originalAnalysis?.assumptions?.rental_maintenance_pct ?? 8.0,
    rentalManagementPct: originalAnalysis?.assumptions?.rental_management_pct ?? 10.0,
    rentalHoa: originalAnalysis?.assumptions?.rental_hoa
      ?? (originalAnalysis?.rental_analysis?.monthly_costs_breakdown?.hoa || 0),
    rentalUtilities: originalAnalysis?.assumptions?.rental_utilities
      ?? (originalAnalysis?.rental_analysis?.monthly_costs_breakdown?.utilities || 0) * 12,
    rentalOtherOpex: originalAnalysis?.assumptions?.rental_other_opex ?? 0,
    // RENTAL INCOME - Initialize from actual rental_analysis values
    rentalMonthlyRent: originalAnalysis?.rental_analysis?.monthly_rent || originalAnalysis?.rent_estimate || 0,
    // FLIP (multi-family only) — estimated rent PER UNIT after renovation. Feeds the
    // GRM income side of the multi-family sale-price ARV (NOT a cash-flow calc).
    // Shared rent-of-record with rental/BRRRR. SFR flip has no rent field.
    // NO FALLBACK TO `rent_estimate` — the fifth and last site of the same defect.
    // `analysis.rent_estimate` is what server.py returns straight from the fetcher, i.e.
    // RentCast's /avm/rent/long-term figure, which is the rent of the WHOLE PROPERTY.
    // Measured on 252 Philip St (Detroit 48215): RentCast's record for it is 6 beds /
    // 2,358 sqft, it priced that whole envelope against houses of 2,000-2,844 sqft, and
    // returned $2,420; the single UNITS in its own comparable set are 900-1,250 sqft.
    //
    // This field is per unit and :1746 multiplies it by the unit count into `user_rent`,
    // so a pre-filled building rent went out doubled. And the guard there fires on
    // `_rentEdited || _grmEdited` — touching only the GRM was enough to send it.
    //
    // `income_approach.monthly_rent_per_unit` stays: the engine divided that one itself.
    // Blank when there is none, for the same reason as the confirmation panel — an empty
    // field is visible, a credible wrong number is not.
    //
    // `|| 0` and not `|| ''`: the input renders `flipRentPerUnit || ''` so 0 already shows
    // as an empty box, while :1746 multiplies this by the unit count — and '' there would
    // make `user_rent` NaN instead of a clean falsy 0 the backend ignores.
    flipRentPerUnit: originalAnalysis?.arv_details?.income_approach?.monthly_rent_per_unit || 0,
    // THE USER'S RENT IS THE RENT. Once they type one it is sent with every recalculation
    // (not only the edit that changed it), so the income view, the total and the stored
    // document keep saying that number — the RentCast estimate never comes back. True
    // from the start when the stored income view already came from the user.
    flipRentIsUser: originalAnalysis?.arv_details?.income_approach?.rent_source === 'user',
    // GRM for the multi-family ARV — BLANK. It used to be pre-filled with the
    // market-DERIVED GRM, and the payload below then sent that back as
    // `multi_family_grm`, which the backend reads as the user's own override. The engine
    // separates the two deliberately (blend_arv_with_income_approach): a supplied GRM is
    // renovated judgment and goes income-led 20/80, a derived one is as-is comps and must
    // stay sales-led. Pre-filling erased that distinction with nobody typing anything.
    //
    // A derived GRM is an AS-IS GRM, so promoting it drags a renovated ARV back down
    // toward as-is. Measured in tests/mf_grm_promotion_impact.py on the three properties
    // with a recorded derived GRM: median |error| 15.5% sales-led against 32.4% promoted,
    // worst case 941 Livernois at $494,400 -> $304,080 (+9.9% -> -32.4%).
    //
    // Nothing is hidden by this: the input's placeholder is the market GRM and a
    // "market est. NN" label sits beside it, so the user still sees the number — it just
    // is not their answer until they type it.
    flipGrm: '',
    // RENTAL CLOSING COSTS (BUG 4)
    // Seeded from what the user entered on the confirmation screen, then the saved
    // bundle, then the default. Without the confirmation tier these silently reset to
    // 3% / 1pt / $0 on the first recalc, wiping fees the INITIAL analysis had already
    // charged against cash invested.
    // `confirmationSeed` (not location.state directly): location.state survives a
    // back-navigation, and these values are commonly a legitimate 0 — which `??` treats
    // as present, so a stale 0 from before the analysis ran would beat an edit made
    // after it. Only a genuinely fresh confirmation run may seed them.
    rentalClosingCostsPct: confirmationSeed('rentalClosingCostsPct')
      ?? originalAnalysis?.assumptions?.rental_closing_costs_pct ?? 3.0,
    rentalOriginationPoints: confirmationSeed('rentalOriginationPoints')
      ?? originalAnalysis?.assumptions?.rental_origination_points ?? DEFAULT_RENTAL_ORIGINATION_POINTS,
    rentalLenderFees: confirmationSeed('rentalLenderFees')
      ?? originalAnalysis?.assumptions?.rental_lender_fees ?? 0,
    rentalTitleAttorneyFees: confirmationSeed('rentalTitleAttorneyFees')
      ?? originalAnalysis?.assumptions?.rental_title_attorney_fees ?? 0,
    rentalEscrowReserves: confirmationSeed('rentalEscrowReserves')
      ?? originalAnalysis?.assumptions?.rental_escrow_reserves ?? 0,
    rentalOtherClosingCosts: confirmationSeed('rentalOtherClosingCosts')
      ?? originalAnalysis?.assumptions?.rental_other_closing_costs ?? 0,
    rentalSellerAgentCommission: confirmationSeed('rentalSellerAgentCommission')
      ?? originalAnalysis?.assumptions?.rental_seller_agent_commission ?? 0,
  });
  
  // PERSISTENCE FIX: Use saved assumptions from sessionStorage, or fall back to defaults.
  // BUT on a FRESH run from PropertyConfirmation, always seed from the confirmation inputs
  // (getDefaultAssumptions reads location.state) — never from stale sessionStorage, which
  // could carry a previous run's rehab and silently override what the user just entered.
  const [assumptions, setAssumptions] = useState(() => {
    // PROBLEM 1: restore the user's edited assumptions (taxes, rehab, closing, financing,
    // lender fees, points, terms) for THIS deal on return — takes precedence so no edit
    // reverts. Not used on a fresh confirmation run (that seeds from the confirmation).
    if (_restoredWork?.assumptions && !fromConfirmation) {
      console.log('[Results] Restored edited assumptions from working state');
      return _restoredWork.assumptions;
    }
    if (savedAssumptions && !fromConfirmation) {
      console.log('[Results] Using recovered assumptions from sessionStorage');
      return savedAssumptions;
    }
    return getDefaultAssumptions();
  });

  // PER-MODE FINANCING buckets. financingByMode.cash / financingByMode.financed each hold
  // their OWN financing inputs (LTV, rates, points, lender fees, down payment); everything
  // else in `assumptions` is shared. Restored from the saved deal / working state, else
  // seeded from the current (shared) assumptions so the Financed bucket starts with any
  // saved terms. Kept in sync with the live inputs by an effect below; loaded on toggle.
  const [financingByMode, setFinancingByMode] = useState(() => {
    if (_restoredWork?.financingByMode) return _restoredWork.financingByMode;
    const seed = {
      ...pickFinancing(assumptions),
      downPaymentMode: _restoredWork?.downPaymentMode || downPaymentMode,
      manualDownPayment: _restoredWork?.manualDownPayment ?? manualDownPayment,
    };
    return { cash: { ...seed }, financed: { ...seed } };
  });
  
  // Default assumptions for reset - Default to CASH
  const defaultAssumptions = {
    rehabCondition: 'auto',
    customRehabPerSqft: '',
    customRehabTotal: '',
    customRehabInputMode: 'total', // Default to total budget mode
    holdingPeriod: 9,
    loanInterest: 12,
    loanPoints: 2,
    closingCostsPercent: 3,
    agentCommission: 6,
    maoRule: 70, // Default target rule (70%)
    propertyTaxes: '',
    insurance: '',
    utilities: '',
    otherHoldingCosts: '',
    downPaymentPct: 20,
    // Financing defaults - CASH (FLIP)
    purchaseFinancing: 'cash',
    purchaseLTV: 80,
    purchaseInterestRate: 11,
    purchaseLoanPoints: DEFAULT_FLIP_ORIGINATION_POINTS,
    purchaseLenderFees: '',
    rehabFinancing: 'cash',
    rehabFinancedPct: 100,
    rehabInterestRate: 12,
    rehabLoanPoints: 0,
    // Deal Tolerance defaults - Balanced mode
    toleranceMode: 'balanced',
    tolerancePricePct: 1, // 1%
    toleranceProfitDollars: 2500, // $2,500
    // ========================================
    // RENTAL-SPECIFIC DEFAULTS
    // ========================================
    rentalFinancingType: 'dscr',
    rentalDownPaymentPct: 25,
    rentalInterestRate: 7.5,
    rentalLoanTermYears: 30,
    rentalLoanPoints: DEFAULT_RENTAL_ORIGINATION_POINTS,
    rentalPropertyCondition: 'turnkey',
    // Rental operating costs - defaults to 0 until user edits
    rentalAnnualTaxes: 0,
    rentalAnnualInsurance: 0,
    rentalVacancyPct: 5.0,
    rentalMaintenancePct: 8.0,
    rentalManagementPct: 10.0,
    rentalHoa: 0,
    rentalUtilities: 0,
    rentalOtherOpex: 0,
    // Rental income
    rentalMonthlyRent: 0,
    // Rental closing costs
    rentalClosingCostsPct: 3.0,
    rentalOriginationPoints: DEFAULT_RENTAL_ORIGINATION_POINTS,
    rentalLenderFees: 0,
    rentalTitleAttorneyFees: 0,
    rentalEscrowReserves: 0,
    rentalOtherClosingCosts: 0,
    rentalSellerAgentCommission: 0,
  };

  // ============================================================
  // V5.0: FETCH ANALYSIS FROM BACKEND IF URL HAS ID BUT NO DATA
  // This handles direct URL access and ensures correct analysis loads
  // ============================================================
  const [fetchingFromBackend, setFetchingFromBackend] = useState(false);
  
  useEffect(() => {
    const fetchAnalysisById = async () => {
      // Only fetch if:
      // 1. URL has an analysis ID
      // 2. We don't have analysis data OR the data doesn't match URL
      const currentAnalysisId = analysis?.analysis_id || analysis?.id;
      
      // V5.0: Only fetch if URL has a real analysis_id (not 'new') and we don't have matching data
      if (urlAnalysisId && urlAnalysisId !== 'new' && (!analysis || (currentAnalysisId && currentAnalysisId !== urlAnalysisId))) {
        console.log('[Results] URL has analysis_id but no matching data, fetching from backend:', urlAnalysisId);
        setFetchingFromBackend(true);
        
        try {
          // /results/sample-flip and /results/sample-rental are the two frozen examples:
          // a file on the server, not a stored analysis. Resolving them here means a
          // reload or a pasted link opens the example again instead of a 404.
          const response = urlAnalysisId.startsWith('sample-')
            ? await analysisAPI.getSampleDeal(urlAnalysisId.slice('sample-'.length), lang)
            : await analysisAPI.getAnalysis(urlAnalysisId, lang);
          if (response.data && urlAnalysisId.startsWith('sample-')) response.data.is_sample = true;
          if (response.data) {
            console.log('[Results] Fetched analysis from backend:', urlAnalysisId);
            response.data._isFreshFromFlow = false;
            response.data._fromSessionStorage = false;
            response.data._fetchedFromBackend = true;
            response.data._sourceType = 'backend_fetch';
            setAnalysis(withActiveModeVoice(response.data));
            setIsFromSessionStorage(false);

            // `assumptions` was seeded at mount with no document (originalAnalysis is null
            // on a URL open), so it holds cash/cash and the page defaults. Seed the
            // financing from the document that just arrived — the pair its numbers were
            // priced with, and the terms — unless this tab's own edits were restored, which
            // outrank the stored copy. Without this the page showed the profit of
            // "purchase financed, rehab cash" next to two cash toggles, and the next
            // recalculation sent cash/cash.
            if (!_restoredWork && !fromConfirmation) {
              const seed = financingSeedFromAnalysis(response.data);
              setAssumptions(prev => ({ ...prev, ...seed.assumptions }));
              setRehabFinancingTouched(seed.rehabFinancingTouched);
            }

            // Update ARV state
            if (response.data.arv) {
              setCurrentARV(response.data.arv);
              setCurrentARVConfidence(response.data.arv_confidence || 'moderate');
              setCurrentARVConfidenceScore(response.data.arv_confidence_score || 50);
            }
          } else {
            // Backend returned no data - redirect to Analyzer
            console.log('[Results] Backend returned no data, redirecting');
            navigate('/analyze', { replace: true });
          }
        } catch (error) {
          console.error('[Results] Failed to fetch analysis from backend:', error);
          // Redirect to Analyzer on fetch failure
          navigate('/analyze', { replace: true });
        } finally {
          setFetchingFromBackend(false);
        }
      }
    };
    
    fetchAnalysisById();
  }, [urlAnalysisId]); // Only re-run when URL analysis ID changes
  
  // ============================================================
  // V2.3.0: FIX DEAL_EXPLANATION LANGUAGE FOR SAVED ANALYSES
  // When loading a saved analysis with different language than UI,
  // fetch the explanation in the correct language (lightweight call)
  // ============================================================
  useEffect(() => {
    const fixExplanationLanguage = async () => {
      // Only run once, only for saved analyses with language mismatch
      if (
        !languageFixAppliedRef.current &&
        analysis &&
        analysis._sourceType === 'saved_analysis' &&
        analysis.language &&
        analysis.language !== lang
      ) {
        languageFixAppliedRef.current = true;
        console.log(`[Results] Fixing explanation language: ${analysis.language} -> ${lang}`);
        
        try {
          const response = await fetch(`${API_BASE}/api/deals/explanation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              strategy: analysis.strategy,
              language: lang,
              flip_analysis: analysis.flip_analysis,
              rental_analysis: analysis.rental_analysis,
              rental_classification: analysis.rental_classification,
              deal_classification: analysis.deal_classification,
              deal_score: analysis.deal_score,
              deal_rating: analysis.deal_rating,
              assumptions: analysis.assumptions,
              purchase_price: analysis.purchase_price,
              // These two are stored as finished text too. Without sending them the
              // endpoint has nothing to restate and the lists below the paragraph stay
              // in the language the deal was saved in.
              risk_factors: analysis.risk_factors,
              opportunities: analysis.opportunities
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            setAnalysis(prev => ({
              ...prev,
              deal_explanation: data.deal_explanation,
              // `??` and not `||`: the backend omits a list it was not given, and an
              // analysis with genuinely zero opportunities must keep its empty array
              // rather than fall back to the previous deal's.
              risk_factors: data.risk_factors ?? prev.risk_factors,
              opportunities: data.opportunities ?? prev.opportunities,
              language: lang
            }));
            console.log('[Results] Explanation language fixed successfully');
          }
        } catch (error) {
          console.error('[Results] Failed to fix explanation language:', error);
        }
      }
    };
    
    fixExplanationLanguage();
  }, [analysis, lang]);
  
  // ============================================================
  // CANONICAL STATE: Single Source of Truth
  // ============================================================
  // INVARIANT: At any moment after analysis or recalculation, there must exist
  // exactly ONE canonical final analysis state used by:
  // - Results UI
  // - Share snapshot
  // - PDF generation
  // - Email generation
  // 
  // NO output may compute from a different source.
  // ============================================================
  latestAnalysisRef.current = analysis;

  const canonicalState = useMemo(() => {
    if (!analysis) return null;
    
    const state = buildCanonicalState({
      analysis,
      propertyOverrides,
      editedPurchasePrice,
      currentARV,
      currentARVConfidence,
      currentARVConfidenceScore,
      assumptions,
      arvModeLabel,
      classificationCriteria
    });
    
    // Log checksum at Results build
    if (state) {
      logCanonicalChecksum('RESULTS_BUILD', state);
    }
    
    return state;
  }, [
    analysis,
    propertyOverrides,
    editedPurchasePrice,
    currentARV,
    currentARVConfidence,
    currentARVConfidenceScore,
    assumptions,
    arvModeLabel,
    classificationCriteria
  ]);

  // CRITICAL: The SSOT is the canonical state - NO FALLBACKS
  // All UI and output surfaces MUST use this exact object
  const SSOT = canonicalState;
  
  // BACKWARD COMPATIBILITY: Alias for existing references
  const currentAnalysisState = canonicalState;
  
  // HONEST BLOCK: when the ARV is not reliable (multi-family with no renovated comps,
  // or unable to compute), the ARV number and the Fix&Flip PASS/FAIL verdict must NOT be
  // shown as confident. Gate the ARV card + deal-classification verdict on this and show
  // the "add your renovated comps" state instead. verdict_reliable is set by the backend.
  // G3: the active mode's own record is the authority (see withActiveModeVoice). The
  // top-level fields are a copy of it; where they disagree, the record wins.
  const _activeModeRecord = analysis?.arv_by_mode?.[analysis?.active_arv_mode] || null;
  const activeArvDetails = _activeModeRecord?.arv_details || analysis?.arv_details;
  const arvStatusRaw = activeArvDetails?.arv_status;
  // Neutral type nudge: driven by DETECTION uncertainty, never by a price ratio.
  const needsTypeVerification = analysis?.property_snapshot?.needs_type_verification === true
    || analysis?.needs_type_verification === true
    || String(analysis?.property_snapshot?.type_confidence || '').toLowerCase() === 'low';
  // The verdict flag is the engine's answer to "may this ARV carry a confident verdict",
  // computed per mode. When it is present it decides alone: an ARV the engine called
  // reliable cannot be blocked by an arv_status left behind by another pool. The
  // arv_status rule is the fallback for documents that predate the flag.
  const _verdictFlag = (typeof _activeModeRecord?.verdict_reliable === 'boolean')
    ? _activeModeRecord.verdict_reliable
    : (typeof analysis?.verdict_reliable === 'boolean' ? analysis.verdict_reliable : undefined);
  const arvBlocked = _verdictFlag !== undefined
    ? !_verdictFlag
    : (arvStatusRaw === 'UNABLE_TO_COMPUTE' || arvStatusRaw === 'NO_RENOVATED_COMPS' || arvStatusRaw === 'UNIT_COUNT_UNCONFIRMED');
  // E: a multi-family whose unit count was never confirmed has NO value yet — not a
  // weak one. The header, the panel and the PDF say "confirm the type", not "add comps".
  const unitCountUnconfirmed = arvStatusRaw === 'UNIT_COUNT_UNCONFIRMED';
  // V5 refused to value this against a single-family pool; V4.4 did it anyway. The
  // number is the best available, but it must not be presented as well supported.
  const noMultifamilyComps =
    activeArvDetails?.v5_declined_reason === 'no_multifamily_comps';
  const noRenovatedComps = arvStatusRaw === 'NO_RENOVATED_COMPS'
    || activeArvDetails?.no_renovated_comps_flag?.triggered === true;
  // WHY this ARV is weak, per fallback tier. The engine writes a different, more precise
  // sentence for each one (floor-anchored vs livable-sold vs for-sale listings) and until
  // now nothing rendered it. arv_warning is English prose with no translation path, so the
  // localised copy is keyed off arv_warning_key (the tier name); the prose is the fallback
  // for any tier not listed here, which is better than showing nothing.
  const ARV_TIER_NOTES = {
    sfr_comps_no_multifamily_sales: {
      en: 'Valued with single-family comparables — no multifamily sales nearby. Add your own comps for a reliable ARV.',
      es: 'Valuado con comparables unifamiliares — no hay ventas multifamiliares cerca. Agrega tus propios comps para un ARV confiable.',
    },
    floor_anchored_no_livable_comps: {
      en: 'No livable comparable sales or listings nearby — this is a floor estimate, not a comp-based value.',
      es: 'No hay ventas ni listados habitables cerca — esto es una estimación de piso, no un valor basado en comps.',
    },
    floor_anchored_rescue_below_floor: {
      en: 'No livable comparable sales or listings nearby — this is a floor estimate, not a comp-based value.',
      es: 'No hay ventas ni listados habitables cerca — esto es una estimación de piso, no un valor basado en comps.',
    },
    as_is_sold_above_livable_floor: {
      en: 'Limited renovated comps here — this uses the livable sold comps available.',
      es: 'Comps renovados limitados aquí — esto usa las ventas habitables disponibles.',
    },
    estimate_from_for_sale_listings: {
      en: 'Based on for-sale listings (asking prices, not closed sales).',
      es: 'Basado en propiedades en venta (precios pedidos, no ventas cerradas).',
    },
    ppsf_fallback_no_bed_data: {
      en: 'Comparables lacked unit data — valued per square foot instead.',
      es: 'Los comparables no traen datos de unidades — valuado por pie cuadrado.',
    },
    unit_count_unconfirmed: { en: 'Confirm the property type (duplex, triplex or fourplex) to compute the value.', es: 'Confirma el tipo de propiedad (dúplex, tríplex o cuádruplex) para calcular el valor.' },
  };
  // Lo que se dice cuando la evidencia la puso el USUARIO. Mismo formato que
  // ARV_TIER_NOTES: una frase, sin jerga de motor, nombrando lo que pasó.
  // Quienes construyeron el ARV y que tan firme lo deja, en utils/arvSourceNote.js: es
  // aritmetica de conteos y texto, y ahi se puede probar como tal en vez de a traves del
  // DOM de una pagina de 6,000 lineas. El porque de cada una de las dos frases esta
  // escrito en ese archivo.
  const arvTierNote = (() => {
    const ad = activeArvDetails;      // G3: the active mode's own record
    if (!ad) return null;
    // COMPS DEL USUARIO GANAN AL TIER.
    //
    // `sales_method` describes the SHAPE of the calculation, not where the evidence came
    // from, so an ARV built entirely from three comps the user added still comes back
    // labelled `floor_anchored_no_livable_comps` — and the note above told that user
    // "No livable comparable sales or listings nearby" seconds after the engine had
    // valued their property on the comps they supplied. Measured on 5060 Tireman: three
    // user comps at $81-90/sqft moved the ARV from $192,400 to $173,237, which is their
    // own band, and the screen denied it had happened.
    //
    // With the editable ARV removed, adding comps is the user's ONLY way to change the
    // number. It cannot end by telling them their work did nothing.
    const usados = Number(analysis?.user_comps_used ?? 0);
    if (usados > 0) {
      const sistema = Number(analysis?.system_comps_used ?? 0);
      return arvSourceNote(usados, sistema, lang);
    }
    const entry = ARV_TIER_NOTES[ad.arv_warning_key];
    return entry ? entry[lang === 'es' ? 'es' : 'en'] : (ad.arv_warning || null);
  })();

  const noRenoMessage = activeArvDetails?.no_renovated_comps_flag?.message
    || (lang === 'en'
        ? 'Limited renovated comps in this area — add your renovated comps for an accurate ARV.'
        : 'Comps renovados limitados en esta área — agregue sus comps renovados para un ARV preciso.');

  // ARV BASIS LABEL: makes the intentional cross-mode difference read as deliberate, not
  // drift. Flip/BRRRR value the AFTER-REPAIR resale; rental/valuation value AS-IS. Prefer the
  // engine's value_basis when set (multi-family — where a renovated cluster IS after-repair
  // regardless of mode), else fall back to strategy.
  const _valueBasis = analysis?.arv_details?.value_basis;
  const _isAfterRepair = _valueBasis === 'after_repair'
    || ((analysis?.strategy === 'flip' || analysis?.strategy === 'brrrr') && _valueBasis !== 'as_is');
  const arvBasisLabel = _isAfterRepair
    ? (lang === 'en' ? 'After-Repair Value (ARV)' : 'Valor Tras Reparación (ARV)')
    : (lang === 'en' ? 'As-Is Value' : 'Valor Actual');

  // THE VALUE ON SCREEN. Always CHEKSTE's analysis now — from the automatic comps, from
  // comparables the user added, or from the two blended. There is no fourth route where
  // a typed number becomes the valuation.
  //
  // `isUserArv` is kept and pinned false rather than deleted: it gates the "(your figure)"
  // marker on screen and in the PDF, and a stale true there would tell a lender the
  // investor set a number that CHEKSTE actually computed. Which comps built it is said
  // by arvTierNote, where it belongs.
  const effectiveARV = currentARV || analysis?.arv || 0;

  // Every figure below is ARV-derived, so none of them means anything while the ARV is
  // blocked. Route them through this instead of rendering a number computed from zero:
  // with ARV = 0 a flip reads "-$178,000 net profit", which is not a loss, it is an
  // absence of data wearing the costume of a measurement.
  const arvDerived = (node, dash = '—') => (arvBlocked ? dash : node);
  const isUserArv = false;
  const arvLabelSuffix = '';

  // A cash-out REFINANCE is not an acquisition. The owner already holds the property:
  // no down payment, no cash invested, and cash-on-cash has no denominator. Every
  // purchase-shaped figure must be suppressed or relabelled wherever this is true.
  const isRefinance = (analysis?.rental_analysis?.rental_purchase_type
    || assumptions?.rentalPurchaseType) === 'refinancing';

  // THE COMP TABLE'S ONLY SOURCE. comps_used is what the engine actually valued from —
  // already past the distressed/listing ban — so it is authoritative and there is no
  // heuristic fallback.
  //
  // Both comp tables (desktop + mobile) previously fell back to
  // nosemgrep: comp-render-must-not-fall-back-to-raw-pool -- quoting the banned pattern to explain it
  // `analysis.sales_comps.slice(0, 5)` — the RAW RentCast pool, listings and distressed
  // included — whenever comps_used was empty. That is a second render path that never
  // saw the ban, which is why distressed rows reappeared in states where comps_used comes
  // back empty (e.g. refinance) even though the header still claimed they were excluded.
  // The same fallback was removed from ComparableManager for exactly this reason; it
  // survived here. Empty now renders empty, and the UI says why.
  const arvTableComps = (analysis?.comps_used && analysis.comps_used.length > 0)
    ? analysis.comps_used
    : [];

  // Derived values from SSOT for consistent UI rendering
  const ssotDealRating = SSOT?.deal_rating || 'moderate';
  const ssotDealScore = SSOT?.deal_score || 0;
  const ssotFlipAnalysis = SSOT?.flip_analysis || {};
  const ssotNetProfit = ssotFlipAnalysis.net_profit || 0;
  const ssotLoanCosts = ssotFlipAnalysis.loan_costs || 0;
  const ssotProfitMargin = ssotFlipAnalysis.profit_margin || 0;
  const ssotMao = ssotFlipAnalysis.mao || 0;
  // A negative MAO means no purchase price makes the deal work. The backend reports
  // mao_viable=false rather than inventing a $1 "target offer"; show "—" instead of a
  // number. (Older cached analyses have no flag — treat mao <= 1 as non-viable too, so
  // the legacy $1 floor never surfaces as a real-looking offer price.)
  const ssotMaoViable = ssotFlipAnalysis.mao_viable !== undefined
    ? ssotFlipAnalysis.mao_viable !== false
    : ssotMao > 1;
  const ssotRoi = ssotFlipAnalysis.roi || 0;

  const [rentalDraft, setRentalDraft] = useState(null);

  const stageRental = useCallback((patch) => {
    setRentalDraft(prev => ({ ...(prev || {}), ...patch }));
  }, []);

  // Pending value if this field was edited, else the applied one.
  const rentalField = useCallback(
    (key) => (rentalDraft && key in rentalDraft ? rentalDraft[key] : assumptions[key]),
    [rentalDraft, assumptions]
  );

  const hasRentalDraft = !!rentalDraft && Object.keys(rentalDraft).length > 0;

  // LIVE MATH (BUG 3). Every sum and derived figure that can be computed from what is
  // already on screen recomputes AS THE USER TYPES — no server round-trip. The Apply
  // button is now only for the part that genuinely needs the backend: the ARV, which is
  // derived from the comp set and cannot be known locally.
  //
  // Composed off the DRAFT so a pending edit is reflected instantly; falls back to the
  // applied assumptions when nothing is staged. Formulas live in utils/rentalMath.js and
  // mirror rental_engine.py, so these numbers do not jump when Apply replaces them.
  const liveRental = useMemo(
    () => computeLiveRental(
      {
        ...assumptions, ...(rentalDraft || {}),
        // On a REFINANCE the appraised value IS the ARV, so an override must drive the
        // whole chain — loan, PITIA, DSCR, cash flow, cash-out.
        ...((assumptions?.rentalPurchaseType === 'refinancing' && isUserArv)
          ? { rentalAppraisedValue: effectiveARV } : {}),
      },
      { purchasePrice: effectivePurchasePrice, marketValue: effectiveARV }
    ),
    [assumptions, rentalDraft, effectivePurchasePrice, effectiveARV, isUserArv]
  );

  // MIN RENT TO QUALIFY — the ONLY place this is computed.
  //
  // DSCR is Rent / PITIA, so the rent that clears a given DSCR is PITIA x minDSCR.
  // This used to be P&I x minDSCR, which understated the bar by exactly the taxes,
  // insurance and HOA — it told a $1,850-PITIA borrower they needed $1,760.
  // Read off liveRental so it tracks edits, and off the user's own Min DSCR setting.
  const minDscrTarget = classificationCriteria?.rental?.min_dscr ?? 1.0;
  const minRentForDscr = liveRental.monthlyPitia * minDscrTarget;
  // The engine's field is `monthlyRent`. Reading a name it does not export gave
  // undefined, and `|| 0` turned that into a rent of zero — so the badge read
  // "✗ Short" on a deal whose $2,000 rent cleared the $1,887 bar comfortably.
  // No `||` fallback here: a missing field must surface, not silently become 0.
  const liveMonthlyRent = liveRental.monthlyRent;
  const rentQualifies = Number.isFinite(liveMonthlyRent)
    && Number.isFinite(minRentForDscr)
    && liveMonthlyRent >= minRentForDscr;

  // V5.0: Generate short explanation (MAX 2 lines) for MinimalDecisionView
  const getShortExplanation = useMemo(() => {
    // BLOCKED ARV: every profit/margin figure below is derived from an ARV we have just
    // told the user we cannot compute. Quoting one here contradicts the "Add comps" tiles
    // and reads as authoritative to a lender. Say what is actually true instead.
    if (arvBlocked) {
      return lang === 'en'
        ? 'ARV is not reliable yet — comparable renovated sales are missing.'
        : 'El ARV aún no es confiable — faltan ventas comparables renovadas.';
    }
    // RENTAL strategy - use rental metrics
    if (analysis?.strategy === 'rental') {
      const cashflow = analysis?.rental_analysis?.monthly_cashflow || 0;
      const dscr = analysis?.rental_analysis?.dscr || 0;
      const capRate = analysis?.rental_analysis?.cap_rate || 0;
      const rentalDecision = analysis?.rental_classification?.decision || '';
      // A CASH purchase has no loan, so DSCR is not a metric that exists for it. Every
      // line below must judge on cash flow + cap rate instead of naming a lender ratio.
      const isCashRental = liveRental.isCash;

      if (rentalDecision === 'BUY' || rentalDecision === 'FLEX_BUY') {
        // DSCR is a LENDER metric — omit it entirely on a cash purchase rather than
        // quoting "0.00 DSCR", which reads as a deal that cannot cover its debt.
        const dscrPart = (!isCashRental && dscr > 0)
          ? (lang === 'en' ? `${dscr.toFixed(2)} DSCR, ` : `${dscr.toFixed(2)} DSCR, `)
          : '';
        return lang === 'en'
          ? `Strong rental. $${Math.round(cashflow).toLocaleString()}/mo cash flow, ${dscrPart}${capRate.toFixed(1)}% cap rate.`
          : `Renta sólida. $${Math.round(cashflow).toLocaleString()}/mo flujo, ${dscrPart}${capRate.toFixed(1)}% cap rate.`;
      } else if (rentalDecision === 'SPECULATIVE') {
        if (isCashRental) {
          return lang === 'en'
            ? `Moderate rental. $${Math.round(cashflow).toLocaleString()}/mo cash flow at a ${capRate.toFixed(1)}% cap rate — review the price and rent.`
            : `Renta moderada. $${Math.round(cashflow).toLocaleString()}/mo de flujo con ${capRate.toFixed(1)}% de cap rate — revisa el precio y la renta.`;
        }
        return lang === 'en'
          ? `Moderate rental. Review cash flow and DSCR before proceeding.`
          : `Renta moderada. Revisa el flujo de caja y DSCR antes de proceder.`;
      } else {
        // Name ONLY the criteria that actually failed. The previous wording asserted that
        // cash flow AND cap rate were both "below your criteria" whenever the verdict was
        // weak — so a deal throwing off +$1,140/mo was told its cash flow fell short, which
        // is plainly false and destroys trust in every other number on the page.
        const failed = (analysis?.rental_classification?.factors || [])
          .filter((f) => !f.passed && !f.within_tolerance);
        if (failed.length) {
          const reasons = failed.map((f) => `${f.label} ${f.value} vs ${f.target}`).join('; ');
          return lang === 'en'
            ? `Weak rental. Falls short on: ${reasons}.`
            : `Renta débil. No cumple en: ${reasons}.`;
        }
        return lang === 'en'
          ? `Weak rental. Does not meet minimum criteria.`
          : `Renta débil. No cumple criterios mínimos.`;
      }
    }
    
    // FLIP strategy - keep existing logic
    const decision = SSOT?.deal_classification?.decision_label || '';
    const profit = ssotNetProfit;
    const margin = ssotProfitMargin;
    const targetProfit = classificationCriteria?.min_profit || 20000;
    const targetMargin = classificationCriteria?.min_margin || 10;
    
    if (decision.includes('GOOD') || decision.includes('BUY')) {
      if (profit >= targetProfit && margin >= targetMargin) {
        return lang === 'en' 
          ? `Strong deal. $${Math.round(profit).toLocaleString()} profit (${margin.toFixed(1)}% margin) exceeds your targets.`
          : `Buen trato. $${Math.round(profit).toLocaleString()} ganancia (${margin.toFixed(1)}% margen) excede tus objetivos.`;
      }
      return lang === 'en'
        ? `Meets your profit criteria. Consider proceeding.`
        : `Cumple con tus criterios de ganancia. Considera proceder.`;
    } else if (decision.includes('MARGINAL') || decision.includes('NEGOTIATE')) {
      const profitGap = targetProfit - profit;
      if (profitGap > 0) {
        return lang === 'en'
          ? `$${Math.round(profitGap).toLocaleString()} below your profit target. Negotiate a lower price.`
          : `$${Math.round(profitGap).toLocaleString()} por debajo de tu objetivo. Negocia un precio más bajo.`;
      }
      return lang === 'en'
        ? `Close to your targets but needs improvement. Consider negotiating.`
        : `Cerca de tus objetivos pero necesita mejora. Considera negociar.`;
    } else {
      return lang === 'en'
        ? `Does not meet your investment criteria. Pass or negotiate significant discount.`
        : `No cumple tus criterios de inversión. Pasa o negocia un descuento significativo.`;
    }
  }, [SSOT, ssotNetProfit, ssotProfitMargin, classificationCriteria, lang, analysis, arvBlocked, assumptions,
      liveRental.isCash]);

  // V5.0: Generate single action suggestion for MinimalDecisionView
  const getActionSuggestion = useMemo(() => {
    // BLOCKED ARV: no discount/offer figure can be honest without a reliable ARV
    // (this printed "Need at least $127,955 discount" while Net Profit read "Add comps").
    if (arvBlocked) {
      return lang === 'en'
        ? 'Add renovated comps to get a reliable ARV before making an offer.'
        : 'Agregue comps renovados para obtener un ARV confiable antes de ofertar.';
    }
    // RENTAL strategy - use rental metrics
    if (analysis?.strategy === 'rental') {
      const cashflow = analysis?.rental_analysis?.monthly_cashflow || 0;
      const dscr = analysis?.rental_analysis?.dscr || 0;
      const rentalDecision = analysis?.rental_classification?.decision || '';
      const purchasePrice = analysis?.purchase_price || 0;
      
      // CASH purchase: there is no loan to qualify for and no financing terms to review.
      // The only levers are price and rent, so never route a cash deal into DSCR advice.
      const isCashRental = liveRental.isCash;

      if (rentalDecision === 'BUY' || rentalDecision === 'FLEX_BUY') {
        return lang === 'en'
          ? `Move forward. This rental meets your criteria.`
          : `Procede. Esta renta cumple tus criterios.`;
      } else if (rentalDecision === 'SPECULATIVE') {
        return lang === 'en'
          ? `Consider negotiating a lower price or increasing rent.`
          : `Considera negociar un precio más bajo o aumentar la renta.`;
      } else {
        if (isCashRental) {
          return lang === 'en'
            ? `Negotiate a lower price or raise the rent to improve cash flow and cap rate.`
            : `Negocia un precio más bajo o sube la renta para mejorar el flujo de caja y el cap rate.`;
        }
        const minRentNeeded = minRentForDscr;   // PITIA x minDSCR — see minRentForDscr
        const currentRent = liveMonthlyRent;
        if (currentRent < minRentNeeded) {
          return lang === 'en'
            ? `Need $${Math.round(minRentNeeded).toLocaleString()}/mo rent to qualify for DSCR. Currently $${Math.round(currentRent).toLocaleString()}/mo.`
            : `Necesitas $${Math.round(minRentNeeded).toLocaleString()}/mo para calificar DSCR. Actualmente $${Math.round(currentRent).toLocaleString()}/mo.`;
        }
        return lang === 'en'
          ? `Does not meet rental criteria. Review financing terms.`
          : `No cumple criterios de renta. Revisa términos de financiamiento.`;
      }
    }
    
    // FLIP strategy - keep existing logic
    const decision = SSOT?.deal_classification?.decision_label || '';
    const profit = ssotNetProfit;
    const purchasePrice = SSOT?.purchase_price || analysis?.purchase_price || 0;
    const targetProfit = classificationCriteria?.min_profit || 20000;
    
    if (decision.includes('GOOD') || decision.includes('BUY')) {
      return lang === 'en'
        ? `Move forward with your offer at $${Math.round(purchasePrice).toLocaleString()}.`
        : `Procede con tu oferta a $${Math.round(purchasePrice).toLocaleString()}.`;
    } else if (decision.includes('MARGINAL') || decision.includes('NEGOTIATE')) {
      const profitGap = targetProfit - profit;
      const suggestedPrice = purchasePrice - Math.max(profitGap, 0);
      return lang === 'en'
        ? `Reduce purchase price to $${Math.round(suggestedPrice).toLocaleString()} to meet your profit target.`
        : `Reduce el precio a $${Math.round(suggestedPrice).toLocaleString()} para alcanzar tu objetivo.`;
    } else {
      const minDiscount = Math.round(Math.abs(profit) + targetProfit);
      return lang === 'en'
        ? `Need at least $${minDiscount.toLocaleString()} discount to make this work.`
        : `Necesitas al menos $${minDiscount.toLocaleString()} de descuento para que funcione.`;
    }
  }, [SSOT, ssotNetProfit, analysis, classificationCriteria, lang, arvBlocked, assumptions,
      liveRental.isCash, minRentForDscr, liveMonthlyRent]);

  // Helper to update assumptions and trigger recalculation
  const updateAssumptions = useCallback((updater) => {
    setAssumptions(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      return newState;
    });
    setUserChangedAssumptions(true);
  }, []);

  // Always-current assumptions, readable synchronously from an event handler.
  // Reading `assumptions` from a callback's closure risks a stale render copy; reading it
  // back out of a setState updater risks the opposite — React only runs that updater
  // synchronously as an eager-state optimization when the fiber has no pending work, so a
  // handler that captures the composed value from inside the updater silently gets
  // nothing whenever that optimization does not apply. A ref is correct in both cases.
  const assumptionsRef = useRef(assumptions);
  assumptionsRef.current = assumptions;

  // ============================================================================
  // RENTAL BREAKDOWN — STAGED EDITS (BUG 3)
  // ============================================================================
  // The rental inputs used to recalculate on every keystroke/blur, so there was no
  // "Apply Changes & Recalculate" step at all — unlike Fix & Flip, whose assumptions
  // panel stages edits and applies them on a button. Typing a rent then a tax figure
  // fired two overlapping server recalcs and the numbers churned while you worked.
  //
  // Rental edits now STAGE into `rentalDraft` and change nothing until applied. Reads
  // go through rentalField() so an input shows the pending value; the applied numbers
  // (cash flow, DSCR, cap rate) keep showing the last RECALCULATED state, which is what
  // makes the button meaningful.

  const applyRentalDraft = useCallback(() => {
    if (!rentalDraft || Object.keys(rentalDraft).length === 0) return;
    // Compose from the ref, NOT from inside a setState updater. Capturing the composed
    // value inside the updater left it null whenever React deferred the updater to the
    // next render, so the recalculation below was skipped entirely — clicking Apply after
    // switching to Cash updated the stored assumption but never re-ran the numbers, and
    // the page kept showing the financed DSCR/PITI until some later edit happened to
    // trigger a recalc.
    const composed = { ...assumptionsRef.current, ...rentalDraft };
    setAssumptions(composed);
    setRentalDraft(null);
    // Recalculate directly rather than via setUserChangedAssumptions — that flag drives a
    // debounced effect which would fire a SECOND identical recalc for the same click.
    recalculateAnalysisRef.current?.(composed);
  }, [rentalDraft]);

  const discardRentalDraft = useCallback(() => setRentalDraft(null), []);

  // Debounced recalculation - uses currentARV to reflect mode switching
  // CRITICAL FIX: Now properly handles both Flip and Rental strategies with correct parameters
  // NOTE: overrides are a NAMED options object ({ arvOverride, purchasePriceOverride }),
  // not positional args. A positional signature previously let a caller pass the purchase
  // price into the arvOverride slot, which set ARV = purchase price. Named keys make that
  // class of bug impossible — callers must say which value they mean.
  const recalculateAnalysis = useCallback(async (newAssumptions, propOverrides = propertyOverrides, options = {}) => {
    if (sampleBlocked()) return;
    const { arvOverride = null, purchasePriceOverride = null, arvStatusOverride, activeModeOverride } = options || {};
    const _latest = latestAnalysisRef.current || analysis;
    // G2: which mode these financials are for, and the status of THAT mode's ARV. From
    // the options when the caller just adopted a mode result (same tick as setAnalysis),
    // else from the freshest analysis we hold — never from this callback's closure.
    const _activeMode = activeModeOverride || _latest?.active_arv_mode || 'both';
    const _arvStatus = arvStatusOverride !== undefined ? arvStatusOverride : (_latest?.arv_details?.arv_status ?? null);
    if (!originalAnalysis) {
      console.warn('[recalculateAnalysis] No originalAnalysis - aborting');
      return;
    }
    
    // Determine current strategy (single source of truth)
    const currentStrategy = analysis?.strategy || originalAnalysis?.strategy || 'flip';
    const isRentalStrategy = currentStrategy === 'rental';
    
    // DEBUG: Log the financing mode being requested
    console.log('[recalculateAnalysis] Called with:', {
      strategy: currentStrategy,
      purchaseFinancing: newAssumptions.purchaseFinancing,
      rehabFinancing: newAssumptions.rehabFinancing,
      currentARV,
      hasPropertyOverrides
    });
    
    // Claim this recalc. Any response that is not the newest one is discarded below.
    const mySeq = ++recalcSeqRef.current;
    const isStale = () => mySeq !== recalcSeqRef.current;

    setRecalculating(true);
    try {
      // Use overrides if present, otherwise use original values
      const sqft = propOverrides.sqft || originalAnalysis.sqft || 1500;
      const beds = propOverrides.beds || originalAnalysis.beds || 3;
      const baths = propOverrides.baths || originalAnalysis.baths || 2;
      const yearBuilt = propOverrides.year_built || originalAnalysis.year_built || 1980;
      
      // Use the ARV override if provided, otherwise use currentARV. PROBLEM 1 GUARD:
      // coerce to a clean number and NEVER send 0/NaN when a real ARV exists — a zero ARV
      // makes the backend MAO negative and floors it to $1 (the "Target Offer $1" bug).
      const _cleanNum = (v) => {
        const n = typeof v === 'string' ? parseFormattedNumber(v) : Number(v);
        return (n === null || isNaN(n)) ? 0 : n;
      };
      const _arvCandidate = _cleanNum(arvOverride !== null ? arvOverride : currentARV);
      const arvToUse = _arvCandidate > 0
        ? _arvCandidate
        : _cleanNum(analysis?.arv || originalAnalysis?.arv || 0);

      // Use purchase price override if provided, otherwise use editedPurchasePrice or original.
      // Coerce to a clean number so a formatted string can never reach the backend/calc.
      const purchasePriceToUse = _cleanNum(
        purchasePriceOverride !== null
          ? purchasePriceOverride
          : (editedPurchasePrice !== null ? editedPurchasePrice : originalAnalysis.purchase_price)
      );
      
      // Build the request payload with strategy-specific parameters
      const requestPayload = {
        // CRITICAL: Tell backend which analysis type to run
        analysis_type: currentStrategy,
        // Property data
        sqft: sqft,
        beds: beds,
        baths: baths,
        year_built: yearBuilt,
        purchase_price: purchasePriceToUse,
        arv: arvToUse,
        // A: the status of the ARV on screen (active mode), so the backend can say whether
        // the verdict it builds from it is reliable — instead of the frontend carrying the
        // verdict_reliable=false written by the FIRST analysis forever.
        arv_status: _arvStatus,
        // G2: the mode this recalculation belongs to — the backend stores the financials
        // under arv_by_mode.<mode> and decides the verdict from that mode's own record.
        active_arv_mode: _activeMode,
        // B: which stored analysis this belongs to, so the recalculated financials are
        // written back (after the ownership check) and a reload shows ARV + profit from
        // the same computation. Absent before anything is saved: then nothing is written.
        analysis_id: analysis?.analysis_id || analysis?.id || originalAnalysis?.analysis_id || originalAnalysis?.id || null,
        rent_estimate: originalAnalysis.rent_estimate,
        tax_assessed_value: originalAnalysis.property_snapshot?.tax_assessed_value,
        property_type: propOverrides.property_type || originalAnalysis.property_snapshot?.property_type || null,
        property_overrides: hasPropertyOverrides ? propOverrides : null,
        // P4: lets the backend recompute the ARV for an edited subject from the CACHED
        // comp pool (no RentCast call) — subject-edit one-brain recompute.
        property_key: analysis?.property_key || originalAnalysis?.property_key || null,
        // Language for narrative generation
        language: lang,
        // CRITICAL: Pass through arv_confidence_score for consistent scoring
        // Carried so the multi-family recompute can re-apply the user's declared gap;
        // the normal recalculate path ignores them because the `arv` we send is already
        // deducted. Read from the analysis, not from local state — a re-run or a reload
        // must not quietly drop what the user declared on the confirmation screen.
        // Read back from the analysis the engine already returned, rather than adding
        // echo fields to the response: arv_details.feature_gap_adjustment.applied is
        // written whenever a gap was declared, and it is the same object the ARV was
        // deducted with — so there is no second copy of this state to drift.
        missing_garage: (analysis?.arv_details?.feature_gap_adjustment
          ?? originalAnalysis?.arv_details?.feature_gap_adjustment)?.applied?.garage?.spaces ?? null,
        missing_basement: (analysis?.arv_details?.feature_gap_adjustment
          ?? originalAnalysis?.arv_details?.feature_gap_adjustment)?.applied?.basement?.finish ?? null,
        arv_confidence_score: (() => {
          // ?? NOT ||. A confidence of 0 is a MEASUREMENT — an ARV that could not be
          // computed — and `||` treated it as absent, sending 80 instead. That is not a
          // rounding difference: comps_score takes this value directly, so the deal went
          // from 56 (not_recommended) to 72 (moderate). A falsy check silently upgraded
          // the verdict of a property with no usable comps. Undefined still means "not
          // supplied", and the backend answers that with a neutral 50, not an optimistic 80.
          const score = currentARVConfidenceScore
            ?? analysis?.arv_confidence_score
            ?? originalAnalysis?.arv_confidence_score
            ?? null;
          console.log('[recalculateAnalysis] arv_confidence_score being sent:', score, {
            currentARVConfidenceScore,
            analysisScore: analysis?.arv_confidence_score,
            originalScore: originalAnalysis?.arv_confidence_score
          });
          return score;
        })(),
        // Rehab parameters (used by both strategies)
        // rehab_condition + the two custom amounts. See src/lib/rehabPayload.js for why the
        // amount is gated on the input mode and never on the condition label.
        ...rehabRequestFields(newAssumptions, parseFormattedNumber),
        // Annual costs - ONLY for FLIP strategy (Rental uses rental-specific fields)
        // These will be overwritten by rental-specific values in the strategy block below
        closing_costs_percent: parseFloat(newAssumptions.closingCostsPercent ?? 3),
      };
      
      // ========================================
      // STRATEGY-SPECIFIC PARAMETERS
      // ========================================
      if (isRentalStrategy) {
        // RENTAL STRATEGY - Use rental-specific parameters
        // CRITICAL: Use rentalAnnualTaxes/rentalAnnualInsurance to preserve values across toggles
        Object.assign(requestPayload, {
          // CRITICAL: These are the rental financing fields the backend expects
          rental_financing_type: newAssumptions.rentalFinancingType || 'dscr',
          rental_down_payment_pct: parseFloat(newAssumptions.rentalDownPaymentPct ?? 25),
          rental_interest_rate: parseFloat(newAssumptions.rentalInterestRate ?? 7.5),
          rental_loan_term_years: parseInt(newAssumptions.rentalLoanTermYears ?? 30),
          rental_loan_points: parseFloat(newAssumptions.rentalLoanPoints ?? DEFAULT_RENTAL_ORIGINATION_POINTS),
          rental_property_condition: newAssumptions.rentalPropertyCondition || 'turnkey',
          // REFINANCE: without these the backend rebuilt every recalc as a PURCHASE and
          // charged the owner a down payment on a house they already own.
          rental_purchase_type: newAssumptions.rentalPurchaseType || 'new_purchase',
          // Whose valuation is on screen. Carried so the backend, the PDF and the share
          // link all say the same thing about it.
          arv_is_user_override: isUserArv,
          appraised_value: newAssumptions.rentalAppraisedValue ?? originalAnalysis?.rental_analysis?.appraised_value ?? null,
          existing_payoff: newAssumptions.rentalExistingPayoff ?? 0,
          refi_ltv: newAssumptions.rentalRefiLtv ?? 75,
          // RENTAL OPERATING COSTS - Use rental-specific fields (NOT generic propertyTaxes/insurance)
          // These are passed as ANNUAL values to the backend
          annual_property_taxes: parseFloat(newAssumptions.rentalAnnualTaxes || 0),
          annual_insurance: parseFloat(newAssumptions.rentalAnnualInsurance || 0),
          annual_utilities: parseFloat(newAssumptions.rentalUtilities || 0),
          // Rental percentage-based operating costs
          rental_vacancy_pct: parseFloat(newAssumptions.rentalVacancyPct ?? 5.0),
          rental_maintenance_pct: parseFloat(newAssumptions.rentalMaintenancePct ?? 8.0),
          rental_management_pct: parseFloat(newAssumptions.rentalManagementPct ?? 10.0),
          rental_hoa: parseFloat(newAssumptions.rentalHoa || 0),
          rental_other_opex: parseFloat(newAssumptions.rentalOtherOpex || 0),
          // Rental income
          rent_estimate: parseFloat(newAssumptions.rentalMonthlyRent || originalAnalysis?.rent_estimate || 0),
          // Rental criteria for classification.
          // BUG C: these were NEVER SENT. The recalculate endpoint therefore fell back to
          // its own defaults (0 / 1.10 DSCR / 6% cap / 100% of market) and judged the deal
          // against those, so a user who saved a 1.0 DSCR target kept being scored at 1.10
          // — the saved criterion appeared not to "stick" because it never left the browser.
          // `??` so a deliberate 0 threshold survives.
          // Fall back to the criteria the CURRENT analysis was already judged against
          // before giving up and letting the backend default. The criteria fetch is async:
          // a recalc firing before it lands sent null, and the user's saved 1.0 DSCR target
          // was silently replaced by the 1.10 default — the very bug this block fixes,
          // reintroduced through a race. The analysis already carries what it used, so
          // there is no window in which the threshold is unknown.
          rental_min_cash_flow: investmentCriteria?.rental?.min_monthly_cash_flow
            ?? analysis?.deal_decision?.user_criteria?.min_monthly_cash_flow ?? null,
          rental_min_dscr: investmentCriteria?.rental?.min_dscr
            ?? analysis?.deal_decision?.user_criteria?.min_dscr ?? null,
          rental_min_cap_rate: investmentCriteria?.rental?.min_cap_rate
            ?? analysis?.deal_decision?.user_criteria?.min_cap_rate ?? null,
          rental_max_price_pct: investmentCriteria?.rental?.max_price_vs_market_pct
            ?? analysis?.deal_decision?.user_criteria?.max_price_vs_market_pct ?? null,
          rental_tolerance_mode: newAssumptions.toleranceMode || 'balanced',
          // Rental closing costs.
          // BUG 4: three of these keys did NOT match the backend RecalculateRequest model
          // (rental_title_fees / rental_escrows / rental_other_closing), so FastAPI dropped
          // them and title, escrow and processing fees vanished from the calculation on the
          // first recalc — silently zeroing costs the user had entered up front. The names
          // below are the model's actual field names.
          rental_closing_costs_pct: parseFloat(newAssumptions.rentalClosingCostsPct ?? 3.0),
          rental_origination_points: parseFloat(newAssumptions.rentalOriginationPoints ?? DEFAULT_RENTAL_ORIGINATION_POINTS),
          rental_lender_fees: parseFloat(newAssumptions.rentalLenderFees || 0),
          rental_title_attorney_fees: parseFloat(newAssumptions.rentalTitleAttorneyFees || 0),
          rental_escrow_reserves: parseFloat(newAssumptions.rentalEscrowReserves || 0),
          rental_other_closing_costs: parseFloat(newAssumptions.rentalOtherClosingCosts || 0),
          rental_seller_agent_commission: parseFloat(newAssumptions.rentalSellerAgentCommission || 0),
        });
      } else {
        // FLIP STRATEGY - Use flip-specific financing parameters
        Object.assign(requestPayload, {
          holding_period: parseInt(newAssumptions.holdingPeriod),
          loan_interest: parseFloat(newAssumptions.loanInterest),
          loan_points: parseFloat(newAssumptions.loanPoints ?? 2),
          agent_commission: parseFloat(newAssumptions.agentCommission),
          property_taxes: newAssumptions.propertyTaxes ? parseFormattedNumber(newAssumptions.propertyTaxes) : null,
          insurance: newAssumptions.insurance ? parseFormattedNumber(newAssumptions.insurance) : null,
          utilities: newAssumptions.utilities ? parseFormattedNumber(newAssumptions.utilities) : null,
          other_holding_costs: newAssumptions.otherHoldingCosts ? parseFormattedNumber(newAssumptions.otherHoldingCosts) : null,
          down_payment_pct: parseFloat(newAssumptions.downPaymentPct ?? 20),
          // Flip financing structure
          purchase_financing: newAssumptions.purchaseFinancing,
          // The manual "$ Amount" down payment reaches the backend HERE or nowhere: there is
          // no dollar-down field in the request, only purchase_ltv. Until this line, typing
          // an amount changed two display strings and left the loan, the interest, the
          // points, Total Cash In and Cash-on-Cash on the untouched LTV.
          // In 'ltv' mode this returns the field verbatim, so the LTV path is unchanged.
          purchase_ltv: impliedPurchaseLtv({
            mode: downPaymentMode,
            manualDownPayment,
            // The SAME price the request carries, so the backend divides by what we did.
            purchasePrice: purchasePriceToUse,
            ltv: newAssumptions.purchaseLTV ?? 80,
          }),
          purchase_interest_rate: parseFloat(newAssumptions.purchaseInterestRate ?? 12),
          purchase_loan_points: parseFloat(newAssumptions.purchaseLoanPoints ?? DEFAULT_FLIP_ORIGINATION_POINTS),
          purchase_lender_fees: newAssumptions.purchaseLenderFees ? parseFormattedNumber(newAssumptions.purchaseLenderFees) : 0,
          rehab_financing: newAssumptions.rehabFinancing,
          rehab_financed_pct: parseFloat(newAssumptions.rehabFinancedPct ?? 100),
          rehab_interest_rate: parseFloat(newAssumptions.rehabInterestRate ?? 12),
          rehab_loan_points: parseFloat(newAssumptions.rehabLoanPoints || 0),
          // MAO Rule
          arv_multiplier: parseFloat(newAssumptions.maoRule || 70) / 100,
          // Deal Tolerance
          tolerance_mode: newAssumptions.toleranceMode || 'balanced',
          tolerance_price_pct: parseFloat(newAssumptions.tolerancePricePct ?? 1),
          tolerance_profit_dollars: parseFloat(newAssumptions.toleranceProfitDollars ?? 2500),
          // V4.0: User-defined criteria for decision engine
          min_profit: classificationCriteria?.min_profit || 20000,
          min_margin: classificationCriteria?.min_margin || 10,
        });
        // MULTI-FAMILY FLIP: send the edited rent-per-unit as the rent-of-record
        // (total monthly) so the multi-family ARV income/GRM uses it, shared with
        // rental/BRRRR. SFR flip omits this (no income side).
        const flipUnitCount = analysis?.arv_details?.income_approach?.unit_count
          || analysis?.property_snapshot?.unit_count
          || analysis?.rental_analysis?.unit_count || 1;
        // BUG 4 FIX: only send the rent-of-record re-blend when the user ACTUALLY edited
        // rent or GRM from the stored baseline. Previously flipRentPerUnit was pre-filled
        // (always > 0), so EVERY recalc — including a pure financing toggle — re-blended the
        // ARV, letting financing change the ARV. ARV must only change from comps/subject/rent
        // inputs, never from financing. When neither is edited we omit this block; the backend
        // then echoes request.arv unchanged (arv_details preserved), so the headline ARV and
        // the income section stay identical across a Cash<->Financed toggle.
        const _baseRent = parseFloat(
          analysis?.arv_details?.income_approach?.monthly_rent_per_unit
          || analysis?.rent_estimate || 0) || 0;
        const _baseGrm = parseFloat(
          analysis?.arv_details?.income_approach?.grm
          || analysis?.arv_details?.income_approach?.gross_rent_multiplier || 0) || 0;
        const _newRent = parseFloat(newAssumptions.flipRentPerUnit) || 0;
        const _newGrm = parseFloat(newAssumptions.flipGrm) || 0;
        const _rentEdited = _newRent > 0 && Math.abs(_newRent - _baseRent) > 0.01;
        const _grmEdited = _newGrm > 0 && Math.abs(_newGrm - _baseGrm) > 0.01;
        if (flipUnitCount > 1 && (_rentEdited || _grmEdited || (newAssumptions.flipRentIsUser && _newRent > 0))) {
          requestPayload.user_rent = parseFloat(newAssumptions.flipRentPerUnit) * flipUnitCount;
          requestPayload.unit_count = flipUnitCount;
          // D: tell the backend which pool the ARV on screen came from. In 'user' mode it
          // must NOT rebuild the ARV from the stored sales component on a rent edit —
          // the user's comparables are the valuation.
          requestPayload.active_arv_mode = _activeMode;
          // Stored ARV components so the backend recomputes income = rent × GRM and
          // re-blends 20/80 with the sales component — NO new RentCast call.
          requestPayload.sales_approach_value =
            analysis?.arv_details?.blended_arv?.sales_approach_value
            || analysis?.arv_details?.sales_approach_value || null;
          // ONLY a GRM the user actually typed. The `|| income_approach?.grm ||
          // gross_rent_multiplier` fallback that used to sit here is why clearing the
          // field changed nothing: the derived GRM went out anyway, and the backend reads
          // any `multi_family_grm` as an override -> income-led 20/80. That is the exact
          // treatment the engine reserves for human judgment and refuses to give a
          // comp-derived number.
          //
          // Null means "no opinion", and the engine then derives its own GRM and weights
          // it as derived — which is the whole point. Same `||`-of-convenience that let a
          // building rent land in a per-unit field: it erased the difference between
          // "typed" and "pre-filled".
          requestPayload.multi_family_grm =
            parseFloat(newAssumptions.flipGrm) > 0 ? parseFloat(newAssumptions.flipGrm) : null;
        }
      }
      
      const response = await analysisAPI.recalculate(requestPayload);

      // A newer edit already fired: drop this result rather than reverting that edit.
      if (isStale()) {
        console.log('[recalculateAnalysis] Discarding stale response (superseded by a newer edit)');
        return;
      }

      // DEBUG: Log the response to verify correct data is returned
      console.log('[recalculateAnalysis] Response received:', {
        flip_analysis: response.data?.flip_analysis ? {
          net_profit: response.data.flip_analysis.net_profit,
          loan_costs: response.data.flip_analysis.loan_costs,
          // flip_analysis has no `total_investment`; the payload names are
          // total_project_cost and cash_invested.
          total_project_cost: response.data.flip_analysis.total_project_cost,
          cash_invested: response.data.flip_analysis.cash_invested
        } : null,
        deal_score: response.data?.deal_score,
        deal_rating: response.data?.deal_rating
      });
      
      // Merge recalculated data with analysis, including current ARV and purchase price
      // CRITICAL: Update rental_classification and deal_decision for rental strategy
      // CRITICAL: Also update property_snapshot when there are overrides (SINGLE SOURCE OF TRUTH)
      setAnalysis(prev => {
        // Build updated property_snapshot if we have overrides
        const updatedPropertySnapshot = hasPropertyOverrides ? {
          ...prev.property_snapshot,
          property_type: propOverrides.property_type || prev.property_snapshot?.property_type,
          beds: propOverrides.beds || prev.property_snapshot?.beds,
          baths: propOverrides.baths || prev.property_snapshot?.baths,
          sqft: propOverrides.sqft || prev.property_snapshot?.sqft,
          year_built: propOverrides.year_built || prev.property_snapshot?.year_built,
        } : prev.property_snapshot;
        
        return {
          ...prev,
          sqft: sqft,
          beds: beds,
          baths: baths,
          year_built: yearBuilt,
          // Use the backend-recomputed ARV when present (multi-family rent-of-record
          // edit re-blends income×GRM with the sales component); else keep current.
          // Guard on > 0 (not != null) so a 0/blank ARV can never replace a real value.
          arv: (response.data.arv > 0 ? response.data.arv : arvToUse),
          // A: adopt the backend's verdict for THIS ARV. `...prev` used to carry the
          // first analysis's false past every recalculation.
          verdict_reliable: response.data.verdict_reliable ?? prev.verdict_reliable,
          // P4: when the backend recomputed from cached comps (subject edit), refresh
          // the ARV details + the exact comps used so the comp table stays in sync.
          arv_details: response.data.arv_details
            || (response.data.income_approach
                ? { ...(prev.arv_details || {}), income_approach: response.data.income_approach }
                : prev.arv_details),
          comps_used: response.data.comps_used || prev.comps_used,
          comps_excluded: response.data.comps_excluded || prev.comps_excluded,
          purchase_price: purchasePriceToUse,
          rehab_breakdown: response.data.rehab_breakdown,
          rehab_estimate: response.data.rehab_estimate,
          flip_analysis: response.data.flip_analysis,
          rental_analysis: response.data.rental_analysis,
          rental_classification: response.data.rental_classification,
          deal_score: response.data.deal_score,
          deal_rating: response.data.deal_rating,
          deal_classification: response.data.deal_classification,
          // deal_decision now arrives fresh from the backend for rental, so there is
          // nothing left to substitute. The old fallback chain fell through to
          // rental_classification, which has NO criteria_passed field — `undefined || 0`
          // then rendered "0/4 Passed" next to a breakdown showing four criteria met.
          // Never swap in an object of a different shape: keep the previous one and say
          // so, loudly, rather than printing a number no calculation produced.
          deal_decision: (() => {
            if (response.data.deal_decision) return response.data.deal_decision;
            const strat = analysis?.strategy || originalAnalysis?.strategy;
            if (strat === 'rental' || strat === 'brrrr') {
              console.error(
                '[recalculateAnalysis] backend returned no deal_decision for a rental ' +
                'recalculation — criteria counters will show the PREVIOUS values. This is ' +
                'a backend contract violation, not a display quirk.'
              );
            }
            return prev.deal_decision;
          })(),
          // Regenerated by the backend from the new financials. Left out of this merge,
          // the spread above carried the previous analysis's judgement forward: a rental
          // at DSCR 1.56 kept the "Low DSCR for financing" written when rent was $1.
          risk_factors: response.data.risk_factors ?? prev.risk_factors,
          opportunities: response.data.opportunities ?? prev.opportunities,
          deal_explanation: response.data.deal_explanation,
          has_user_overrides: hasPropertyOverrides,
          // Update property_snapshot with confirmed values
          property_snapshot: updatedPropertySnapshot,
          // Also update top-level convenience fields
          property_type: propOverrides.property_type || prev.property_type,
        };
      });
      
      // Mark as having unsaved changes
      setHasUnsavedChanges(true);
      
    } catch (error) {
      console.error('Recalculation error:', error);
      console.error('Recalculation error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        newAssumptions: {
          purchaseFinancing: newAssumptions.purchaseFinancing,
          rehabFinancing: newAssumptions.rehabFinancing,
        }
      });
      
      // CRITICAL FIX: On error, show specific message and DO NOT leave stale data
      // Reset the assumptions to trigger a visible state change
      const errorMsg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(
        lang === 'en' 
          ? `Recalculation failed: ${errorMsg}` 
          : `Error al recalcular: ${errorMsg}`
      );
      
      // If the error is related to financing mode change, revert the UI toggle state
      // so user doesn't see stale data with wrong mode indicator
      // Note: We intentionally do NOT revert assumptions here - that would cause infinite loops
      // Instead we show a clear error and let user retry
    } finally {
      // Only the newest recalc may clear the spinner — an older one finishing must not
      // make the UI look settled while a newer request is still in flight.
      if (!isStale()) setRecalculating(false);
    }
  }, [sampleBlocked, originalAnalysis, analysis, lang, propertyOverrides, hasPropertyOverrides, currentARV, editedPurchasePrice, currentARVConfidenceScore, investmentCriteria,
      // Both are plain component state, not part of `assumptions`, so they arrive only
      // through this closure. Left out, the callback would keep sending the down payment
      // from before the last keystroke — the stale-closure bug the rehab flag already hit.
      downPaymentMode, manualDownPayment]);

  // Keep the ref pointing at the latest recalculateAnalysis for applyRentalDraft.
  recalculateAnalysisRef.current = recalculateAnalysis;

  // Handle manual market data refresh (user-triggered only)
  const handleRefreshMarketData = useCallback(async () => {
    if (sampleBlocked()) return;
    if (!analysis?.property_key || !analysis?.address) {
      toast.error(lang === 'en' ? 'Cannot refresh: missing property data' : 'No se puede actualizar: faltan datos de la propiedad');
      return;
    }
    
    setIsRefreshingData(true);
    try {
      const response = await analysisAPI.refreshMarketData(analysis.property_key, analysis.address);
      
      if (response.data?.success) {
        // Update the analysis state with new data freshness info
        setAnalysis(prev => ({
          ...prev,
          data_freshness: response.data.data_freshness,
          rent_estimate: response.data.rent_estimate || prev.rent_estimate,
          arv: response.data.arv || prev.arv,
        }));
        
        toast.success(lang === 'en' ? 'Market data refreshed!' : '¡Datos de mercado actualizados!');
        
        // Trigger recalculation with new data
        recalculateAnalysis(assumptions);
      } else {
        toast.error(response.data?.message || (lang === 'en' ? 'Refresh failed' : 'Error al actualizar'));
      }
    } catch (error) {
      console.error('Market data refresh error:', error);
      toast.error(lang === 'en' ? 'Failed to refresh market data' : 'Error al actualizar datos de mercado');
    } finally {
      setIsRefreshingData(false);
    }
  }, [sampleBlocked, analysis, lang, assumptions, recalculateAnalysis]);

  // PERSISTENCE: Save analysis to sessionStorage whenever it changes
  useEffect(() => {
    if (analysis) {
      try {
        sessionStorage.setItem('chekdeal_last_analysis', JSON.stringify(analysis));
      } catch (e) {
        console.warn('Failed to save analysis to sessionStorage:', e);
      }
    }
  }, [analysis]);

  // PERSISTENCE: Save assumptions to sessionStorage whenever they change
  // This is CRITICAL for preserving user-edited Rental values (rent, taxes, insurance, financing type)
  useEffect(() => {
    if (assumptions && analysis) {
      try {
        // Include address to only restore for same property
        const assumptionsToSave = { 
          ...assumptions, 
          _address: analysis?.address || analysis?.property_address || '' 
        };
        sessionStorage.setItem('chekdeal_last_assumptions', JSON.stringify(assumptionsToSave));
        console.log('[Results] Saved assumptions to sessionStorage (with address)');
      } catch (e) {
        console.warn('Failed to save assumptions to sessionStorage:', e);
      }
    }
  }, [assumptions, analysis]);

  // PROBLEM 1: persist the FULL working state (edited analysis + assumptions + property
  // overrides + price/down-payment edits) keyed by analysis id, so navigating away and
  // back restores every edit for THIS deal. getInitialAnalysis + the useState inits read
  // this key back (CASE 0.5). Keyed by id => never leaks into a different deal.
  useEffect(() => {
    if (!WORKING_KEY || !analysis) return;
    try {
      sessionStorage.setItem(WORKING_KEY, JSON.stringify({
        analysis,
        assumptions,
        financingByMode,
        propertyOverrides,
        hasPropertyOverrides,
        editedPurchasePrice,
        manualDownPayment,
        downPaymentMode,
        // Without this, reopening a saved analysis forgets that rehab financing was a
        // deliberate choice and the purchase toggle starts overwriting it again.
        rehabFinancingTouched,
        _savedAt: Date.now(),
      }));
    } catch (e) {
      console.warn('Failed to save working state:', e);
    }
  }, [WORKING_KEY, analysis, assumptions, financingByMode, propertyOverrides, hasPropertyOverrides, editedPurchasePrice, manualDownPayment, downPaymentMode, rehabFinancingTouched]);

  // PER-MODE FINANCING: keep the ACTIVE mode's bucket in sync with the live financing
  // inputs, so editing LTV/fees/points/down-payment records into financingByMode[mode].
  // The OTHER mode's bucket is untouched — that's what preserves its terms across toggles.
  useEffect(() => {
    const mode = assumptions?.purchaseFinancing === 'financed' ? 'financed' : 'cash';
    setFinancingByMode((prev) => ({
      ...prev,
      [mode]: { ...pickFinancing(assumptions), downPaymentMode, manualDownPayment },
    }));
  }, [assumptions, downPaymentMode, manualDownPayment]);

  // PER-MODE FINANCING: switch financing mode, loading THIS mode's saved financing bucket
  // (LTV/rates/points/fees/down-payment) into the live inputs. The other mode's bucket is
  // left intact, so toggling Cash<->Financed never wipes the terms you set. Shared inputs
  // (rehab/price/taxes/...) are untouched.
  const switchFinancingMode = useCallback((newMode) => {
    const target = newMode === 'financed' ? 'financed' : 'cash';
    // For a RENTAL the live mode is the rental financing type, not purchaseFinancing —
    // otherwise the toggle early-returned as a no-op and the rental never switched.
    const isRentalDeal = (analysis?.strategy || originalAnalysis?.strategy) === 'rental';
    const liveRentalFin = liveRental.financingType;
    const oldMode = isRentalDeal
      ? (liveRentalFin === 'cash' ? 'cash' : 'financed')
      : (assumptions?.purchaseFinancing === 'financed' ? 'financed' : 'cash');
    if (target === oldMode) return;
    // Snapshot current live financing into the OLD bucket; leave the other bucket intact.
    const oldSnapshot = { ...pickFinancing(assumptions), downPaymentMode, manualDownPayment };
    const nextByMode = { ...financingByMode, [oldMode]: oldSnapshot };
    const bucket = nextByMode[target] || {};
    setFinancingByMode(nextByMode);
    // Load the target mode's saved financing into the live inputs (shared inputs untouched).
    const newAssumptions = {
      ...assumptions,
      ...pickFinancing(bucket),
      purchaseFinancing: target,
      // Empty once the user has chosen for rehab, so the purchase toggle stops writing it.
      // See src/lib/rehabFinancingInheritance.js for why the flag exists.
      ...rehabFinancingOnPurchaseToggle(target, rehabFinancingTouched),
      // A cash rental has NO loan: cash flow must be NOI, not NOI - debt service.
      // UNCHANGED: in rental the toggle IS the financing decision, so it still leads.
      rentalFinancingType: target === 'cash' ? 'cash' : 'dscr',
    };
    if (bucket.downPaymentMode) setDownPaymentMode(bucket.downPaymentMode);
    if (bucket.manualDownPayment !== undefined) setManualDownPayment(bucket.manualDownPayment);
    setAssumptions(newAssumptions);
    // The top-level toggle applies immediately, so any edits still staged in the rental
    // panel are now against the OTHER mode's terms — drop them rather than let a later
    // Apply replay them onto a mode the user did not stage them for.
    setRentalDraft(null);
    recalculateAnalysis(newAssumptions, propertyOverrides);
  }, [assumptions, financingByMode, downPaymentMode, manualDownPayment, propertyOverrides, recalculateAnalysis, analysis, originalAnalysis,
      // Missing here, the callback would close over a stale `false` and go on overwriting a
      // rehab choice the user had already made. It happens to be refreshed today only because
      // `assumptions` changes in the same click, which is luck rather than a guarantee.
      rehabFinancingTouched,
      liveRental.financingType]);

  // Load user's investment criteria
  useEffect(() => {
    const loadInvestmentCriteria = async () => {
      if (!user) {
        // Use defaults for non-logged-in users
        setInvestmentCriteria({
          flip: { min_net_profit: 30000, min_profit_margin: 15, min_roi: 20 },
          rental: { min_monthly_cash_flow: 250, min_cap_rate: 7, min_cash_on_cash: 10, min_dscr: 1.25 },
          brrrr: { min_cash_flow_after_refi: 200, max_cash_left_in_deal: 25000, target_refi_ltv: 75 }
        });
        setCriteriaLoaded(true);
        return;
      }
      
      try {
        const response = await investmentCriteriaAPI.getCriteria();
        const criteria = response.data.criteria;
        setInvestmentCriteria(criteria);
        setCriteriaLoaded(true);
        
        // P0-2 FIX: Sync classificationCriteria with user's actual investment criteria
        // This ensures explanation text uses user's min_profit, not hardcoded 25000
        if (criteria?.flip) {
          setClassificationCriteria(prev => ({
            ...prev,
            min_profit: criteria.flip.min_net_profit || prev.min_profit,
            min_margin: criteria.flip.min_profit_margin || prev.min_margin,
          }));
          console.log('[Criteria] Synced classificationCriteria with user investment criteria:', {
            min_profit: criteria.flip.min_net_profit,
            min_margin: criteria.flip.min_profit_margin
          });
        }
        
        // Show onboarding if user hasn't completed it
        if (!response.data.has_completed_onboarding) {
          setShowCriteriaOnboarding(true);
        }
      } catch (error) {
        console.error('Failed to load investment criteria:', error);
        // Use defaults on error
        setInvestmentCriteria({
          flip: { min_net_profit: 30000, min_profit_margin: 15, min_roi: 20 },
          rental: { min_monthly_cash_flow: 250, min_cap_rate: 7, min_cash_on_cash: 10, min_dscr: 1.25 },
          brrrr: { min_cash_flow_after_refi: 200, max_cash_left_in_deal: 25000, target_refi_ltv: 75 }
        });
        setCriteriaLoaded(true);
      }
    };
    
    loadInvestmentCriteria();
  }, [user]);

  // Track if user explicitly changed assumptions (not automatic updates)
  const [userChangedAssumptions, setUserChangedAssumptions] = useState(false);
  
  // Handle assumption changes with debounce - ONLY when user explicitly changes
  useEffect(() => {
    // Skip if not triggered by user action
    if (!userChangedAssumptions) return;

    // Recalculate on ANY assumption change — do NOT gate on a panel being open. The old
    // `if (showAssumptions || showPropertyDetails)` gate left the numbers stale until the
    // user toggled something (e.g. Cash<->Financed) to shake it loose. Every change to
    // rehab / price / financing / condition must trigger a full immediate recalculation.
    const timer = setTimeout(() => {
      recalculateAnalysis(assumptions);
      setUserChangedAssumptions(false); // Reset flag after recalculation
    }, 500);
    return () => clearTimeout(timer);
  }, [userChangedAssumptions, assumptions]); // re-run when the change flag flips

  // Returning from "Edit My Criteria": the working deal was restored (CASE 0) and the new
  // criteria were re-read from localStorage on mount. Re-score the preserved deal against
  // those criteria ONCE — keeps the user's rehab/price/financing edits, just re-evaluates
  // PASS/FAIL. Fires a single recalc, then clears the flag so it can't loop.
  const criteriaReturnHandledRef = useRef(false);

  useEffect(() => {
    if (analysis?._criteriaReturn && !criteriaReturnHandledRef.current) {
      criteriaReturnHandledRef.current = true;
      setAnalysis(prev => (prev ? { ...prev, _criteriaReturn: false } : prev));
      recalculateAnalysis(assumptions);
    }
  }, [analysis, assumptions, recalculateAnalysis]);

  // Resume pending action after user authentication
  useEffect(() => {
    if (user && pendingAction) {
      // User just authenticated, resume the pending action
      console.log('[Auth] Resuming pending action:', pendingAction);
      console.log('[Auth] Current analysis state:', analysis?.address, analysis?.strategy);
      
      // Use a small delay to ensure React state is fully settled after auth
      const timer = setTimeout(() => {
        switch (pendingAction) {
          case 'download':
            // Call handleDownloadPDF - it will use the current analysis state
            handleDownloadPDF();
            break;
          case 'preview':
            setShowPDFPreview(true);
            break;
          case 'save':
            handleSave();
            break;
          case 'email':
            setShowEmailModal(true);
            break;
          case 'share':
            // P0-1 FIX: Set flag to trigger handleShareWithAuth after it's defined
            // We can't call handleShareWithAuth directly here due to initialization order
            setPendingShareAfterAuth(true);
            break;
          default:
            console.log('[Auth] Unknown pending action:', pendingAction);
        }
        
        // Clear the pending action
        setPendingAction(null);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [user, pendingAction, analysis]);

  // P0-1 FIX: Handle deferred share snapshot creation after auth
  // Uses ref to call handleShareWithAuth which is defined later
  useEffect(() => {
    if (pendingShareAfterAuth && user && handleShareWithAuthRef.current) {
      setPendingShareAfterAuth(false);
      console.log('[Share] Calling deferred handleShareWithAuth after auth');
      handleShareWithAuthRef.current();
    }
  }, [pendingShareAfterAuth, user]);

  // Update recent analysis state when ARV mode or analysis changes significantly
  useEffect(() => {
    if (!analysis || !hasUnsavedChanges) return;
    if (analysis.is_sample) return;   // an example is nobody's history
    
    const timer = setTimeout(async () => {
      try {
        const updates = {
          arv_mode: arvModeLabel,
          analysis_data: analysis,
          deal_score: analysis.deal_score,
          deal_rating: analysis.deal_rating,
          net_profit: analysis.flip_analysis?.net_profit,
          arv: analysis.arv,
          purchase_price: analysis.purchase_price
        };
        
        if (user?.user_id) {
          await recentAnalysesAPI.updateState({
            user_id: user.user_id,
            address: analysis.address,
            city: analysis.city,
            state: analysis.state,
            updates
          });
        } else {
          updateGuestRecentState(analysis.address, analysis.city, analysis.state, updates);
        }
      } catch (err) {
        console.warn('Failed to update recent state:', err);
      }
    }, 2000); // Debounce 2 seconds to avoid excessive updates
    
    return () => clearTimeout(timer);
  }, [analysis, arvModeLabel, hasUnsavedChanges, user]);

  // Persist analysis to sessionStorage for navigation state preservation
  useEffect(() => {
    if (analysis) {
      try {
        sessionStorage.setItem('chekdeal_last_analysis', JSON.stringify(analysis));
      } catch (e) {
        console.warn('Failed to persist analysis to sessionStorage:', e);
      }
    }
  }, [analysis]);


  // Check if feedback prompt should be shown (after 3+ analyses)
  useEffect(() => {
    const checkFeedbackPrompt = async () => {
      if (!user?.user_id) return;
      
      // Check localStorage for feedback dismissal
      const feedbackDismissed = localStorage.getItem('chekdeal_feedback_dismissed');
      const feedbackSubmitted = localStorage.getItem('chekdeal_feedback_submitted');
      
      if (feedbackDismissed || feedbackSubmitted) return;
      
      // Get analyses count from usage stats.
      // THROUGH THE AXIOS CLIENT, NOT fetch(): the usage endpoint takes its identity from
      // the session header the client's interceptor adds. A raw fetch here carried no
      // header and got 401 on every call since the endpoint stopped trusting the id in
      // the URL — silently, so this prompt never showed.
      try {
        const response = await subscriptionAPI.getUsageInfo(user.user_id);
        if (response?.data) {
          const data = response.data;
          const analysesCount = data.analyses_used_total || 0;
          
          if (analysesCount >= 3) {
            // Small delay to show after results have loaded
            setTimeout(() => setShowFeedbackPrompt(true), 3000);
          }
        }
      } catch (err) {
        console.warn('Failed to check feedback prompt:', err);
      }
    };
    
    checkFeedbackPrompt();
  }, [user]);
  
  // CONVERSION: Check if upgrade prompt should be shown based on deal result and usage
  useEffect(() => {
    const checkUpgradePrompt = async () => {
      if (!user?.user_id) return;
      
      // Check localStorage for upgrade prompt dismissal (within 24 hours)
      const dismissedAt = localStorage.getItem('chekdeal_upgrade_prompt_dismissed_at');
      if (dismissedAt) {
        const dismissedTime = parseInt(dismissedAt, 10);
        const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
        if (hoursSinceDismissed < 24) return; // Don't show for 24 hours after dismissal
      }
      
      // Check if user is on a FREE plan.
      // Same as above: axios client, session header. This is the upgrade prompt — the
      // raw fetch's silent 401 hid it from every FREE/TRIAL user since the identity
      // migration.
      try {
        const usageResponse = await subscriptionAPI.getUsageInfo(user.user_id);
        if (!usageResponse?.data) return;

        const usageData = usageResponse.data;
        
        // Only show prompts for FREE or TRIAL users (not for paid or founder)
        if (!['FREE', 'TRIAL'].includes(usageData.plan_type)) return;
        
        // Fetch deal breakdown for value messaging
        try {
          const breakdownResponse = await analyticsAPI.getDealBreakdown(user.user_id);
          if (breakdownResponse?.data) {
            setDealStats(breakdownResponse.data);
          }
        } catch (err) {
          console.warn('Failed to fetch deal breakdown:', err);
        }
        
        // Determine prompt variant based on deal result
        const dealDecision = analysis?.deal_classification?.decision;
        const analysesRemaining = usageData.analyses_remaining || 0;
        
        // Priority: limit approaching > deal result
        if (analysesRemaining <= 1 && analysesRemaining >= 0) {
          setUpgradePromptVariant('limitApproaching');
          setTimeout(() => setShowUpgradePrompt(true), 3000);
        } else if (dealDecision === 'NO_PASS') {
          setUpgradePromptVariant('afterPass');
          setTimeout(() => setShowUpgradePrompt(true), 2500);
        } else if (dealDecision === 'MARGINAL') {
          setUpgradePromptVariant('afterNegotiate');
          setTimeout(() => setShowUpgradePrompt(true), 3000);
        } else if (dealDecision === 'GOOD_DEAL' && usageData.analyses_used >= 2) {
          // Show after BUY only if they've analyzed 2+ deals (engaged user)
          setUpgradePromptVariant('afterBuy');
          setTimeout(() => setShowUpgradePrompt(true), 4000);
        }
      } catch (err) {
        console.warn('Failed to check upgrade prompt:', err);
      }
    };
    
    checkUpgradePrompt();
  }, [user, analysis]);

  // Handle upgrade prompt dismissal
  const handleDismissUpgradePrompt = () => {
    setShowUpgradePrompt(false);
    localStorage.setItem('chekdeal_upgrade_prompt_dismissed_at', Date.now().toString());
  };

  // V5.0 STRICT: If no valid analysis source, REDIRECT to Analyzer immediately
  // NO auto-loading from sessionStorage, NO showing stale data
  useEffect(() => {
    if (!analysis && !fetchingFromBackend) {
      // Check if we have a URL analysis_id that needs fetching
      if (urlAnalysisId && urlAnalysisId !== 'new') {
        // Don't redirect - we're waiting for backend fetch
        return;
      }
      
      // No valid source - redirect to Analyzer
      console.log('[Results] V5.0: No valid analysis source, redirecting to /analyze');
      navigate('/analyze', { replace: true });
    }
  }, [analysis, fetchingFromBackend, urlAnalysisId, navigate]);

  if (!analysis) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center px-4 max-w-md">
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-3">
                {lang === 'en' ? 'Analysis Data Not Found' : 'Datos del Análisis No Encontrados'}
              </h2>
              <p className="text-muted-foreground mb-3">
                {lang === 'en' 
                  ? 'Your previous analysis is no longer available. This can happen if:' 
                  : 'Tu análisis anterior ya no está disponible. Esto puede ocurrir si:'}
              </p>
              <ul className="text-sm text-slate-500 text-left mb-4 space-y-1 bg-slate-50 p-3 rounded-lg">
                <li>• {lang === 'en' ? 'Your browser session was cleared' : 'Tu sesión del navegador fue borrada'}</li>
                <li>• {lang === 'en' ? 'You opened the app in a new tab' : 'Abriste la aplicación en una nueva pestaña'}</li>
                <li>• {lang === 'en' ? 'The page was bookmarked without saving' : 'La página fue guardada sin salvar el análisis'}</li>
              </ul>
              <p className="text-sm text-slate-400 mb-4">
                {lang === 'en' 
                  ? 'Tip: Sign in and use "Save Deal" to preserve your analyses.' 
                  : 'Consejo: Inicia sesión y usa "Guardar Deal" para preservar tus análisis.'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/analyze')}
                className="px-6 py-3 bg-secondary text-white rounded-lg font-semibold hover:bg-secondary/90 transition-colors"
              >
                {lang === 'en' ? 'Start New Analysis' : 'Iniciar Nuevo Análisis'}
              </button>
              {!user && (
                <button
                  onClick={() => setShowAuthPrompt(true)}
                  className="px-6 py-3 bg-white text-secondary border-2 border-secondary rounded-lg font-semibold hover:bg-secondary/5 transition-colors"
                >
                  {lang === 'en' ? 'Sign In' : 'Iniciar Sesión'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is a valuation-only analysis (no investment metrics)
  const isValuationMode = analysis?.strategy === 'valuation';

  // Helper function to check authentication before executing action
  const requireAuth = (action, actionName) => {
    if (!user) {
      setPendingAction(actionName);
      setShowAuthPrompt(true);
      return false;
    }
    return true;
  };

  // Wrapper functions for gated actions
  const handleSaveWithAuth = () => {
    if (sampleBlocked()) return;
    if (requireAuth(handleSave, 'save')) {
      handleSave();
    }
  };

  const handlePreviewPDFWithAuth = () => {
    // Warn user if comps were modified and ARV recalculation is in progress
    if (compsModifiedSinceRecalc) {
      toast.warning(
        lang === 'en'
          ? 'Comparables changed - ARV is being recalculated. Please wait.'
          : 'Comparables cambiaron - ARV se está recalculando. Por favor espere.'
      );
    }
    if (requireAuth(() => setShowPDFPreview(true), 'preview')) {
      setShowPDFPreview(true);
    }
  };

  const handleDownloadPDFWithAuth = () => {
    if (requireAuth(handleDownloadPDF, 'download')) {
      handleDownloadPDF();
    }
  };

  const handleSendEmailWithAuth = () => {
    if (sampleBlocked()) return;
    if (requireAuth(() => setShowEmailModal(true), 'email')) {
      setShowEmailModal(true);
    }
  };

  const handleShareWithAuth = async () => {
    if (sampleBlocked()) return;
    // INVARIANT: Share MUST use the canonical serializer - no manual construction
    if (!requireAuth(() => {}, 'share')) {
      return; // Auth prompt will be shown
    }
    
    setCreatingShareSnapshot(true);
    
    try {
      // CANONICAL STATE ENFORCEMENT: Use serializer, not manual construction
      if (!canonicalState) {
        console.error('[Share] Canonical state is null - cannot create snapshot');
        toast.error('Unable to create share link. Please try again.');
        return;
      }
      
      // Use the canonical serializer - this is the ONLY path to share
      const sharePayload = serializeForShare(canonicalState);
      
      // Create snapshot in backend using serialized payload
      const response = await shareAPI.createShareSnapshot(sharePayload.analysis);
      
      if (response.data?.share_id) {
        const shareId = response.data.share_id;
        setCurrentShareId(shareId); // Store for email use
        setCurrentShareUrl(`${window.location.origin}/shared/${shareId}`);
        setShowShareModal(true);
        console.log(`[Share] Created snapshot ${shareId}`);
      } else {
        throw new Error('Failed to create share snapshot');
      }
    } catch (error) {
      console.error('[Share] Error creating snapshot:', error);
      toast.error(lang === 'en' ? 'Failed to create share link. Please try again.' : 'Error al crear enlace. Inténtalo de nuevo.');
      // HARDENED: Do NOT fallback to stale analysis_id - fail explicitly
      // A share link must only render from an exact persisted snapshot
      setCurrentShareId(null); // Clear any stale share_id
    } finally {
      setCreatingShareSnapshot(false);
    }
  };

  // P0-1 FIX: Store handleShareWithAuth in ref for deferred calls from auth resume
  handleShareWithAuthRef.current = handleShareWithAuth;

  // V4.5: Re-run analysis with latest engine (for stale sessionStorage data)
  const handleRerunAnalysis = async () => {
    if (sampleBlocked()) return;
    if (!analysis?.address) {
      toast.error(lang === 'en' ? 'Cannot re-run: missing address' : 'No se puede volver a ejecutar: falta la dirección');
      return;
    }
    
    setRerunningAnalysis(true);
    try {
      console.log('[Results] Re-running analysis with latest engine for:', analysis.address);
      
      // API_BASE is not defined anywhere in this file — this line threw
      // "ReferenceError: API_BASE is not defined" the instant the handler ran, caught
      // by the catch below and shown as a generic "Failed to re-run" toast. Nobody
      // noticed because the button was unreachable at the same time. Use the shared
      // axios client every other call here uses, so auth and base URL come from one
      // place. (The same undefined identifier still sits at line ~943 on the
      // deals/explanation call — separate bug, recorded in BACKLOG.md.)
      const response = await analysisAPI.analyze(
        buildRerunRequest({ analysis, assumptions, user, lang })
      );

      const newAnalysis = response.data;
      if (!newAnalysis) {
        throw new Error('Analysis returned no data');
      }
      console.log('[Results] Re-run complete. New ARV:', newAnalysis.arv);
      
      // Update state with fresh analysis
      setAnalysis(newAnalysis);
      setCurrentARV(newAnalysis.arv);
      setCurrentARVConfidence(newAnalysis.arv_confidence);
      setCurrentARVConfidenceScore(newAnalysis.arv_confidence_score);
      setIsFromSessionStorage(false);
      
      // Save to sessionStorage
      sessionStorage.setItem('chekdeal_last_analysis', JSON.stringify(newAnalysis));
      
      toast.success(lang === 'en' ? 'Analysis updated with latest engine!' : '¡Análisis actualizado con el motor más reciente!');
    } catch (error) {
      console.error('[Results] Re-run failed:', error);
      toast.error(lang === 'en' ? 'Failed to re-run analysis' : 'Error al volver a ejecutar el análisis');
    } finally {
      setRerunningAnalysis(false);
    }
  };

  const handleSave = async () => {
    if (sampleBlocked()) return;
    setSaving(true);
    try {
      // No fabricated identity. This used to fall back to 'demo_user', which is how a
      // logged-out visitor's "save" returned 200 while writing into a shared bucket that
      // /deals/saved -- already session-scoped -- would never read back to them. It
      // looked like saving and saved nothing, and it walked past the plan gate on the way.
      // handleSaveWithAuth already calls requireAuth('save'); this stops undercutting it.
      const userId = user?.user_id;
      // CAPTURE THE COMPLETE ON-SCREEN STATE at click time — mode + every edit — so the
      // saved deal reopens EXACTLY as left. financing mode lives in assumptions
      // (purchaseFinancing/rehabFinancing) along with all financing terms, rehab, taxes,
      // insurance, closing, holding. Embedded in analysis_data so the backend (which stores
      // analysis_data as-is and upserts by address) persists it permanently.
      // Snapshot the ACTIVE mode's live financing into its bucket, so both mode buckets
      // (Cash + Financed) are captured with the latest terms.
      const _activeMode = assumptions?.purchaseFinancing === 'financed' ? 'financed' : 'cash';
      const financingByModeToSave = {
        ...financingByMode,
        [_activeMode]: { ...pickFinancing(assumptions), downPaymentMode, manualDownPayment },
      };
      const savedWorkingState = {
        assumptions,
        financingByMode: financingByModeToSave,  // per-mode Cash/Financed financing buckets
        propertyOverrides,
        hasPropertyOverrides,
        editedPurchasePrice,
        manualDownPayment,
        downPaymentMode,
        _savedAt: Date.now(),
      };
      const analysisToSave = { ...analysis, _saved_working_state: savedWorkingState };
      const saveResponse = await dealsAPI.save({
        user_id: userId,
        analysis_id: analysis.analysis_id,
        analysis_data: analysisToSave,
        notes: '',
        tags: []
      });
      
      // Mark recent analysis as saved (non-blocking)
      const savedDealId = saveResponse.data?.saved_deal_id;
      if (savedDealId) {
        try {
          if (user?.user_id) {
            await recentAnalysesAPI.markAsSaved({
              user_id: user.user_id,
              address: analysis.address,
              city: analysis.city,
              state: analysis.state,
              saved_deal_id: savedDealId
            });
          } else {
            markGuestRecentAsSaved(analysis.address, analysis.city, analysis.state, savedDealId);
          }
        } catch (recentErr) {
          console.warn('Failed to update recent history:', recentErr);
        }
      }
      
      toast.success(t('msg_saved'));
      setHasUnsavedChanges(false);
      setIsSaved(true);
    } catch (error) {
      toast.error(lang === 'en' ? 'Failed to save deal' : 'Error al guardar trato');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    setGeneratingPDF(true);
    
    // VERSION INDICATOR - helps diagnose cache issues
    const PDF_VERSION = 'v13-robust-fallback';
    console.log(`[PDF-${PDF_VERSION}] handleDownloadPDF called!`);
    console.log(`[PDF-${PDF_VERSION}] Analysis address:`, analysis?.address);
    console.log(`[PDF-${PDF_VERSION}] Analysis strategy:`, analysis?.strategy);
    console.log('[PDF-v10] Current analysis rental_analysis:', {
      monthly_cashflow: analysis?.rental_analysis?.monthly_cashflow,
      dscr: analysis?.rental_analysis?.dscr,
      cap_rate: analysis?.rental_analysis?.cap_rate,
      financing_type: analysis?.rental_analysis?.financing_type,
    });
    
    // SAFEGUARD: Verify we have a valid analysis
    if (!analysis || !analysis.address) {
      console.error('[PDF-v10] No valid analysis state!');
      toast.error(lang === 'en' 
        ? 'No analysis data available. Please run an analysis first.' 
        : 'No hay datos de análisis disponibles. Ejecute un análisis primero.');
      setGeneratingPDF(false);
      return;
    }
    
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      console.log('[PDF-v10] API URL:', apiUrl);
      
      // Step 1: Prepare the PDF and get a download token
      // CRITICAL: Use the CURRENT analysis state (includes all user edits)
      // SINGLE SOURCE OF TRUTH: currentAnalysisState
      const analysisToUse = currentAnalysisState || analysis;
      console.log('[PDF-v10] Using currentAnalysisState:', {
        deal_rating: analysisToUse.deal_rating,
        property_type: analysisToUse.property_type,
        beds: analysisToUse.beds,
        financing_mode: analysisToUse.financing_mode,
      });
      
      console.log('[PDF-v10] Fetching download token...');
      const prepareResponse = await fetch(`${apiUrl}/api/report/pdf/prepare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('chekdeal_session_token') 
            ? { 'Authorization': `Bearer ${localStorage.getItem('chekdeal_session_token')}` }
            : {})
        },
        body: JSON.stringify({
          analysis: analysisToUse,  // SINGLE SOURCE OF TRUTH: currentAnalysisState
          language: lang
        })
      });
      
      console.log('[PDF-v12] Prepare response status:', prepareResponse.status);
      
      if (!prepareResponse.ok) {
        const errorText = await prepareResponse.text();
        console.error('[PDF-v10] Prepare failed:', errorText);
        throw new Error(`Failed to prepare: ${prepareResponse.status} - ${errorText}`);
      }
      
      const data = await prepareResponse.json();
      const download_token = data.download_token;
      console.log('[PDF-v10] Token received:', download_token ? download_token.substring(0, 8) + '...' : 'NONE');
      
      if (!download_token) {
        throw new Error('No download token received from server');
      }
      
      // Step 2: Download the PDF using fetch and create blob URL
      // This method works better in preview environments than window.location.assign
      const downloadUrl = `${apiUrl}/api/report/pdf/download/${download_token}`;
      console.log('[PDF-v10] Downloading from:', downloadUrl);
      
      const downloadResponse = await fetch(downloadUrl);
      
      if (!downloadResponse.ok) {
        const errorText = await downloadResponse.text();
        throw new Error(`Download failed: ${downloadResponse.status} - ${errorText}`);
      }
      
      // Check if we got a PDF
      const contentType = downloadResponse.headers.get('content-type');
      console.log('[PDF-v10] Response content-type:', contentType);
      
      if (!contentType || !contentType.includes('application/pdf')) {
        const text = await downloadResponse.text();
        console.error('[PDF-v10] Not a PDF response:', text.substring(0, 200));
        throw new Error('Server did not return a PDF file');
      }
      
      // Create blob and download
      const blob = await downloadResponse.blob();
      console.log('[PDF-v12] Blob size:', blob.size, 'bytes');
      
      if (blob.size < 100) {
        throw new Error('PDF file is too small - generation may have failed');
      }
      
      const filename = `CHEKSTE_Report_${analysis?.address?.replace(/[^a-z0-9]/gi, '_') || 'Property'}.pdf`;
      
      // DOWNLOAD STRATEGY: Multiple fallbacks for different environments
      // In preview sandbox environments, direct blob download often fails silently
      // We prioritize opening in new tab which is more reliable
      
      let downloadSucceeded = false;
      let blobUrl = null;
      
      try {
        blobUrl = window.URL.createObjectURL(blob);
        console.log('[PDF] Created blob URL:', blobUrl);
      } catch (e) {
        console.error('[PDF] Failed to create blob URL:', e);
        throw new Error('Failed to process PDF file');
      }
      
      // DOWNLOAD STRATEGY V14: Direct backend URL approach for sandbox environments
      // In preview/sandbox environments, blob downloads often fail silently.
      // The most reliable method is to navigate directly to the backend download URL.
      
      const PDF_VERSION = 'v14';
      console.log(`[PDF-${PDF_VERSION}] Starting download sequence...`);
      console.log(`[PDF-${PDF_VERSION}] Backend download URL:`, downloadUrl);
      console.log(`[PDF-${PDF_VERSION}] Blob size:`, blob.size);
      console.log(`[PDF-${PDF_VERSION}] Filename:`, filename);
      
      // Store URLs globally for debugging
      window.__pdfDownloadUrl = downloadUrl;
      window.__pdfBlobSize = blob.size;
      
      // PRIMARY STRATEGY: Use iframe to trigger download from backend URL
      // This works in most sandbox environments where blob downloads fail
      try {
        console.log(`[PDF-${PDF_VERSION}] Strategy 1: iframe download from backend URL`);
        
        // Create hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;visibility:hidden;';
        iframe.src = downloadUrl;
        document.body.appendChild(iframe);
        
        // Give it time to trigger the download
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Clean up
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        
        downloadSucceeded = true;
        toast.success(lang === 'en' ? 'PDF download started!' : '¡Descarga de PDF iniciada!');
        console.log(`[PDF-${PDF_VERSION}] iframe download attempted`);
      } catch (e) {
        console.warn(`[PDF-${PDF_VERSION}] iframe strategy failed:`, e);
      }
      
      // FALLBACK: Direct window.location navigation
      if (!downloadSucceeded) {
        try {
          console.log(`[PDF-${PDF_VERSION}] Strategy 2: Direct navigation to backend URL`);
          window.location.href = downloadUrl;
          downloadSucceeded = true;
          // Don't show toast here as page will navigate
        } catch (e) {
          console.warn(`[PDF-${PDF_VERSION}] Direct navigation failed:`, e);
        }
      }
      
      // LAST RESORT: Show manual download link
      if (!downloadSucceeded) {
        console.log(`[PDF-${PDF_VERSION}] Strategy 3: Showing manual download link`);
        toast.info(
          lang === 'en' 
            ? 'Click here to download your PDF:'
            : 'Haz clic aquí para descargar tu PDF:',
          {
            duration: 30000,
            action: {
              label: lang === 'en' ? 'Download PDF' : 'Descargar PDF',
              onClick: () => {
                window.open(downloadUrl, '_blank');
              }
            }
          }
        );
        downloadSucceeded = true;
      }
      
      // Cleanup blob URL after delay (not needed for backend URL approach, but safe)
      if (blobUrl) {
        setTimeout(() => {
          try {
            window.URL.revokeObjectURL(blobUrl);
          } catch (e) { /* ignore */ }
        }, 60000);
      }
      
      setGeneratingPDF(false);
        
    } catch (error) {
      console.error('[PDF-v10] Error:', error);
      // Show detailed error message
      toast.error(lang === 'en' 
        ? `PDF download failed: ${error.message}` 
        : `Error al descargar PDF: ${error.message}`);
      setGeneratingPDF(false);
    }
  };

  const handleSendEmail = async () => {
    if (sampleBlocked()) return;
    if (!emailAddresses.trim()) {
      toast.error(lang === 'en' ? 'Please enter at least one email address' : 'Ingresa al menos una dirección de correo');
      return;
    }
    
    // Parse and validate email addresses
    const emails = emailAddresses
      .split(/[,;\s]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    
    if (invalidEmails.length > 0) {
      toast.error(lang === 'en' 
        ? `Invalid email(s): ${invalidEmails.join(', ')}`
        : `Correo(s) inválido(s): ${invalidEmails.join(', ')}`);
      return;
    }
    
    setSendingEmail(true);
    try {
      // CRITICAL: Use currentAnalysisState for email report (SINGLE SOURCE OF TRUTH)
      const analysisToSend = currentAnalysisState || analysis;
      await analysisAPI.sendReportEmail(analysisToSend, emails, lang);
      
      toast.success(lang === 'en' 
        ? `Report sent to ${emails.length} recipient(s)!`
        : `Reporte enviado a ${emails.length} destinatario(s)!`);
      
      setShowEmailModal(false);
      setEmailAddresses('');
    } catch (error) {
      console.error('Email send error:', error);
      toast.error(lang === 'en' ? 'Failed to send email' : 'Error al enviar correo');
    } finally {
      setSendingEmail(false);
    }
  };

  // Why the alarm fires, decided ONCE for both places that warn about this value: this box
  // and the one-line mark beside the ARV figure ~260 lines down. Neither reimplements the
  // rule, so a mark can never appear pointing at a notice that is not there.
  // See utils/arvAlarm.js for what it will and will not say.
  const alarmReason = arvAlarmReason(analysis, currentARVConfidenceScore);

  const ARVConfidenceAlarm = () => {
    if (!alarmReason) return null;

    const arvDetails = analysis?.arv_details || {};
    const gate = arvDetails?.confidence_gate;
    const { title, body, asking, closed } = arvAlarmContent(alarmReason, gate, lang);

    // Kept for the consumers that already read it. The score is still what fires
    // 'weak_comps'; it just is not the only thing that fires the box any more.
    const score = Number(arvDetails?.confidence?.score
      ?? analysis?.arv_confidence_score
      ?? currentARVConfidenceScore);
    const scoreKnown = Number.isFinite(score);

    // TWO LEVELS, NOT ONE. A note fires on 15 of 22 corpus analyses (59% of them
    // 'weak_comps'); a contradiction between sources is rare. Dressing both in the same
    // amber alarm spends the alarm on the common case, and a warning seen two times in
    // three stops being read — including on the one that matters. So the frequent cases
    // get a quiet slate note with a lightbulb, and amber is reserved for the real
    // contradiction. Same box, same testid, same copy source; only the volume differs.
    const isWarning = arvAlarmSeverity(alarmReason) === 'warning';

    return (
      <div
        className={isWarning
          ? 'mb-6 p-4 sm:p-5 rounded-xl border-2 bg-amber-50 border-amber-300'
          : 'mb-6 p-3 sm:p-4 rounded-xl border bg-slate-50 border-slate-200'}
        data-testid="arv-confidence-alarm"
        data-alarm-reason={alarmReason}
        data-alarm-severity={isWarning ? 'warning' : 'note'}
        data-confidence-score={scoreKnown ? String(score) : 'unknown'}
      >
        <div className="flex items-start gap-3">
          {isWarning ? (
            <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
          ) : (
            <Lightbulb className="flex-shrink-0 w-5 h-5 mt-0.5 text-slate-400" />
          )}
          <div className="flex-1">
            <h3 className={isWarning
              ? 'font-semibold text-base sm:text-lg text-amber-800'
              : 'font-semibold text-sm sm:text-base text-slate-700'}>
              {title}
            </h3>
            {/* The two figures, with no hint as to which is right, because the measurement
                does not know which is right. They are here so "they differ" is something
                the reader can see rather than something they take on trust. */}
            {asking !== null && closed !== null && (
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="arv-gate-amounts">
                <div className="text-amber-800">
                  <span className="text-amber-700">
                    {lang === 'en' ? 'Asking prices:' : 'Precios pedidos:'}
                  </span>{' '}
                  <span className="font-semibold">${formatWholeDollars(asking)}</span>
                </div>
                <div className="text-amber-800">
                  <span className="text-amber-700">
                    {lang === 'en' ? 'Closed sales:' : 'Ventas cerradas:'}
                  </span>{' '}
                  <span className="font-semibold">${formatWholeDollars(closed)}</span>
                </div>
              </div>
            )}
            <p className={isWarning
              ? 'mt-1 text-sm text-amber-700'
              : 'mt-1 text-sm text-slate-600 leading-relaxed'}>
              {body}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Value Warning Banner component for LOW confidence or UNABLE_TO_COMPUTE
  const ARVWarningBanner = () => {
    const arvDetails = analysis.arv_details || {};
    const arvStatus = arvDetails.arv_status;
    const arvConfidence = arvDetails.arv_confidence || analysis.arv_confidence;
    
    // Only show for LOW confidence or UNABLE_TO_COMPUTE
    if (arvConfidence !== 'LOW' && arvStatus !== 'UNABLE_TO_COMPUTE' && arvStatus !== 'LOW_CONFIDENCE') {
      return null;
    }
    
    const isUnableToCompute = arvStatus === 'UNABLE_TO_COMPUTE';
    
    return (
      <div 
        className={`mb-6 p-4 sm:p-5 rounded-xl border-2 ${
          isUnableToCompute 
            ? 'bg-red-50 border-red-300' 
            : 'bg-amber-50 border-amber-300'
        }`}
        data-testid="arv-warning-banner"
      >
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            isUnableToCompute ? 'bg-red-100' : 'bg-amber-100'
          }`}>
            <AlertTriangle className={`w-5 h-5 ${
              isUnableToCompute ? 'text-red-600' : 'text-amber-600'
            }`} />
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold text-base sm:text-lg ${
              isUnableToCompute ? 'text-red-800' : 'text-amber-800'
            }`}>
              {isUnableToCompute 
                ? (lang === 'en' ? 'Value Cannot Be Computed' : 'No se puede calcular el valor')
                : (lang === 'en' ? 'Limited Comps Detected' : 'Comps Limitados Detectados')
              }
            </h3>
            <p className={`mt-1 text-sm ${
              isUnableToCompute ? 'text-red-700' : 'text-amber-700'
            }`}>
              {isUnableToCompute
                ? (lang === 'en' 
                    ? 'Unable to compute a reliable value automatically. Consider adding manual comparables or consulting a professional appraiser.'
                    : 'No se puede calcular un valor confiable automáticamente. Considera agregar comparables manuales o consultar a un tasador profesional.')
                : (lang === 'en'
                    ? 'The estimated value is based on the best available comparables and may improve if you add manual comparables.'
                    : 'Comps limitados detectados. El valor estimado se basa en los mejores comparables disponibles y puede mejorar si agregas comparables manuales.')
              }
            </p>
            {isUnableToCompute && (
              <div className="mt-3">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {lang === 'en' ? 'Professional Appraisal Recommended' : 'Se Recomienda Tasación Profesional'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Calculate Deal Score Confidence based on data quality
  const getDealScoreConfidence = () => {
    let confidenceScore = 0;
    let factors = [];
    
    // Factor 1: ARV confidence (0-30 points)
    const arvConfidencePoints = {
      high: 30,
      medium: 20,
      low: 10
    };
    confidenceScore += arvConfidencePoints[analysis.arv_confidence] || 15;
    
    // Factor 2: Number of comparables (0-25 points)
    const compCount = analysis.comparable_count || analysis.sales_comps?.length || 0;
    if (compCount >= 5) confidenceScore += 25;
    else if (compCount >= 3) confidenceScore += 20;
    else if (compCount >= 1) confidenceScore += 10;
    
    // Factor 3: Property data completeness (0-25 points)
    let dataPoints = 0;
    if (analysis.sqft && analysis.sqft > 0) dataPoints += 5;
    if (analysis.beds && analysis.beds > 0) dataPoints += 5;
    if (analysis.baths && analysis.baths > 0) dataPoints += 5;
    if (analysis.year_built && analysis.year_built > 0) dataPoints += 5;
    if (analysis.property_snapshot?.tax_assessed_value) dataPoints += 5;
    confidenceScore += dataPoints;
    
    // Factor 4: Consistency signals (0-20 points)
    // Check if ARV, comps, and rent signals are consistent
    if (analysis.rental_analysis?.monthly_rent > 0) confidenceScore += 10;
    if (analysis.arv_confidence_score >= 50) confidenceScore += 10;
    
    // Determine confidence level
    if (confidenceScore >= 75) return { level: 'high', score: confidenceScore };
    if (confidenceScore >= 50) return { level: 'medium', score: confidenceScore };
    return { level: 'low', score: confidenceScore };
  };

  // Generate dynamic deal explanation bullets
  // CRITICAL: Uses SSOT values for consistency
  const generateDealExplanation = () => {
    const bullets = [];
    // A blocked ARV makes every profit sentence below a statement about a value we do
    // not have. With ARV = 0 they read as confident losses ("-$178k"), which is worse
    // than saying nothing. Gate at the source rather than at each of the twelve strings.
    if (arvBlocked) {
      return [{
        type: 'warning',
        text: lang === 'en'
          ? 'Profit cannot be assessed while the value of this property is unavailable. Add comparables you know to compute one.'
          : 'No se puede evaluar la ganancia mientras no haya un valor para esta propiedad. Agregá comparables que conozcas para calcularlo.',
      }];
    }
    const profitMargin = ssotProfitMargin;
    const netProfit = ssotNetProfit;
    const compCount = SSOT?.comparable_count || analysis.comparable_count || analysis.sales_comps?.length || 0;
    const capRate = liveRental.capRate;
    const cashflow = liveRental.monthlyCashFlow;
    const rehabCondition = analysis.rehab_breakdown?.condition || 'medium';
    const rehabPerSqft = (SSOT?.sqft || analysis.sqft) > 0 ? Math.round((SSOT?.rehab_estimate || analysis.rehab_estimate) / (SSOT?.sqft || analysis.sqft)) : 0;
    const riskCount = analysis.risk_factors?.length || 0;
    const oppCount = analysis.opportunities?.length || 0;
    
    // Bullet 1: Profit/Score explanation
    if (profitMargin >= 15 && netProfit > 0) {
      bullets.push({
        type: 'positive',
        text: lang === 'en' 
          ? `Strong profit potential with ${profitMargin.toFixed(1)}% margin on a $${formatWholeDollars(netProfit)} net profit.`
          : `Fuerte potencial de ganancia con ${profitMargin.toFixed(1)}% de margen en una ganancia neta de $${formatWholeDollars(netProfit)}.`
      });
    } else if (profitMargin >= 5 && netProfit > 0) {
      bullets.push({
        type: 'neutral',
        text: lang === 'en'
          ? `Moderate profit potential with ${profitMargin.toFixed(1)}% margin.`
          : `Potencial de ganancia moderado con ${profitMargin.toFixed(1)}% de margen.`
      });
    } else {
      bullets.push({
        type: 'negative',
        text: lang === 'en'
          ? 'Limited profit potential. Consider negotiating a lower price.'
          : 'Potencial de ganancia limitado. Considera negociar un precio más bajo.'
      });
    }
    
    // Bullet 2: ARV confidence explanation
    if (analysis.arv_confidence === 'high') {
      bullets.push({
        type: 'positive',
        text: lang === 'en'
          ? `ARV confidence is high based on ${compCount} recent comparable sales nearby.`
          : `Confianza de ARV alta basada en ${compCount} ventas comparables recientes cercanas.`
      });
    } else if (analysis.arv_confidence === 'medium') {
      bullets.push({
        type: 'neutral',
        text: lang === 'en'
          ? 'ARV estimate has medium confidence. More comparables would improve accuracy.'
          : 'Estimación de ARV con confianza media. Más comparables mejorarían la precisión.'
      });
    } else {
      bullets.push({
        type: 'negative',
        text: lang === 'en'
          ? 'ARV confidence is low. Limited comparable data available.'
          : 'Confianza de ARV baja. Datos comparables limitados disponibles.'
      });
    }
    
    // Bullet 3: Rehab condition
    const rehabTexts = {
      light: {
        en: `Light rehab expected ($${rehabPerSqft}/sqft) - cosmetic updates only.`,
        es: `Rehabilitación leve esperada ($${rehabPerSqft}/pie²) - solo actualizaciones cosméticas.`
      },
      medium: {
        en: `Medium rehab required ($${rehabPerSqft}/sqft) - kitchen/bath updates needed.`,
        es: `Rehabilitación media requerida ($${rehabPerSqft}/pie²) - actualizaciones de cocina/baño necesarias.`
      },
      heavy: {
        en: `Heavy rehab needed ($${rehabPerSqft}/sqft) - significant renovation required.`,
        es: `Rehabilitación pesada necesaria ($${rehabPerSqft}/pie²) - renovación significativa requerida.`
      },
      custom: {
        en: `Custom rehab estimate: $${formatWholeDollars(analysis.rehab_estimate)} total.`,
        es: `Estimación de rehabilitación personalizada: $${formatWholeDollars(analysis.rehab_estimate)} total.`
      }
    };
    const rehabType = rehabCondition === 'heavy' ? 'negative' : (rehabCondition === 'light' ? 'positive' : 'neutral');
    bullets.push({
      type: rehabType,
      text: rehabTexts[rehabCondition]?.[lang] || rehabTexts.medium[lang]
    });
    
    // Bullet 4: Rental metrics OR risk/opportunities.
    // Cap rate / cashflow are RENTAL concepts — only surface them for income
    // strategies (rental/brrrr), never on a pure flip (defense-in-depth: the flip
    // response also nulls rental_analysis server-side).
    const isIncomeStrategy = analysis.strategy === 'rental' || analysis.strategy === 'brrrr';
    if (isIncomeStrategy && capRate >= 8 && cashflow > 200) {
      bullets.push({
        type: 'positive',
        text: lang === 'en'
          ? `Strong rental metrics: ${capRate.toFixed(1)}% cap rate and $${formatWholeDollars(cashflow)}/month cashflow.`
          : `Métricas de renta sólidas: ${capRate.toFixed(1)}% tasa de capitalización y $${formatWholeDollars(cashflow)}/mes flujo de efectivo.`
      });
    } else if (isIncomeStrategy && capRate >= 5) {
      bullets.push({
        type: 'neutral',
        text: lang === 'en'
          ? `Rental returns are moderate with ${capRate.toFixed(1)}% cap rate.`
          : `Rendimientos de renta moderados con ${capRate.toFixed(1)}% tasa de capitalización.`
      });
    } else if (riskCount > 0) {
      bullets.push({
        type: 'negative',
        text: lang === 'en'
          ? `${riskCount} risk factor${riskCount > 1 ? 's' : ''} identified. Review carefully.`
          : `${riskCount} factor${riskCount > 1 ? 'es' : ''} de riesgo identificado${riskCount > 1 ? 's' : ''}. Revisar cuidadosamente.`
      });
    } else if (oppCount > 0) {
      bullets.push({
        type: 'positive',
        text: lang === 'en'
          ? `${oppCount} opportunity factor${oppCount > 1 ? 's' : ''} identified.`
          : `${oppCount} factor${oppCount > 1 ? 'es' : ''} de oportunidad identificado${oppCount > 1 ? 's' : ''}.`
      });
    }
    
    return bullets;
  };

  const dealConfidence = getDealScoreConfidence();
  const explanationBullets = generateDealExplanation();

  // Calculate Equity Spread
  const equitySpread = (analysis.arv || 0) - (analysis.purchase_price || 0);
  const equitySpreadPercent = analysis.arv > 0 ? (equitySpread / analysis.arv) * 100 : 0;
  
  // Equity color via the shared helper: POSITIVE equity = green (you have equity —
  // good), NEGATIVE = red (overpaying), ~$0 = neutral. (Was a gradient that wrongly
  // showed positive-but-under-20% equity in RED.)
  const equityColors = financialCardColor(equitySpread, 1);

  // Calculate Investment Recommendation using user's investment criteria
  // CRITICAL: Uses SSOT values (ssotFlipAnalysis, etc.) for consistency
  const getInvestmentRecommendation = () => {
    // Same reason as generateDealExplanation: every criterion below is measured against
    // profit, and profit is measured against the ARV. With no ARV there is no verdict to
    // give, and "fails: net profit -$178,000" is a claim about a number that does not
    // exist. Returns the established shape so every consumer keeps working.
    if (arvBlocked) {
      return {
        action: 'unknown',
        label: 'NEEDS DATA',
        labelEs: 'FALTAN DATOS',
        sublabel: lang === 'en' ? 'Value Unavailable' : 'Valor No Disponible',
        message: lang === 'en'
          ? 'This property cannot be scored until it has a value. Add comparables you know, or enter the missing property details.'
          : 'No se puede puntuar esta propiedad hasta que tenga un valor. Agregá comparables que conozcas, o completá los datos faltantes de la propiedad.',
        colors: { bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-500', glow: 'shadow-amber-200' },
        icon: 'alert',
        failedCriteria: [],
      };
    }
    const strategy = analysis.strategy || 'flip';
    const mao = ssotMao;
    const purchasePrice = SSOT?.purchase_price || analysis.purchase_price || 0;
    const arvConfidence = SSOT?.arv_confidence || analysis.arv_confidence || 'low';
    
    // Default criteria (used if investmentCriteria not loaded)
    const defaultCriteria = {
      flip: { min_net_profit: 30000, min_profit_margin: 15, min_roi: 20 },
      rental: { min_monthly_cash_flow: 250, min_cap_rate: 7, min_cash_on_cash: 10, min_dscr: 1.25 }
    };
    
    const criteria = investmentCriteria || defaultCriteria;
    const failedCriteria = [];
    
    if (strategy === 'flip' || strategy === 'both') {
      // CRITICAL: Use SSOT values for consistency
      const netProfit = ssotNetProfit;
      const profitMargin = ssotProfitMargin;
      const roi = ssotRoi;
      
      const minProfit = criteria.flip?.min_net_profit || 30000;
      const minMargin = criteria.flip?.min_profit_margin || 15;
      const minROI = criteria.flip?.min_roi || 20;
      
      // Check each criterion
      if (netProfit < minProfit) {
        failedCriteria.push(lang === 'en' 
          ? `Net profit ($${formatWholeDollars(netProfit)}) is below your minimum ($${formatWholeDollars(minProfit)})`
          : `Ganancia neta ($${formatWholeDollars(netProfit)}) está debajo de tu mínimo ($${formatWholeDollars(minProfit)})`
        );
      }
      if (profitMargin < minMargin) {
        failedCriteria.push(lang === 'en'
          ? `Profit margin (${profitMargin.toFixed(1)}%) is below your minimum (${minMargin}%)`
          : `Margen de ganancia (${profitMargin.toFixed(1)}%) está debajo de tu mínimo (${minMargin}%)`
        );
      }
      if (roi < minROI && roi !== 0) {
        failedCriteria.push(lang === 'en'
          ? `ROI (${roi.toFixed(1)}%) is below your minimum (${minROI}%)`
          : `ROI (${roi.toFixed(1)}%) está debajo de tu mínimo (${minROI}%)`
        );
      }
      if (mao < purchasePrice) {
        failedCriteria.push(lang === 'en'
          ? `Purchase price exceeds your ${assumptions.maoRule}% target threshold by $${formatWholeDollars(purchasePrice - mao)}`
          : `El precio excede tu umbral objetivo del ${assumptions.maoRule}% por $${formatWholeDollars(purchasePrice - mao)}`
        );
      }
    }
    
    if (strategy === 'rental' || strategy === 'both') {
      // Read LIVE, like capRate/dscr already do. Mixing a stale cash flow with a live
      // cap rate made this text contradict the numbers on screen during an edit.
      const cashFlow = liveRental.monthlyCashFlow;
      const capRate = liveRental.capRate;
      const cashOnCash = liveRental.cashOnCash;
      const dscr = liveRental.dscr;

      const minCashFlow = criteria.rental?.min_monthly_cash_flow ?? 250;
      const minCapRate = criteria.rental?.min_cap_rate ?? 7;
      const minCashOnCash = criteria.rental?.min_cash_on_cash ?? 10;  // 0 is a real minimum
      const minDSCR = minDscrTarget;
      
      if (cashFlow < minCashFlow) {
        failedCriteria.push(lang === 'en'
          ? `Monthly cash flow ($${formatWholeDollars(cashFlow)}) is below your minimum ($${formatWholeDollars(minCashFlow)})`
          : `Flujo mensual ($${formatWholeDollars(cashFlow)}) está debajo de tu mínimo ($${formatWholeDollars(minCashFlow)})`
        );
      }
      if (capRate < minCapRate && capRate > 0) {
        failedCriteria.push(lang === 'en'
          ? `Cap rate (${capRate.toFixed(1)}%) is below your minimum (${minCapRate}%)`
          : `Cap rate (${capRate.toFixed(1)}%) está debajo de tu mínimo (${minCapRate}%)`
        );
      }
      if (cashOnCash < minCashOnCash && cashOnCash > 0) {
        failedCriteria.push(lang === 'en'
          ? `Cash-on-cash (${cashOnCash.toFixed(1)}%) is below your minimum (${minCashOnCash}%)`
          : `Cash-on-cash (${cashOnCash.toFixed(1)}%) está debajo de tu mínimo (${minCashOnCash}%)`
        );
      }
      if (dscr < minDSCR && dscr > 0) {
        failedCriteria.push(lang === 'en'
          ? `DSCR (${dscr.toFixed(2)}) is below your minimum (${minDSCR})`
          : `DSCR (${dscr.toFixed(2)}) está debajo de tu mínimo (${minDSCR})`
        );
      }
    }
    
    // Low confidence warning
    if (arvConfidence === 'low') {
      failedCriteria.push(lang === 'en'
        ? 'ARV confidence is low - proceed with caution'
        : 'Confianza del valor es baja - procede con precaución'
      );
    }
    
    // Determine recommendation based on failed criteria
    // CRITICAL: Use SSOT values for consistency
    const netProfit = ssotNetProfit;
    const profitMargin = ssotProfitMargin;
    const priceAboveMao = mao < purchasePrice;
    const isProfitable = netProfit > 0 && profitMargin > 5;
    
    if (failedCriteria.length === 0) {
      return {
        action: 'pass',
        label: 'PASS',
        labelEs: 'APROBADO',
        sublabel: lang === 'en' ? 'Meets Your Investment Criteria' : 'Cumple Tus Criterios de Inversión',
        explanation: lang === 'en' 
          ? 'This deal meets all your minimum requirements. Review the details and proceed with due diligence.'
          : 'Este trato cumple todos tus requisitos mínimos. Revisa los detalles y procede con diligencia debida.',
        colors: { bg: 'bg-green-500', text: 'text-white', border: 'border-green-500', glow: 'shadow-green-200' },
        icon: 'check',
        failedCriteria: []
      };
    } else if (isProfitable && priceAboveMao && failedCriteria.length === 1) {
      // Special case: Profitable but ONLY exceeds target threshold
      return {
        action: 'negotiate',
        label: 'NEGOTIATE',
        labelEs: 'NEGOCIAR',
        sublabel: lang === 'en' ? 'Profitable But Above Your Target' : 'Rentable Pero Sobre Tu Objetivo',
        explanation: lang === 'en'
          ? `This deal is profitable ($${formatWholeDollars(netProfit)} net profit) but exceeds your ${assumptions.maoRule}% target. Consider negotiating the price down by $${formatWholeDollars(purchasePrice - mao)}.`
          : `Este trato es rentable ($${formatWholeDollars(netProfit)} ganancia neta) pero excede tu objetivo del ${assumptions.maoRule}%. Considera negociar el precio $${formatWholeDollars(purchasePrice - mao)} menos.`,
        colors: { bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-500', glow: 'shadow-amber-200' },
        icon: 'warning',
        failedCriteria
      };
    } else if (failedCriteria.length <= 2 && mao >= purchasePrice * 0.85) {
      // NEGOTIATE: Close but not quite there
      return {
        action: 'negotiate',
        label: 'NEGOTIATE',
        labelEs: 'NEGOCIAR',
        sublabel: lang === 'en' ? 'May Work With Better Terms' : 'Puede Funcionar Con Mejores Términos',
        explanation: failedCriteria[0],
        colors: { bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-500', glow: 'shadow-amber-200' },
        icon: 'warning',
        failedCriteria
      };
    }
    
    // NO PASS: Does not meet criteria
    return {
      action: 'no_pass',
      label: 'NO PASS',
      labelEs: 'NO APROBADO',
      sublabel: lang === 'en' ? 'Does Not Meet Your Investment Criteria' : 'No Cumple Tus Criterios de Inversión',
      explanation: failedCriteria[0],
      colors: { bg: 'bg-red-500', text: 'text-white', border: 'border-red-500', glow: 'shadow-red-200' },
      icon: 'x',
      failedCriteria
    };
  };
  
  const recommendation = getInvestmentRecommendation();

  // Handle property override changes
  const handlePropertyOverride = (field, value) => {
    // Handle string fields (property_type) separately from numeric fields
    let processedValue;
    if (field === 'property_type') {
      processedValue = value === '' ? null : value;  // Keep as string
    } else {
      processedValue = value === '' ? null : parseFloat(value);  // Convert to number
    }
    
    const newOverrides = { ...propertyOverrides, [field]: processedValue };
    setPropertyOverrides(newOverrides);
    
    // Check if any override differs from original
    const hasAnyOverride = Object.keys(newOverrides).some(key => {
      if (key === 'property_type') {
        // For property_type, compare with property_snapshot
        return newOverrides[key] !== null && 
               newOverrides[key] !== originalAnalysis?.property_snapshot?.property_type;
      }
      return newOverrides[key] !== null && newOverrides[key] !== originalAnalysis[key];
    });
    setHasPropertyOverrides(hasAnyOverride);
    // Typing invalidates the last run: what is on screen no longer reflects these values.
    setOverridesApplied(false);
  };

  // Unified ARV recompute — the ONE brain (backend compute_arv_unified) over
  // system + user comps, with an optional edited subject. Sets top card AND ARV
  // section from the SAME result so they can never diverge, then (flip/rental)
  // recomputes financials off that ARV. Returns the new ARV.
  // G — ONE VOICE. Every path that receives an ARV result from the server (choosing a
  // mode, recalculating after a comp, editing the subject, declaring a feature gap)
  // adopts the SAME set of fields. The old handlers each copied a different subset —
  // arv + arv_details here, arv + comps_used there — which is how the page ended up
  // valuing from the user's comps while "N comparable sales used", the ARV table, the
  // excluded list and the PDF still described the system pool.
  const adoptArvResult = (prev, d, extra = {}) => ({
    ...prev,
    arv: d.arv,
    arv_confidence: d.arv_confidence,
    arv_confidence_score: d.arv_confidence_score,
    arv_details: d.arv_details || d.arv_by_mode?.[d.active_arv_mode]?.arv_details || prev.arv_details,
    comps_used: d.comps_used || [],
    comps_excluded: d.comps_excluded || [],
    comparable_count: (d.comps_used || []).length,
    system_comps_used: d.system_comps_used ?? prev.system_comps_used,
    user_comps_used: d.user_comps_used ?? prev.user_comps_used,
    verdict_reliable: d.verdict_reliable ?? prev.verdict_reliable,
    active_arv_mode: d.active_arv_mode || prev.active_arv_mode || 'both',
    arv_by_mode: d.arv_by_mode || prev.arv_by_mode,
    // G2: the financials last computed FOR this mode (the server hands them back with
    // the mode), so profit, verdict and target offer are this mode's at once; the
    // recalculation that follows refreshes them against the inputs of the moment.
    ...(Object.fromEntries(['flip_analysis', 'rental_analysis', 'rental_classification', 'deal_decision',
      'deal_classification', 'deal_score', 'deal_rating', 'deal_explanation', 'risk_factors', 'opportunities',
      'rehab_estimate', 'rehab_breakdown'].filter(k => d[k] !== undefined && d[k] !== null).map(k => [k, d[k]]))),
    ...extra,
  });

  const recomputeUnifiedARV = async (editedSubject, overridesForFinancials) => {
    if (sampleBlocked()) return;
    if (!analysis?.analysis_id) {
      // No persisted analysis to recompute against — fall back to a financials recalc.
      recalculateAnalysis(assumptions, overridesForFinancials || {});
      return null;
    }
    const res = await compsAPI.recalculateARV({
      analysis_id: analysis.analysis_id,
      // G: the mode the user is on stays on. This used to force system+user (Blended)
      // whenever the subject was edited, silently leaving "User Only".
      active_arv_mode: analysis?.active_arv_mode || 'both',
      edited_subject: editedSubject || null,
    });
    const d = res.data || {};
    const newARV = d.arv;
    setAnalysis(prev => adoptArvResult(prev, d));
    setCurrentARV(newARV);
    setCurrentARVConfidence(d.arv_confidence);
    setCurrentARVConfidenceScore(d.arv_confidence_score);
    // Financials off the new ARV — flip/rental only (valuation has none).
    if (!isValuationMode) {
      recalculateAnalysis(assumptions, overridesForFinancials || {}, { arvOverride: newARV });
    }
    return newARV;
  };

  // Declaring a feature gap recomputes immediately — there is no Apply button here,
  // because a checkbox that needs a second click to mean anything is the confusion this
  // screen just removed from Property Details.
  //
  // BOTH fields go on every call, always, even when null. That is deliberate: the server
  // treats a field PRESENT in the body as authoritative and a field absent as "inherit
  // what was declared before". Sending only the one that changed would make unticking a
  // box impossible — the other would silently come back from storage.
  const applyFeatureGap = async (nextBasement, nextGarage) => {
    if (sampleBlocked()) return;
    setMissingBasement(nextBasement);
    setMissingGarage(nextGarage);
    if (!analysis?.analysis_id) return;
    setGapRecalculating(true);
    try {
      const res = await compsAPI.recalculateARV({
        analysis_id: analysis.analysis_id,
        active_arv_mode: analysis?.active_arv_mode || 'both',   // G: keep the user's mode
        missing_basement: nextBasement,
        missing_garage: nextGarage,
      });
      const d = res.data || {};
      setAnalysis(prev => adoptArvResult(prev, d));
      setCurrentARV(d.arv);
      setCurrentARVConfidence(d.arv_confidence);
      setCurrentARVConfidenceScore(d.arv_confidence_score);
      toast.success(lang === 'en' ? 'Value updated' : 'Valor actualizado');
    } catch (e) {
      toast.error(lang === 'en' ? 'Could not update the value' : 'No se pudo actualizar el valor');
    } finally {
      setGapRecalculating(false);
    }
  };

  // Apply property overrides — recompute ARV on the EDITED subject through the
  // unified path (honors user comps), then financials.
  const applyPropertyOverrides = async () => {
    if (sampleBlocked()) return;
    try {
      const editedSubject = {
        ...(propertyOverrides.sqft != null ? { sqft: propertyOverrides.sqft } : {}),
        ...(propertyOverrides.beds != null ? { beds: propertyOverrides.beds } : {}),
        ...(propertyOverrides.baths != null ? { baths: propertyOverrides.baths } : {}),
        ...(propertyOverrides.year_built != null ? { year_built: propertyOverrides.year_built } : {}),
        ...(propertyOverrides.property_type != null ? { property_type: propertyOverrides.property_type } : {}),
      };
      await recomputeUnifiedARV(editedSubject, propertyOverrides);
      setOverridesApplied(true);
      toast.success(lang === 'en' ? 'Property details updated' : 'Detalles de propiedad actualizados');
    } catch (e) {
      toast.error(lang === 'en' ? 'Failed to update property details' : 'Error al actualizar detalles');
    }
  };

  // Reset property overrides to original — recompute ARV on the original subject.
  const resetPropertyOverrides = async () => {
    setPropertyOverrides({
      beds: null,
      baths: null,
      sqft: null,
      year_built: null,
      lot_size: null,
      property_type: null,
    });
    setHasPropertyOverrides(false);
    setOverridesApplied(false);
    try {
      await recomputeUnifiedARV(null, {});
      toast.success(lang === 'en' ? 'Reset to original data' : 'Restablecido a datos originales');
    } catch (e) {
      toast.error(lang === 'en' ? 'Failed to reset' : 'Error al restablecer');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-7xl">
        {isSample && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="sample-banner">
            <span className="font-semibold">{lang === 'en' ? 'Example — demo data' : 'Ejemplo — datos de demostración'}</span>
            <span className="text-amber-700">{lang === 'en' ? '· Read-only. Analyze a property of your own for live numbers.' : '· Solo lectura. Analiza una propiedad tuya para ver números en vivo.'}</span>
          </div>
        )}
        {/* Back Navigation */}
        <button
          onClick={() => navigate('/analyze')}
          className="mb-4 flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors group"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          {lang === 'en' ? 'Back to Analyzer' : 'Volver al Analizador'}
        </button>
        
        {/* V4.5: Stale Data Warning - Shows when viewing persisted analysis from sessionStorage */}
        {isFromSessionStorage && (
          <div 
            className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            data-testid="stale-data-warning"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {lang === 'en' ? 'Viewing Saved Analysis' : 'Viendo Análisis Guardado'}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {lang === 'en' 
                    ? 'This analysis was saved from a previous session. Click "Re-run" to get the latest valuation.'
                    : 'Este análisis fue guardado de una sesión anterior. Haga clic en "Volver a ejecutar" para obtener la valoración más reciente.'}
                </p>
              </div>
            </div>
            <button
              onClick={handleRerunAnalysis}
              disabled={rerunningAnalysis}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 rounded-lg transition-colors"
              data-testid="rerun-analysis-btn"
            >
              {rerunningAnalysis ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {lang === 'en' ? 'Running...' : 'Ejecutando...'}
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  {lang === 'en' ? 'Re-run Analysis' : 'Volver a Ejecutar'}
                </>
              )}
            </button>
          </div>
        )}
        
        {/* V5.0: MINIMAL DECISION VIEW - Investor-First Interface */}
        {/* Shows only: Decision, 4 Core Metrics, Short Explanation, Action Suggestion */}
        {!isValuationMode && (
          <div className="mb-4 sm:mb-6">
            <MinimalDecisionView
              decision={getCanonicalDecision(SSOT, lang).decision}
              decisionLabel={getCanonicalDecision(SSOT, lang).label}
              netProfit={ssotNetProfit}
              profitMargin={ssotProfitMargin}
              arv={SSOT?.arv || analysis?.arv}
              targetOffer={ssotMao}
              targetOfferViable={ssotMaoViable}
              arvBlocked={arvBlocked}
              blockedReason={unitCountUnconfirmed ? 'confirm_type' : 'add_comps'}
              purchasePrice={SSOT?.purchase_price || analysis?.purchase_price}
              strategy={analysis?.strategy || 'flip'}
              /* LIVE (BUG 3): these are pure arithmetic, so they must move with the
                 breakdown's figures. Reading them from analysis.rental_analysis while the
                 breakdown read live values meant the page showed TWO different cash flows
                 at once mid-edit — the exact "nothing is connected" problem. The VERDICT
                 above still comes from the backend and updates on Apply, which the recalc
                 bar states. */
              rentalCashflow={analysis?.strategy === 'rental' ? liveRental.monthlyCashFlow : 0}
              rentalDscr={analysis?.strategy === 'rental' ? liveRental.dscr : 0}
              rentalCapRate={analysis?.strategy === 'rental' ? liveRental.capRate : 0}
              isRefinance={isRefinance}
              shortExplanation={getShortExplanation}
              actionSuggestion={getActionSuggestion}
              onViewDetails={() => setShowFullAnalysis(!showFullAnalysis)}
              showDetails={showFullAnalysis}
              lang={lang}
              onToggleFinancing={(mode) => switchFinancingMode(mode)}
              financingMode={analysis?.strategy === 'rental'
                ? (liveRental.isCash ? 'cash' : 'financed')
                : assumptions.purchaseFinancing}
              isRecalculating={recalculating}
              purchaseFinancing={assumptions.purchaseFinancing || 'cash'}
              rehabFinancing={assumptions.rehabFinancing || 'cash'}
              // BUG 1 FIX: use the backend's single source of truth for the interest-only
              // carry (purchase loan + financed rehab, reconciled with loan_costs), the SAME
              // value the Financing Structure section shows. The old inline re-derivation only
              // covered the purchase leg (missing the rehab leg) and used default LTV/rate,
              // producing a wrong, smaller headline ($240 vs the correct $1,440).
              monthlyIOPayment={Math.round(analysis.flip_analysis?.monthly_loan_payment || 0)}
              /* Rental financing comes from the RENTAL analysis, never from the flip
                 purchaseFinancing toggle. Deriving it from purchaseFinancing (which
                 defaults to 'cash') made the hero claim "no loan — paid in cash"
                 while the analysis was still DSCR and the cash flow was net of a
                 $393 payment. */
              rentalFinancingType={liveRental.financingType}
              monthlyDebtService={liveRental.monthlyDebtService}
              monthlyPiti={liveRental.monthlyPiti}
              monthlyEscrow={liveRental.monthlyEscrow}
            />

            {/* F: the user's ARV is graded by the coherence of THEIR comps. When one does
                not fit, say so HERE, under the verdict — not in a small note further down
                — so a BUY is never read without noticing which comp pulled the number. */}
            {!arvBlocked && activeArvDetails?.user_comp_assessment?.outliers?.length > 0 && (
              <UserCompOutlierAlert
                assessment={activeArvDetails.user_comp_assessment}
                lang={lang}
                onReview={() => {
                  const cm = document.querySelector('[data-testid="comparable-manager"]');
                  if (cm) cm.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              />
            )}
            {/* F, option 2: a size difference is information, not a penalty — amber note,
                confidence and verdict untouched. */}
            {!arvBlocked && activeArvDetails?.user_comp_assessment?.size_notes?.length > 0 && (
              <UserCompSizeNote notes={activeArvDetails.user_comp_assessment.size_notes} lang={lang} />
            )}
            
            {/* UNSAVED EDITS vs EXPORTS.
                The PDF and the shared link render the last APPLIED analysis — the numbers
                the engine actually computed — never the pending edits on screen. Without
                saying so, the two silently disagree and a third party receives figures the
                user believes they changed. The export still works; it just tells the truth
                about which numbers it carries. */}
            {hasRentalDraft && (
              <div
                className="mt-3 mx-auto max-w-xl text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-center"
                data-testid="unsaved-edits-export-notice"
              >
                {lang === 'en'
                  ? 'Unsaved changes — the PDF and shared link show the last applied analysis. Apply changes to include them.'
                  : 'Cambios sin aplicar — el PDF y el enlace compartido muestran el último análisis aplicado. Aplica los cambios para incluirlos.'}
              </div>
            )}

            {/* Quick Action Buttons - Always visible (with plan gating) */}
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              {/* SHARE - requires paid plan */}
              <button
                onClick={() => canUseFeature(user, 'save_deals') ? handleShareWithAuth() : handleLockedFeature('save_deals', lang === 'en' ? 'Share' : 'Compartir')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  canUseFeature(user, 'save_deals')
                    ? 'text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-50 border border-slate-200'
                    : 'text-slate-400 bg-slate-100 border border-slate-200'
                }`}
                data-testid="quick-share-btn"
              >
                {canUseFeature(user, 'save_deals') ? <Share2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {lang === 'en' ? 'Share' : 'Compartir'}
              </button>
              {/* PDF - free for all */}
              <button
                onClick={() => setShowPDFPreview(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-all"
                data-testid="quick-pdf-btn"
              >
                <FileText className="w-3.5 h-3.5" />
                {lang === 'en' ? 'PDF' : 'PDF'}
              </button>
              {/* SAVE - requires paid plan. RE-SAVE: enabled whenever there are unsaved
                  changes (edits since last save), so one Save button both creates and
                  updates. The amber dot is the "unsaved changes" indicator. */}
              <button
                onClick={() => canUseFeature(user, 'save_deals') ? handleSaveWithAuth() : handleLockedFeature('save_deals', lang === 'en' ? 'Save Deals' : 'Guardar Tratos')}
                disabled={canUseFeature(user, 'save_deals') && (saving || (isSaved && !hasUnsavedChanges))}
                className={`relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  canUseFeature(user, 'save_deals')
                    ? (hasUnsavedChanges
                        ? 'text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300'
                        : 'text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-50 border border-slate-200 disabled:opacity-50')
                    : 'text-slate-400 bg-slate-100 border border-slate-200'
                }`}
                data-testid="quick-save-btn"
              >
                {hasUnsavedChanges && canUseFeature(user, 'save_deals') && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-500" data-testid="unsaved-indicator" />
                )}
                {canUseFeature(user, 'save_deals') ? <Save className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {hasUnsavedChanges
                  ? (lang === 'en' ? 'Save changes' : 'Guardar cambios')
                  : (isSaved ? (lang === 'en' ? 'Saved' : 'Guardado') : (lang === 'en' ? 'Save' : 'Guardar'))}
              </button>
            </div>
          </div>
        )}
        
        {/* V5.0: FULL ANALYSIS - Hidden by default, shown on "View Full Analysis" click */}
        {(showFullAnalysis || isValuationMode) && (
        <>
        {/* Property Header - Simplified for full view */}
        <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4 sm:mb-6">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">
                {analysis.address}
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground">
                {analysis.city}, {analysis.state} {analysis.zip_code}
              </p>
              
              {/* Address verification notice */}
              {analysis.original_input && analysis.normalized_address && 
               analysis.original_input.toLowerCase().trim() !== analysis.normalized_address.toLowerCase().trim() && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">
                        {lang === 'en' ? 'Address Verification' : 'Verificación de Dirección'}
                      </p>
                      <p className="text-amber-700 mt-1">
                        <span className="font-medium">{lang === 'en' ? 'You entered:' : 'Ingresaste:'}</span>{' '}
                        <span className="italic">{analysis.original_input}</span>
                      </p>
                      <p className="text-amber-700">
                        <span className="font-medium">{lang === 'en' ? 'Analyzing:' : 'Analizando:'}</span>{' '}
                        <span>{analysis.normalized_address}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-4 text-sm text-muted-foreground">
                <span>{analysis.beds} {lang === 'en' ? 'beds' : 'hab'}</span>
                <span>•</span>
                <span>{analysis.baths} {lang === 'en' ? 'baths' : 'baños'}</span>
                <span>•</span>
                <span>{analysis.sqft?.toLocaleString()} {lang === 'en' ? 'sqft' : 'pies²'}</span>
                {analysis.year_built && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span className="hidden sm:inline">{lang === 'en' ? 'Built' : 'Construido'} {analysis.year_built}</span>
                  </>
                )}
                {/* Strategy Badge */}
                {analysis.strategy && (
                  <>
                    <span>•</span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      analysis.strategy === 'flip' ? 'bg-blue-100 text-blue-700' :
                      analysis.strategy === 'rental' ? 'bg-green-100 text-green-700' :
                      analysis.strategy === 'valuation' ? 'bg-indigo-100 text-indigo-700' :
                      'bg-purple-100 text-purple-700'
                    }`} data-testid="strategy-badge">
                      {analysis.strategy === 'flip' && '🔨'}
                      {analysis.strategy === 'rental' && '🏠'}
                      {analysis.strategy === 'brrrr' && '🔄'}
                      {analysis.strategy === 'valuation' && '📊'}
                      {analysis.strategy === 'flip' ? 'Fix & Flip' :
                       analysis.strategy === 'rental' ? (lang === 'en' ? 'Rental' : 'Alquiler') : 
                       analysis.strategy === 'valuation' ? (lang === 'en' ? 'Property Value' : 'Valor de Propiedad') :
                       'BRRRR'}
                    </span>
                  </>
                )}
                {/* Data Freshness Indicator */}
                {analysis.data_freshness?.rentcast_fetched_at && (
                  <DataFreshnessIndicator 
                    fetchedAt={analysis.data_freshness.rentcast_fetched_at}
                    isCached={analysis.data_freshness.is_cached}
                    onRefresh={handleRefreshMarketData}
                    lang={lang}
                    isRefreshing={isRefreshingData}
                    analysisCreatedAt={analysis.created_at}
                  />
                )}
              </div>
            </div>
            {/* V5.0: In full analysis mode, remove redundant decision/toggle elements */}
            {/* The MinimalDecisionView above already shows decision and toggle */}
          </div>
          
          {/* V5.0: Financing toggle removed - now in MinimalDecisionView */}
          
          {/* Deal Classification Card - BUY / NEGOTIATE / PASS (from Decision Engine) */}
          {/* This includes: Decision, Financial Impact, Why (bullets), How to Improve (expanded), Path to Deal */}
          {/* SINGLE SOURCE OF TRUTH: Pass maoRule to ensure all sections use the user's active threshold */}
          {/* P0-2 FIX: Also pass userMinProfit to eliminate hardcoded $25,000 fallback */}
          {/* FLIP ONLY: This component is specific to Flip strategy */}
          {/* VERDICT GATE: never show a confident BUY/PASS off a blocked (unreliable) ARV. */}
          {!isValuationMode && analysis.strategy === 'flip' && arvBlocked && unitCountUnconfirmed && (
            <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-5 mb-4 flex items-start gap-3" data-testid="verdict-needs-type">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-base font-bold text-amber-900">
                  {lang === 'en' ? 'Confirm the property type to see the value' : 'Confirma el tipo de propiedad para ver el valor'}
                </h3>
                <p className="text-sm text-amber-800 mt-1">
                  {lang === 'en'
                    ? 'This looks like a multi-family building and the number of units is not confirmed. No value is computed on a guess — a duplex priced as a fourplex would double the ARV. Pick the type and the analysis runs on it.'
                    : 'Parece un edificio multifamiliar y el número de unidades no está confirmado. No se calcula ningún valor a ciegas — un dúplex valuado como cuádruplex duplicaría el ARV. Elige el tipo y el análisis corre con él.'}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select
                    value={propertyOverrides.property_type || ''}
                    onChange={(e) => handlePropertyOverride('property_type', e.target.value)}
                    className="px-3 py-2 border-2 border-amber-400 rounded-lg bg-white text-sm font-medium"
                    data-testid="confirm-type-select"
                  >
                    <option value="">{lang === 'en' ? 'Choose the type…' : 'Elige el tipo…'}</option>
                    <option value="single_family">{lang === 'en' ? 'Single Family' : 'Unifamiliar'}</option>
                    <option value="duplex">{lang === 'en' ? 'Duplex (2 units)' : 'Dúplex (2 unidades)'}</option>
                    <option value="triplex">{lang === 'en' ? 'Triplex (3 units)' : 'Tríplex (3 unidades)'}</option>
                    <option value="fourplex">{lang === 'en' ? 'Fourplex (4 units)' : 'Cuádruplex (4 unidades)'}</option>
                  </select>
                  <button
                    type="button"
                    onClick={applyPropertyOverrides}
                    disabled={!propertyOverrides.property_type}
                    className="px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
                    data-testid="confirm-type-apply"
                  >
                    {lang === 'en' ? 'Confirm and compute' : 'Confirmar y calcular'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {!isValuationMode && analysis.strategy === 'flip' && arvBlocked && !unitCountUnconfirmed && (
            <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-5 mb-4 flex items-start gap-3" data-testid="verdict-blocked-add-comps">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-base font-bold text-amber-900">
                  {lang === 'en'
                    ? 'No confident verdict yet — ARV needs renovated comps'
                    : 'Aún no hay veredicto confiable — el ARV necesita comps renovados'}
                </h3>
                <p className="text-sm text-amber-800 mt-1">{noRenoMessage}</p>
                <p className="text-xs text-amber-700 mt-1">
                  {lang === 'en'
                    ? "We won't show a PASS/FAIL or profit number off an unreliable ARV. Add your renovated comparables below and the analysis updates to a confident value."
                    : 'No mostraremos un PASS/FAIL ni ganancia con un ARV poco confiable. Agregue sus comparables renovados abajo y el análisis se actualizará a un valor confiable.'}
                </p>
                <button
                  onClick={() => {
                    const cm = document.querySelector('[data-testid="comparable-manager"]');
                    if (cm) cm.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className="mt-3 px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                  data-testid="add-reno-comps-cta"
                >
                  {lang === 'en' ? '+ Add Renovated Comps' : '+ Agregar Comps Renovados'}
                </button>
              </div>
            </div>
          )}
          {!isValuationMode && analysis.strategy === 'flip' && !arvBlocked && analysis.deal_classification && (
            <DealClassificationCard
              classification={SSOT?.deal_classification || analysis.deal_classification}
              lang={lang}
              maoRule={assumptions.maoRule}
              userMinProfit={classificationCriteria?.min_profit}
              userMinMargin={classificationCriteria?.min_margin}
            />
          )}
          
          {/* Target Offer vs Asking Price Comparison - FLIP ONLY.
              PROBLEM 2: hidden when the ARV is blocked — a MAO off an unreliable as-is ARV
              (the "$1 target / -$1.5M profit" case) is misleading; the honest "add comps"
              banner already covers this state. */}
          {/* Also hidden when the MAO is non-viable (negative): there is no target price
              to compare against, so quoting one ("$1") would be fabricated. */}
          {!isValuationMode && analysis.strategy === 'flip' && !arvBlocked && !ssotMaoViable && (
            <div className="rounded-xl p-4 sm:p-6 mb-4 bg-amber-50 border border-amber-200" data-testid="mao-not-viable">
              <h3 className="text-lg font-semibold flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                {lang === 'en' ? 'No Target Offer — Deal Does Not Work at Any Price' : 'Sin Oferta Objetivo — No Funciona a Ningún Precio'}
              </h3>
              <p className="text-sm mt-1 text-amber-700">
                {lang === 'en'
                  ? `Rehab alone exceeds ARV × your ${assumptions.maoRule}% rule, so there is no purchase price that meets your criteria. Lower the rehab scope or re-check the ARV.`
                  : `Solo la renovación excede el ARV × tu regla del ${assumptions.maoRule}%, así que no hay precio de compra que cumpla tus criterios. Reduce el alcance de la renovación o revisa el ARV.`}
              </p>
            </div>
          )}

          {!isValuationMode && analysis.strategy === 'flip' && !arvBlocked && ssotMaoViable && (
            <div className={`rounded-xl p-4 sm:p-6 mb-4 ${
              ssotMao >= (SSOT?.purchase_price || analysis.purchase_price)
                ? 'bg-green-50 border border-green-200'
                : 'bg-amber-50 border border-amber-200'
            }`} data-testid="mao-comparison">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold flex items-center gap-2 ${
                    ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) ? 'text-green-800' : 'text-amber-800'
                  }`}>
                    {ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) ? (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        {lang === 'en' ? 'Price Within Your Target' : 'Precio Dentro de Tu Objetivo'}
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5" />
                        {lang === 'en' ? 'Price Exceeds Your Target' : 'El Precio Excede Tu Objetivo'}
                      </>
                    )}
                  </h3>
                  <p className={`text-sm mt-1 ${
                    ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) ? 'text-green-700' : 'text-amber-700'
                  }`}>
                    {lang === 'en' 
                      ? `Based on your ${assumptions.maoRule}% rule, the target offer is $${formatWholeDollars(ssotMao)}. Purchase price ($${formatWholeDollars(SSOT?.purchase_price || analysis.purchase_price)}) is ${ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) ? 'within' : 'above'} this threshold.`
                      : `Basado en tu regla del ${assumptions.maoRule}%, el precio objetivo es $${formatWholeDollars(ssotMao)}. El precio ($${formatWholeDollars(SSOT?.purchase_price || analysis.purchase_price)}) está ${ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) ? 'dentro de' : 'por encima de'} este umbral.`}
                  </p>
                  {/* Note about profitability vs target discipline */}
                  {ssotMao < (SSOT?.purchase_price || analysis.purchase_price) && ssotNetProfit > 0 && (
                    <p className="text-xs mt-2 text-amber-600 bg-amber-100 px-3 py-1.5 rounded-lg inline-block">
                      {lang === 'en' 
                        ? '💡 Note: This deal shows profit but exceeds your purchase discipline threshold. Consider negotiating.'
                        : '💡 Nota: Este trato muestra ganancia pero excede tu umbral de disciplina. Considera negociar.'}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500 mb-1">{assumptions.maoRule}% {lang === 'en' ? 'Target Rule' : 'Regla Objetivo'}</div>
                  <div className={`text-2xl sm:text-3xl font-bold ${
                    ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) ? 'text-green-800' : 'text-amber-800'
                  }`}>
                    {ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) ? '+' : '-'}${formatWholeDollars(Math.abs(ssotMao - (SSOT?.purchase_price || analysis.purchase_price)))}
                  </div>
                  <div className={`text-sm ${
                    ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) ? 'text-green-700' : 'text-amber-700'
                  }`}>
                    {ssotMao >= (SSOT?.purchase_price || analysis.purchase_price) 
                      ? (lang === 'en' ? 'Under Target' : 'Bajo Objetivo')
                      : (lang === 'en' ? 'Negotiation gap' : 'Brecha de negociación')}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* NOTE: Usage Display and Action Buttons MOVED to bottom of page */}
          
          {/* ARV Warning Banner - Shows for LOW confidence or UNABLE_TO_COMPUTE */}
          <ARVWarningBanner />

          {/* Confidence alarm - shows when the score is below the measured threshold */}
          <ARVConfidenceAlarm />
          
          {/* NOTE: Action buttons MOVED to bottom of page - see "USAGE DISPLAY + ACTION BUTTONS" section */}
          
          {/* Explain this deal panel */}
          {showExplanation && (
            <div className="mt-4 p-4 sm:p-5 bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl border border-blue-100 animate-fade-in" data-testid="explanation-panel">
              <h3 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-secondary" />
                {t('explain_deal_title')}
              </h3>
              <ul className="space-y-2.5">
                {explanationBullets.map((bullet, index) => (
                  <li 
                    key={index} 
                    className={`flex items-start gap-2.5 text-sm ${
                      bullet.type === 'positive' ? 'text-green-700' :
                      bullet.type === 'negative' ? 'text-red-700' :
                      'text-slate-700'
                    }`}
                  >
                    <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                      bullet.type === 'positive' ? 'bg-green-100' :
                      bullet.type === 'negative' ? 'bg-red-100' :
                      'bg-yellow-100'
                    }`}>
                      {bullet.type === 'positive' ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                      ) : bullet.type === 'negative' ? (
                        <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                      ) : (
                        <Info className="w-3.5 h-3.5 text-yellow-600" />
                      )}
                    </span>
                    <span>{bullet.text}</span>
                  </li>
                ))}
              </ul>
              
              {/* Deal Score Confidence detail in explanation */}
              <div className={`mt-4 pt-3 border-t border-blue-100 flex items-center gap-2 text-sm ${
                dealConfidence.level === 'high' ? 'text-green-700' :
                dealConfidence.level === 'medium' ? 'text-yellow-700' :
                'text-red-700'
              }`}>
                <ShieldCheck className="w-4 h-4" />
                <span>
                  {lang === 'en' 
                    ? `Analysis confidence: ${dealConfidence.level.charAt(0).toUpperCase() + dealConfidence.level.slice(1)}`
                    : `Confianza del análisis: ${dealConfidence.level === 'high' ? 'Alta' : dealConfidence.level === 'medium' ? 'Media' : 'Baja'}`}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Key Metrics Grid - Responsive cards with proper overflow handling */}
        <div className={`grid ${isValuationMode ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'} gap-3 sm:gap-4 mb-4 sm:mb-6`}>
          {/* Asking Price - First to show importance */}
          {/* Purchase Price Card - NOW EDITABLE */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-xl shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-slate-300 flex-shrink-0" />
                <span className="text-xs font-medium text-slate-300">
                  {/* A cash-out REFINANCE has no purchase price — the deal is underwritten
                      against the appraised value. Labelling it "Purchase Price" (and doing
                      so a second time next to "Appraised Value" for the identical figure)
                      describes a transaction that is not happening. */}
                  {isRefinance
                    ? (lang === 'en' ? 'Appraised Value' : 'Valor Tasado')
                    : isValuationMode
                      ? (lang === 'en' ? 'Current Value / Price' : 'Valor / Precio Actual')
                    // nosemgrep: purchase-price-must-be-refinance-aware -- isRefinance ternary above
                      : (lang === 'en' ? 'Purchase Price' : 'Precio de Compra')}
                </span>
              </div>
              {!isEditingPurchasePrice && (
                <button
                  onClick={() => setIsEditingPurchasePrice(true)}
                  className="text-xs px-2 py-0.5 bg-white/10 hover:bg-white/20 rounded text-slate-300 transition-colors"
                  data-testid="edit-purchase-price-btn"
                >
                  {lang === 'en' ? 'Edit' : 'Editar'}
                </button>
              )}
            </div>
            
            {isEditingPurchasePrice ? (
              <div className="space-y-2">
                <div className="relative">
                  {/* BUG 5: $0,000,000 formatting; store clean number, parse-safe for calc. */}
                  <FormattedCurrencyInput
                    value={editedPurchasePrice !== null ? editedPurchasePrice : (analysis.purchase_price || '')}
                    onChange={(val) => setEditedPurchasePrice(parseFormattedNumber(val))}
                    prefix="$"
                    inputMode="numeric"
                    className="w-full pr-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditingPurchasePrice(false);
                        if (editedPurchasePrice !== null && editedPurchasePrice !== analysis.purchase_price) {
                          recalculateAnalysis(assumptions, propertyOverrides, { purchasePriceOverride: editedPurchasePrice });
                        }
                      } else if (e.key === 'Escape') {
                        setIsEditingPurchasePrice(false);
                        setEditedPurchasePrice(null);
                      }
                    }}
                    autoFocus
                    data-testid="purchase-price-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setIsEditingPurchasePrice(false);
                      if (editedPurchasePrice !== null && editedPurchasePrice !== analysis.purchase_price) {
                        recalculateAnalysis(assumptions, propertyOverrides, { purchasePriceOverride: editedPurchasePrice });
                      }
                    }}
                    className="flex-1 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded transition-colors"
                    data-testid="apply-purchase-price-btn"
                  >
                    {lang === 'en' ? 'Apply' : 'Aplicar'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingPurchasePrice(false);
                      setEditedPurchasePrice(null);
                    }}
                    className="flex-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded transition-colors"
                    data-testid="cancel-purchase-price-btn"
                  >
                    {lang === 'en' ? 'Cancel' : 'Cancelar'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-xl sm:text-2xl font-bold break-all">
                  {/* Show "No price" placeholder for valuation mode with zero/empty price */}
                  {isValuationMode && (!analysis.purchase_price || analysis.purchase_price === 0) && editedPurchasePrice === null ? (
                    <span className="text-slate-400 italic text-lg">{lang === 'en' ? 'No price' : 'Sin precio'}</span>
                  ) : (
                    <>${formatWholeDollars(editedPurchasePrice !== null ? editedPurchasePrice : analysis.purchase_price)}</>
                  )}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {editedPurchasePrice !== null && editedPurchasePrice !== analysis.purchase_price ? (
                    <span className="text-amber-400">
                      {lang === 'en' ? 'Edited (Orig: $' : 'Editado (Orig: $'}{formatWholeDollars(analysis.purchase_price)})
                    </span>
                  ) : (
                    isValuationMode 
                      ? (lang === 'en' ? 'Optional - for equity calculation' : 'Opcional - para cálculo de equidad')
                      : (lang === 'en' ? 'Your input' : 'Tu entrada')
                  )}
                </div>
              </>
            )}
          </div>

          {/* Estimated Value Card (formerly ARV) - with info tooltip */}
          <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-5" data-testid="estimated-value-card">
            <div className="flex items-center gap-2 mb-2 group relative flex-wrap">
              <TrendingUp className="w-4 h-4 text-secondary flex-shrink-0" />
              <span className="text-xs font-medium text-muted-foreground">
                {isRefinance
                  ? (lang === 'en' ? 'Appraised Value' : 'Valor Tasado')
                  : t('result_arv')}
                {/* WHOSE NUMBER IS THIS. Marked wherever the value appears — screen and
                    PDF — so a lender can see the investor set it, not us. */}
                {isUserArv && (
                  <span className="text-[10px] font-semibold text-amber-700" data-testid="arv-your-figure">
                    {arvLabelSuffix}
                  </span>
                )}
              </span>
              {/* SFR/mode basis label: After-Repair (flip/brrrr) vs As-Is (rental/valuation),
                  so the intentional cross-mode value difference reads as deliberate. */}
              {!arvBlocked && (
                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${_isAfterRepair ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {arvBasisLabel}
                </span>
              )}
              <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help flex-shrink-0" />
              {/* Estimated Value Tooltip */}
              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 w-64">
                {lang === 'en' 
                  ? 'Estimated resale value based on comparable properties in your area'
                  : 'Valor de reventa estimado basado en propiedades comparables en tu área'}
                <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-800"></div>
              </div>
            </div>
            {analysis.arv_details?.arv_status === 'UNABLE_TO_COMPUTE' ? (
              <>
                <div className="text-lg sm:text-xl font-bold text-red-600 break-words mb-1" data-testid="arv-unable-to-compute">
                  {lang === 'en' ? 'Unable to Compute' : 'No se puede calcular'}
                </div>
                <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {lang === 'en' ? 'Appraisal Required' : 'Requiere Tasación'}
                </span>
              </>
            ) : noRenovatedComps ? (
              <>
                {/* ALWAYS A NUMBER, ALWAYS THE CAVEAT — both, never one instead of the other.
                    The engine never returns a blank here: with no renovated cluster it walks
                    down to livable sold comps, then for-sale listings, then a floor-anchored
                    estimate, and labels whichever it used. This card used to throw that value
                    away and render only "Add renovated comps", so a user asking what a
                    property is worth got a to-do item and no figure at all. The number is
                    shown in amber, next to the caveat and the low-confidence badge, so it
                    reads as an estimate that wants better comps — not as a confident ARV. */}
                {effectiveARV > 0 && (
                  <>
                    <div className="text-xl sm:text-2xl font-bold text-amber-800 break-words">
                      ${formatWholeDollars(effectiveARV)}
                    </div>
                    <span className="sr-only" data-testid="arv-amount">
                      ${formatWholeDollars(effectiveARV)}
                    </span>
                  </>
                )}
                <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  {lang === 'en' ? 'Low confidence — limited comps' : 'Baja confianza — comps limitados'}
                </span>
                {/* What the user can DO about it. The testid keeps its original name so the
                    specs that assert the blocked state keep working; only the wording moved
                    from naming a defect to naming an action. */}
                <div className="text-sm font-semibold text-amber-700 break-words mt-1" data-testid="arv-add-renovated-comps">
                  {lang === 'en'
                    ? 'Add your comps for a stronger ARV'
                    : 'Agrega tus comps para un ARV más preciso'}
                </div>
                {/* WHY this particular ARV is weak — different per fallback tier, and until
                    now computed by the engine and read by nobody. Localised off the tier key
                    because arv_warning itself is English prose with no translation path. */}
                {arvTierNote && (
                  <div className="text-xs text-slate-500 mt-1 leading-snug" data-testid="arv-tier-note">
                    {arvTierNote}
                  </div>
                )}
                {/* Only when it says something the headline does not. Now that the value is
                    on screen, an as-is figure equal to it just prints the same number twice. */}
                {analysis.arv_details?.no_renovated_comps_flag?.as_is_estimate > 0
                  && Math.round(analysis.arv_details.no_renovated_comps_flag.as_is_estimate) !== Math.round(effectiveARV) && (
                  <div className="text-xs text-slate-500 mt-1">
                    {lang === 'en' ? 'As-is estimate only: ' : 'Solo estimación actual: '}
                    ${formatWholeDollars(analysis.arv_details.no_renovated_comps_flag.as_is_estimate)}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* NOT EDITABLE. The ARV is what CHEKSTE's analysis produces, by one of
                    three routes: the automatic comps, comparables the user adds, or the
                    two blended. Typing a number here bypassed the engine entirely — every
                    filter, the comp funnel, the size adjustment, the livability floor —
                    and produced a figure with no evidence behind it that then drove
                    profit, margin, MAO and the verdict.
                    Adding comparables is the supported way to change it: the user supplies
                    evidence and the engine processes it. Measured on 5060 Tireman, three
                    user comps at $81-90/sqft move the ARV from $192,400 to $173,237. */}
                <div className="text-xl sm:text-2xl font-bold text-primary break-all mb-1"
                     data-testid="arv-amount-display">
                  ${formatWholeDollars(effectiveARV)}
                </div>
                <div className="text-[11px] text-slate-400">
                  {lang === 'en'
                    ? "CHEKSTE's estimate — add comparables to change it"
                    : 'Estimación de CHEKSTE — agrega comparables para cambiarla'}
                </div>
                {/* The thread between the number and the warning about it. The full notice
                    is ~260 lines of JSX above this — correct in ORDER, but one or two phone
                    scrolls away, and the reader who comes straight down to the figure is
                    exactly the reader who acts on it. So the figure carries a pointer.
                    NOT a second notice: no copy repeated, no amounts, no reason given. It
                    reads the same `alarmReason` the box above does, so one can never appear
                    without the other. */}
                {alarmReason && (
                  arvAlarmSeverity(alarmReason) === 'warning' ? (
                    <div className="text-[11px] text-amber-700 mt-1 leading-snug font-medium"
                         data-testid="arv-not-confirmed-mark"
                         data-alarm-reason={alarmReason}
                         data-alarm-severity="warning">
                      ⚠ {arvNotConfirmedMark(lang, 'warning')}
                    </div>
                  ) : (
                    /* A pointer, not a second warning: no triangle, no amber, normal
                       weight. It must not out-shout the line above it, which is the
                       card's own quiet caption. */
                    <div className="text-[11px] text-slate-500 mt-1 leading-snug"
                         data-testid="arv-not-confirmed-mark"
                         data-alarm-reason={alarmReason}
                         data-alarm-severity="note">
                      {arvNotConfirmedMark(lang, 'note')}
                    </div>
                  )
                )}
                {/* Kept for tests/consumers that read the plain rendered value. */}
                <span className="sr-only" data-testid="arv-amount">
                  ${formatWholeDollars(effectiveARV)}
                </span>
                {/* BUG 3: removed the abstract "Confidence: Medium/45" badge — it doesn't help
                    the investor and duplicates nothing actionable. The confidence LEVEL still
                    drives the honest-block / "add renovated comps" warnings below when it's low. */}
              </>
            )}

            {/* What the figure above is, and what it is not — under the number itself,
                where the decision happens. Gated on !arvBlocked like the basis label: with
                no value to qualify, "estimate based on comparable sales" would describe
                something the screen is not showing. */}
            {!arvBlocked && (
              <div className="text-[11px] text-slate-400 mt-1.5 leading-snug"
                   data-testid="arv-appraisal-disclaimer">
                {lang === 'en'
                  ? 'Estimate based on comparable sales — not a substitute for a professional appraisal.'
                  : 'Estimación basada en ventas comparables — no sustituye una tasación profesional.'}
              </div>
            )}
          </div>

          {/* Equity Spread Card - Show in valuation mode as "Potential Equity" */}
          {isValuationMode && (
            <div className={`rounded-xl shadow-sm border-2 p-4 sm:p-5 ${equityColors.bg} ${equityColors.border}`} data-testid="potential-equity-card">
              <div className="flex items-center gap-2 mb-2 group relative">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${equityColors.indicator}`}></div>
                <span className={`text-xs font-medium ${equityColors.text}`}>
                  {lang === 'en' ? 'Potential Equity' : 'Equidad Potencial'}
                </span>
                <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help flex-shrink-0" />
                <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 w-64">
                  {lang === 'en' 
                    ? 'Difference between estimated value and current price/value'
                    : 'Diferencia entre el valor estimado y el precio/valor actual'}
                  <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-800"></div>
                </div>
              </div>
              {/* GAP #2: when the ARV is honest-blocked (no renovated comps), equity is
                  derived from an unreliable value — don't show a confident number. */}
              {arvBlocked ? (
                <>
                  <div className="text-base sm:text-lg font-bold text-amber-700 break-words" data-testid="equity-add-comps">
                    {lang === 'en' ? 'Add renovated comps' : 'Agregue comps renovados'}
                  </div>
                  <div className="text-xs mt-1 text-amber-600">
                    {lang === 'en' ? 'Equity needs a reliable value' : 'La equidad necesita un valor confiable'}
                  </div>
                </>
              ) : (
                <>
                  <div className={`text-xl sm:text-2xl font-bold break-all ${equityColors.text}`} data-testid="equity-amount">
                    ${formatWholeDollars(equitySpread)}
                  </div>
                  <div className={`text-xs mt-1 ${equityColors.text}`}>
                    {equitySpreadPercent.toFixed(1)}% {lang === 'en' ? 'of estimated value' : 'del valor estimado'}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Investment-Only Cards - Hidden in valuation mode */}
          {!isValuationMode && (
            <>
              {/* FLIP-ONLY CARDS: Net Profit, Rehab, Target Offer */}
              {analysis.strategy === 'flip' && (
                <>
                  {/* Net Profit Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-success flex-shrink-0" />
                      <span className="text-xs font-medium text-muted-foreground">
                        {lang === 'en' ? 'Net Profit' : 'Ganancia Neta'}
                      </span>
                    </div>
                    <div className={`text-xl sm:text-2xl font-bold break-all ${financialTextColor(ssotNetProfit)}`}>
                      ${formatWholeDollars(ssotNetProfit)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {ssotProfitMargin?.toFixed(1)}% margin
                    </div>
                  </div>

                  {/* Rehab Cost Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Wrench className="w-4 h-4 text-warning flex-shrink-0" />
                      <span className="text-xs font-medium text-muted-foreground">
                        {t('result_rehab')}
                      </span>
                    </div>
                    <div className="text-xl sm:text-2xl font-bold text-primary break-all">
                      ${formatWholeDollars(analysis.rehab_estimate)}
                    </div>
                  </div>

                  {/* Target Offer Price Card (formerly MAO) - with tooltip */}
                  <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-2 group relative">
                      <Target className="w-4 h-4 text-secondary flex-shrink-0" />
                      <span className="text-xs font-medium text-muted-foreground">
                        {t('result_mao')}
                      </span>
                      <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help flex-shrink-0" />
                      {/* Target Offer Price Tooltip */}
                      <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 w-64">
                        {lang === 'en' 
                          ? `Maximum price you should pay to meet your ${assumptions.maoRule}% investment criteria. Target = (Value × ${assumptions.maoRule}%) - Rehab`
                          : `Precio máximo que debes pagar para cumplir tus criterios del ${assumptions.maoRule}%. Objetivo = (Valor × ${assumptions.maoRule}%) - Rehab`}
                        <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-800"></div>
                      </div>
                    </div>
                    <div className="text-xl sm:text-2xl font-bold text-primary break-all">
                      {arvDerived(`$${formatWholeDollars(analysis.flip_analysis?.mao)}`)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{assumptions.maoRule}% {lang === 'en' ? 'rule' : 'regla'}</div>
                  </div>

                  {/* Equity Spread Card (for Flip) */}
                  <div className={`rounded-xl shadow-sm border-2 p-4 sm:p-5 ${equityColors.bg} ${equityColors.border}`}>
                    <div className="flex items-center gap-2 mb-2 group relative">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${equityColors.indicator}`}></div>
                      <span className={`text-xs font-medium ${equityColors.text}`}>
                        {t('result_equity_spread')}
                      </span>
                      <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help flex-shrink-0" />
                      {/* Equity Spread Tooltip */}
                      <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 w-64">
                        {t('result_equity_spread_tooltip')}
                        <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-800"></div>
                      </div>
                    </div>
                    {/* GAP #2: blocked ARV -> equity is unreliable; don't show a confident number. */}
                    {arvBlocked ? (
                      <>
                        <div className="text-base sm:text-lg font-bold text-amber-700 break-words">
                          {lang === 'en' ? 'Add renovated comps' : 'Agregue comps renovados'}
                        </div>
                        <div className="text-xs mt-1 text-amber-600">
                          {lang === 'en' ? 'Needs a reliable ARV' : 'Necesita un ARV confiable'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`text-xl sm:text-2xl font-bold break-all ${equityColors.text}`}>
                          ${formatWholeDollars(equitySpread)}
                        </div>
                        <div className={`text-xs mt-1 ${equityColors.text}`}>
                          {equitySpreadPercent.toFixed(1)}% {lang === 'en' ? 'of Est. Value' : 'del Valor Est.'}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
              
              {/* RENTAL-ONLY CARDS: Cash Flow, DSCR/Cap Rate */}
              {analysis.strategy === 'rental' && analysis.rental_analysis && (
                <>
                  {/* Monthly Cash Flow Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-xs font-medium text-muted-foreground">
                        {lang === 'en' ? 'Monthly Cash Flow' : 'Flujo Mensual'}
                      </span>
                    </div>
                    <div className={`text-xl sm:text-2xl font-bold break-all ${financialTextColor(liveRental.monthlyCashFlow)}`}>
                      ${Math.round(liveRental.monthlyCashFlow).toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      ${Math.round((liveRental.monthlyCashFlow) * 12).toLocaleString()}/yr
                    </div>
                  </div>

                  {/* DSCR or Cap Rate Card */}
                  {liveRental.financingType === 'dscr' ? (
                    <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="text-xs font-medium text-muted-foreground">DSCR</span>
                      </div>
                      <div className={`text-xl sm:text-2xl font-bold break-all ${(liveRental.dscr) >= 1.25 ? 'text-green-600' : (liveRental.dscr) >= 1.0 ? 'text-amber-600' : 'text-red-600'}`}>
                        {(liveRental.dscr).toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {(liveRental.dscr) >= 1.25 
                          ? (lang === 'en' ? 'Lender Ready' : 'Listo para Prestamista')
                          : (liveRental.dscr) >= 1.0 
                            ? (lang === 'en' ? 'Marginal' : 'Marginal')
                            : (lang === 'en' ? 'Below 1.0' : 'Bajo 1.0')}
                      </div>
                      {liveRental.financingType === 'dscr' && 
                       liveRental.monthlyDebtService > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <div className="text-xs text-slate-500">
                            {lang === 'en' ? 'Min rent for DSCR:' : 'Renta mín. para DSCR:'}
                          </div>
                          <div
                            data-testid="rental-min-rent-dscr"
                            className={`text-xs font-semibold ${
                              rentQualifies ? 'text-green-600' : 'text-amber-600'
                            }`}
                          >
                            ${formatWholeDollars(minRentForDscr)}/mo
                            {rentQualifies
                              ? (lang === 'en' ? ' ✓ Qualifies' : ' ✓ Califica')
                              : (lang === 'en' ? ' ✗ Short' : ' ✗ Insuficiente')
                            }
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="text-xs font-medium text-muted-foreground">
                          {lang === 'en' ? 'Cap Rate' : 'Cap Rate'}
                        </span>
                      </div>
                      <div className="text-xl sm:text-2xl font-bold text-blue-600 break-all">
                        {(liveRental.capRate).toFixed(1)}%
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {lang === 'en' ? 'Cash Purchase' : 'Compra en Efectivo'}
                      </div>
                    </div>
                  )}

                  {/* Cash on Cash Return Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        {lang === 'en' ? 'Cash-on-Cash' : 'Retorno Efectivo'}
                        <span className="cursor-help" title={lang === 'en' 
                          ? (isRefinance ? 'Not applicable on a refinance — no cash invested' : 'Annual Cash Flow ÷ Cash Required (Down Payment + Closing Costs)') 
                          : 'Flujo Anual ÷ Efectivo Requerido (Enganche + Costos de Cierre)'}>
                          <HelpCircle className="w-3 h-3 text-slate-400" />
                        </span>
                      </span>
                    </div>
                    <div className="text-xl sm:text-2xl font-bold text-purple-600 break-all">
                      {(liveRental.cashOnCash).toFixed(1)}%
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {lang === 'en' ? 'Annual return on your cash invested' : 'Retorno anual sobre tu efectivo invertido'}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Strategy-Specific Metrics Section - Only show ADDITIONAL metrics not in Core Numbers */}
        {/* NOTE: Flip metrics (ARV, Rehab, Profit, Target) are already in Core Numbers - don't duplicate */}
        {!isValuationMode && analysis.strategy && analysis.strategy !== 'valuation' && (analysis.strategy === 'rental' || analysis.strategy === 'brrrr') && (
          <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 mb-4 sm:mb-6" data-testid="strategy-metrics">
            
            {/* RENTAL ANALYSIS - Decision-First Layout */}
            {analysis.strategy === 'rental' && analysis.rental_analysis && (
              <>
                {/* HERO: Decision + Key Numbers */}
                <div className={`rounded-2xl p-5 mb-4 ${
                  analysis.rental_classification?.decision === 'BUY' 
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                    : analysis.rental_classification?.decision === 'FLEX_BUY'
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                      : analysis.rental_classification?.decision === 'SPECULATIVE'
                        ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                        : 'bg-gradient-to-br from-red-500 to-rose-600'
                }`} data-testid="rental-decision-hero">
                  
                  {/* Decision Row */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-white">
                      <div className="text-3xl sm:text-4xl font-black tracking-tight">
                        {/* ONE VERDICT. This used to map the classification with its own
                            vocabulary (BUY/CONSIDER/MAYBE/AVOID) while the hero mapped the
                            SAME stored value with another (STRONG/MODERATE/WEAK RENTAL),
                            and deal_decision.rating supplied a third word (PASS). One deal
                            therefore showed up to three verdicts at once. Both now go
                            through getCanonicalDecision, so they cannot disagree.
                            deal_decision.rating is deliberately NOT used here: it counts
                            criteria passed, which is a different question from the verdict. */}
                        {getCanonicalDecision(SSOT, lang).label}
                      </div>
                      <div className="text-sm text-white/80 mt-1">
                        {lang === 'en' ? 'Rental Investment' : 'Inversión de Alquiler'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-white/70 uppercase tracking-wide">
                        {lang === 'en' ? 'Criteria' : 'Criterios'}
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {analysis.deal_decision?.criteria_passed || 0}
                        <span className="text-sm font-normal">/{analysis.deal_decision?.total_criteria || 4}</span>
                      </div>
                      <div className="text-xs text-white/70">
                        {lang === 'en' ? 'Passed' : 'Aprobados'}
                      </div>
                    </div>
                  </div>

                  {/* Key Numbers - Cash Flow + DSCR */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/20 backdrop-blur rounded-xl p-3">
                      <div className="text-xs text-white/80 mb-1">
                        {lang === 'en' ? 'Monthly Cash Flow' : 'Flujo Mensual'}
                      </div>
                      <div className="text-2xl font-bold text-white">
                        ${Math.round(liveRental.monthlyCashFlow).toLocaleString()}
                      </div>
                      <div className="text-xs text-white/70">
                        ${Math.round((liveRental.monthlyCashFlow) * 12).toLocaleString()}/yr
                      </div>
                    </div>
                    
                    {liveRental.financingType === 'dscr' ? (
                      <div className="bg-white/20 backdrop-blur rounded-xl p-3">
                        <div className="text-xs text-white/80 mb-1">DSCR</div>
                        <div className="text-2xl font-bold text-white">
                          {(liveRental.dscr).toFixed(2)}
                        </div>
                        <div className="text-xs text-white/70">
                          {(liveRental.dscr) >= 1.25 
                            ? (lang === 'en' ? '✓ Lender Ready' : '✓ Listo para Prestamista')
                            : (liveRental.dscr) >= 1.0 
                              ? (lang === 'en' ? '⚠ Marginal' : '⚠ Marginal')
                              : (lang === 'en' ? '✗ Below 1.0' : '✗ Bajo 1.0')}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white/20 backdrop-blur rounded-xl p-3">
                        <div className="text-xs text-white/80 mb-1">
                          {lang === 'en' ? 'Cap Rate' : 'Cap Rate'}
                        </div>
                        <div className="text-2xl font-bold text-white">
                          {(liveRental.capRate).toFixed(1)}%
                        </div>
                        <div className="text-xs text-white/70">
                          {lang === 'en' ? 'Cash Purchase' : 'Compra en Efectivo'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ========================================
                    EDITABLE RENTAL FINANCIAL BREAKDOWN
                    All inputs directly affect recalculation
                    Spanish tooltips for operating expenses
                    ======================================== */}
                <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4" data-testid="rental-financial-breakdown">
                  <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2 border-b pb-2">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    {lang === 'en' ? 'Editable Financial Breakdown' : 'Desglose Financiero Editable'}
                    {recalculating && <RefreshCw className="w-3 h-3 text-slate-400 animate-spin ml-2" />}
                  </h4>
                  
                  {/* ====== INCOME Section ====== */}
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
                      {lang === 'en' ? 'INCOME' : 'INGRESOS'}
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-green-700">{lang === 'en' ? 'Monthly Rent' : 'Renta Mensual'}</span>
                        <div className="flex items-center gap-2">
                          {/* BUG 5: $0,000,000 formatting; value parsed clean for calc. */}
                          <FormattedCurrencyInput
                            value={rentalField('rentalMonthlyRent') || ''}
                            onChange={(val) => {
                              const num = parseFormattedNumber(val) || 0;
                              stageRental({ rentalMonthlyRent: num });
                            }}
                            prefix="$"
                            className="w-28 px-2 py-1 text-right font-bold text-green-800 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            data-testid="rental-monthly-rent-input"
                          />
                        </div>
                      </div>
                      {analysis.rent_estimate > 0 && (
                        <div className="flex justify-between items-center text-xs mt-1">
                          <span className="text-green-600 flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            {lang === 'en' ? 'Market Est.:' : 'Est. Mercado:'}
                          </span>
                          <span className={`font-medium ${
                            (rentalField('rentalMonthlyRent') || 0) >= analysis.rent_estimate 
                              ? 'text-green-600' 
                              : 'text-amber-600'
                          }`}>
                            {/* NOT multiplied by the unit count. `rent_estimate` is
                                RentCast's /avm/rent/long-term figure and it is already the
                                rent of the WHOLE property — measured on 252 Philip St,
                                where RentCast priced the entire 6-bed / 2,358 sqft envelope
                                at $2,420. Multiplying printed "$4,840/mo (2 units)" as the
                                market benchmark for a Detroit 48215 duplex, which is double,
                                and it is the number the user judges their own rent against.
                                The comparison beside it was always right — total vs total;
                                only the printed figure was wrong. */}
                            ${formatWholeDollars(analysis.rent_estimate || 0)}/mo
                            {(analysis.rental_analysis?.unit_count || 1) > 1
                              && (lang === 'en' ? ' (whole property)' : ' (propiedad completa)')}
                            {(rentalField('rentalMonthlyRent') || 0) >= analysis.rent_estimate 
                              ? (lang === 'en' ? ' ✓ At/Above market' : ' ✓ En/Sobre mercado')
                              : (lang === 'en' ? ' ↑ Below market' : ' ↑ Bajo mercado')
                            }
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm border-t border-green-200 pt-2">
                        <span className="text-green-700">{lang === 'en' ? 'Annual Gross Rent' : 'Renta Anual Bruta'}</span>
                        <span className="font-bold text-green-800">${formatWholeDollars((rentalField('rentalMonthlyRent') || 0) * 12)}</span>
                      </div>
                    </div>
                  </div>

                  {/* ====== OPERATING EXPENSES Section (with Spanish tooltips) ====== */}
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">
                      {lang === 'en' ? 'OPERATING EXPENSES' : 'GASTOS OPERATIVOS'}
                    </div>
                    {/* Say plainly where taxes/insurance/HOA are counted. They are inputs
                        here, but they are CHARGED in the PITIA payment — counted once,
                        never in both places. Without this line the same three costs appear
                        in two sections and it reads like a double count. */}
                    <div className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 mb-2"
                         data-testid="opex-included-in-pitia-note">
                      {lang === 'en'
                        ? 'ⓘ Taxes, insurance & HOA are already included in the PITIA payment below — they are entered here but counted once, in the payment.'
                        : 'ⓘ Impuestos, seguro y HOA ya están incluidos en el pago PITIA abajo — se ingresan aquí pero se cuentan una sola vez, en el pago.'}
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 space-y-2">
                      {/* Annual Property Taxes */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-red-700">{lang === 'en' ? 'Annual Property Taxes' : 'Impuestos Anuales'}</span>
                          <span className="text-red-400 cursor-help" title={lang === 'en' 
                            ? "Estimated annual property taxes. Converted to monthly for analysis."
                            : "Monto anual estimado de impuestos de la propiedad. Se convierte a mensual para el análisis."}>ⓘ</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FormattedCurrencyInput
                            value={rentalField('rentalAnnualTaxes') || ''}
                            onChange={(val) => {
                              const num = parseFormattedNumber(val) || 0;
                              stageRental({ rentalAnnualTaxes: num });
                            }}
                            prefix="$"
                            className="w-24 px-2 py-1 text-right text-red-800 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="0"
                            data-testid="rental-annual-taxes-input"
                          />
                          <span className="text-xs text-red-500">/yr</span>
                        </div>
                      </div>
                      
                      {/* Annual Insurance */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-red-700">{lang === 'en' ? 'Annual Insurance' : 'Seguro Anual'}</span>
                          <span className="text-red-400 cursor-help" title={lang === 'en'
                            ? "Annual insurance cost. Used to calculate total monthly expenses."
                            : "Costo anual del seguro. Se usa para calcular el gasto mensual total."}>ⓘ</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FormattedCurrencyInput
                            value={rentalField('rentalAnnualInsurance') || ''}
                            onChange={(val) => {
                              const num = parseFormattedNumber(val) || 0;
                              stageRental({ rentalAnnualInsurance: num });
                            }}
                            prefix="$"
                            className="w-24 px-2 py-1 text-right text-red-800 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="0"
                            data-testid="rental-annual-insurance-input"
                          />
                          <span className="text-xs text-red-500">/yr</span>
                        </div>
                      </div>

                      {/* Vacancy Reserve % */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-red-700">{lang === 'en' ? 'Vacancy Reserve' : 'Reserva Vacancia'}</span>
                          <span className="text-red-400 cursor-help" title={lang === 'en'
                            ? "Reserve for months when property might not be rented."
                            : "Reserva para meses o periodos en los que la propiedad podría no estar rentada."}>ⓘ</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.5"
                            value={rentalField('rentalVacancyPct') ?? ''}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              stageRental({ rentalVacancyPct: val });
                            }}
                            className="w-16 px-2 py-1 text-right text-red-800 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            data-testid="rental-vacancy-pct-input"
                          />
                          <span className="text-red-600">%</span>
                          <span className="text-xs text-red-500">(${Math.round((rentalField('rentalMonthlyRent') || 0) * (rentalField('rentalVacancyPct') || 0) / 100)}/mo)</span>
                        </div>
                      </div>

                      {/* Maintenance Reserve % */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-red-700">{lang === 'en' ? 'Maintenance Reserve' : 'Mantenimiento'}</span>
                          <span className="text-red-400 cursor-help" title={lang === 'en'
                            ? "Estimated reserve for repairs and ordinary maintenance."
                            : "Reserva estimada para reparaciones y mantenimiento ordinario."}>ⓘ</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.5"
                            value={rentalField('rentalMaintenancePct') ?? ''}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              stageRental({ rentalMaintenancePct: val });
                            }}
                            className="w-16 px-2 py-1 text-right text-red-800 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            data-testid="rental-maintenance-pct-input"
                          />
                          <span className="text-red-600">%</span>
                          <span className="text-xs text-red-500">(${Math.round((rentalField('rentalMonthlyRent') || 0) * (rentalField('rentalMaintenancePct') || 0) / 100)}/mo)</span>
                        </div>
                      </div>

                      {/* Property Management % */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-red-700">{lang === 'en' ? 'Property Management' : 'Administración'}</span>
                          <span className="text-red-400 cursor-help" title={lang === 'en'
                            ? "Property management cost if using a property manager."
                            : "Costo de administración de la propiedad si usas property management."}>ⓘ</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.5"
                            value={rentalField('rentalManagementPct') ?? ''}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              stageRental({ rentalManagementPct: val });
                            }}
                            className="w-16 px-2 py-1 text-right text-red-800 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            data-testid="rental-management-pct-input"
                          />
                          <span className="text-red-600">%</span>
                          <span className="text-xs text-red-500">(${Math.round((rentalField('rentalMonthlyRent') || 0) * (rentalField('rentalManagementPct') || 0) / 100)}/mo)</span>
                        </div>
                      </div>

                      {/* HOA */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-red-700">HOA</span>
                          <span className="text-red-400 cursor-help" title={lang === 'en'
                            ? "Homeowners Association monthly fee if applicable."
                            : "Cuota mensual de la asociación de propietarios si aplica."}>ⓘ</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FormattedCurrencyInput
                            value={rentalField('rentalHoa') || ''}
                            onChange={(val) => {
                              const num = parseFormattedNumber(val) || 0;
                              stageRental({ rentalHoa: num });
                            }}
                            prefix="$"
                            className="w-20 px-2 py-1 text-right text-red-800 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="0"
                            data-testid="rental-hoa-input"
                          />
                          <span className="text-xs text-red-500">/mo</span>
                        </div>
                      </div>

                      {/* Other OpEx */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-red-700">{lang === 'en' ? 'Other Monthly OpEx' : 'Otros Gastos Operativos'}</span>
                          <span className="text-red-400 cursor-help" title={lang === 'en'
                            ? "Any additional monthly expenses related to property operation."
                            : "Cualquier gasto adicional mensual relacionado con la operación de la propiedad."}>ⓘ</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FormattedCurrencyInput
                            value={rentalField('rentalOtherOpex') || ''}
                            onChange={(val) => {
                              const num = parseFormattedNumber(val) || 0;
                              stageRental({ rentalOtherOpex: num });
                            }}
                            prefix="$"
                            className="w-20 px-2 py-1 text-right text-red-800 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="0"
                            data-testid="rental-other-opex-input"
                          />
                          <span className="text-xs text-red-500">/mo</span>
                        </div>
                      </div>

                      {/* Total Monthly OpEx (calculated) */}
                      <div className="flex justify-between text-sm border-t border-red-200 pt-2 font-medium">
                        <span className="text-red-700">
                          {lang === 'en' ? 'Other Monthly Costs (not in the payment)' : 'Otros Costos Mensuales (fuera del pago)'}
                        </span>
                        <span className="font-bold text-red-800" data-testid="rental-total-opex">${Math.round(liveRental.monthlyOtherOpex).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* ====== FINANCING Section ====== */}
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                      {lang === 'en' ? 'FINANCING' : 'FINANCIAMIENTO'}
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                      {/* Financing Type Toggle */}
                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-blue-700">{lang === 'en' ? 'Financing Type' : 'Tipo de Financiamiento'}</span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              stageRental({ rentalFinancingType: 'cash' });
                            }}
                            className={`px-3 py-1 rounded text-xs font-semibold transition-all ${
                              rentalField('rentalFinancingType') === 'cash'
                                ? 'bg-emerald-600 text-white'
                                : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-100'
                            }`}
                            data-testid="financing-type-cash-btn"
                          >
                            {lang === 'en' ? 'Cash' : 'Efectivo'}
                          </button>
                          <button
                            onClick={() => {
                              stageRental({ rentalFinancingType: 'dscr' });
                            }}
                            className={`px-3 py-1 rounded text-xs font-semibold transition-all ${
                              liveRental.financingType === 'dscr'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-100'
                            }`}
                            data-testid="financing-type-dscr-btn"
                          >
                            DSCR
                          </button>
                        </div>
                      </div>

                      {/* Only show financing details if DSCR */}
                      {liveRental.financingType === 'dscr' && (
                        <>
                          {/* Down Payment % - Hide for refinancing */}
                          {rentalField('rentalPurchaseType') !== 'refinancing' && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-blue-700">{lang === 'en' ? 'Down Payment' : 'Enganche'}</span>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  step="1"
                                  value={rentalField('rentalDownPaymentPct') ?? ''}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    stageRental({ rentalDownPaymentPct: val });
                                  }}
                                  className="w-16 px-2 py-1 text-right text-blue-800 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  data-testid="rental-down-payment-input"
                                />
                                <span className="text-blue-600">%</span>
                                <span className="text-xs text-blue-500">(${Math.round((analysis.purchase_price || 0) * (rentalField('rentalDownPaymentPct') || 25) / 100).toLocaleString()})</span>
                              </div>
                            </div>
                          )}

                          {/* Interest Rate */}
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-blue-700">{lang === 'en' ? 'Interest Rate' : 'Tasa de Interés'}</span>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.125"
                                value={rentalField('rentalInterestRate') ?? ''}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  stageRental({ rentalInterestRate: val });
                                }}
                                className="w-16 px-2 py-1 text-right text-blue-800 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                data-testid="rental-interest-rate-input"
                              />
                              <span className="text-blue-600">%</span>
                            </div>
                          </div>

                          {/* Loan Term */}
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-blue-700">{lang === 'en' ? 'Loan Term' : 'Plazo del Préstamo'}</span>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={rentalField('rentalLoanTermYears') ?? ''}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 30;
                                  stageRental({ rentalLoanTermYears: val });
                                }}
                                className="w-16 px-2 py-1 text-right text-blue-800 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                data-testid="rental-loan-term-input"
                              />
                              <span className="text-blue-600">{lang === 'en' ? 'years' : 'años'}</span>
                            </div>
                          </div>

                          {/* Origination Points */}
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-blue-700">{lang === 'en' ? 'Origination Points' : 'Puntos de Origen'}</span>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.25"
                                value={rentalField('rentalOriginationPoints') ?? ''}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  stageRental({ rentalOriginationPoints: val });
                                }}
                                className="w-16 px-2 py-1 text-right text-blue-800 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                data-testid="rental-points-input"
                              />
                              <span className="text-blue-600">pts</span>
                            </div>
                          </div>

                          {/* Loan Amount (derived) */}
                          <div className="flex justify-between text-sm border-t border-blue-200 pt-2">
                            <span className="text-blue-700">{lang === 'en' ? 'Loan Amount' : 'Monto del Préstamo'}</span>
                            <span className="font-semibold text-blue-800" data-testid="rental-loan-amount">${Math.round(liveRental.loanAmount).toLocaleString()}</span>
                          </div>

                          {/* TWO numbers, two purposes — labelled so they can't be confused:
                              - Debt Service (P+I) is what the cash flow and DSCR use,
                                because taxes/insurance are already in Operating Expenses.
                              - PITI is what the owner actually pays each month. */}
                          {/* THE PAYMENT — PITIA. Principal + Interest + Taxes +
                              Insurance + Association (HOA). This is what the owner
                              actually pays, and what DSCR is measured against: no lender
                              finances a property whose taxes, insurance or HOA go unpaid.
                              Broken out so it is obvious where each cost sits, and HOA is
                              always listed even at $0 so nothing looks missing. */}
                          <div className="flex justify-between text-sm border-t border-blue-200 pt-2">
                            <span className="text-blue-600">{lang === 'en' ? 'Principal & Interest' : 'Capital e Interés'}</span>
                            <span className="text-blue-800" data-testid="rental-debt-service-pi">
                              ${Math.round(liveRental.monthlyDebtService).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-600">{lang === 'en' ? 'Taxes' : 'Impuestos'}</span>
                            <span className="text-blue-800" data-testid="rental-pitia-taxes">
                              ${Math.round((rentalField('rentalAnnualTaxes') || 0) / 12).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-600">{lang === 'en' ? 'Insurance' : 'Seguro'}</span>
                            <span className="text-blue-800" data-testid="rental-pitia-insurance">
                              ${Math.round((rentalField('rentalAnnualInsurance') || 0) / 12).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-600">{lang === 'en' ? 'HOA (Association)' : 'HOA (Asociación)'}</span>
                            <span className="text-blue-800" data-testid="rental-pitia-hoa">
                              ${Math.round(rentalField('rentalHoa') || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-base font-bold border-t border-blue-300 pt-2 mt-1">
                            <span className="text-blue-800">
                              {lang === 'en' ? 'Monthly Payment (PITIA)' : 'Pago Mensual (PITIA)'}
                            </span>
                            <span className="text-blue-900" data-testid="rental-pitia">
                              ${Math.round(liveRental.monthlyPitia).toLocaleString()}
                            </span>
                          </div>

                          {/* Does the rent cover it? The whole question, answered plainly. */}
                          <div className="flex justify-between text-sm mt-2">
                            <span className="text-blue-700">{lang === 'en' ? 'Monthly Rent' : 'Renta Mensual'}</span>
                            <span className="font-semibold text-blue-800">
                              ${Math.round(rentalField('rentalMonthlyRent') || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className={`flex justify-between text-sm font-semibold rounded-lg px-2 py-1.5 ${
                            (rentalField('rentalMonthlyRent') || 0) >= liveRental.monthlyPitia
                              ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                               data-testid="rental-covers-payment">
                            <span>{lang === 'en' ? 'Rent covers the payment?' : '¿La renta cubre el pago?'}</span>
                            <span>
                              {(rentalField('rentalMonthlyRent') || 0) >= liveRental.monthlyPitia
                                ? `${lang === 'en' ? 'YES' : 'SÍ'} +$${Math.round((rentalField('rentalMonthlyRent') || 0) - liveRental.monthlyPitia).toLocaleString()}`
                                : `${lang === 'en' ? 'NO' : 'NO'} -$${Math.round(liveRental.monthlyPitia - (rentalField('rentalMonthlyRent') || 0)).toLocaleString()}`}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm font-medium mt-1">
                            <span className="text-blue-700">
                              {lang === 'en' ? 'DSCR (Rent ÷ PITIA)' : 'DSCR (Renta ÷ PITIA)'}
                            </span>
                            <span className="font-bold text-blue-800" data-testid="rental-dscr-pitia">
                              {liveRental.dscr.toFixed(2)}
                            </span>
                          </div>
                          <div className="text-[11px] text-blue-600 -mt-1">
                            {lang === 'en'
                              ? '1.00 = the rent exactly covers the full payment, taxes/insurance/HOA included'
                              : '1.00 = la renta cubre exactamente el pago completo, con impuestos/seguro/HOA'}
                          </div>
                        </>
                      )}

                      {/* CASH mode. There is no loan, but the property still costs money
                          to hold every month — and because taxes/insurance/HOA now live in
                          the payment rather than in operating expenses, hiding this block
                          in cash mode made those three costs disappear from the screen
                          entirely. They are shown here as the carrying cost instead. */}
                      {rentalField('rentalFinancingType') === 'cash' && (
                        <>
                          <div className="text-sm text-blue-600 italic text-center py-1">
                            {lang === 'en' ? 'No loan — cash purchase' : 'Sin préstamo — compra en efectivo'}
                          </div>
                          <div className="flex justify-between text-sm border-t border-blue-200 pt-2">
                            <span className="text-blue-600">{lang === 'en' ? 'Taxes' : 'Impuestos'}</span>
                            <span className="text-blue-800">
                              ${Math.round((rentalField('rentalAnnualTaxes') || 0) / 12).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-600">{lang === 'en' ? 'Insurance' : 'Seguro'}</span>
                            <span className="text-blue-800">
                              ${Math.round((rentalField('rentalAnnualInsurance') || 0) / 12).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-600">{lang === 'en' ? 'HOA (Association)' : 'HOA (Asociación)'}</span>
                            <span className="text-blue-800">
                              ${Math.round(rentalField('rentalHoa') || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-base font-bold border-t border-blue-300 pt-2 mt-1">
                            <span className="text-blue-800">
                              {lang === 'en' ? 'Monthly Carrying Cost' : 'Costo Mensual de Mantener'}
                            </span>
                            <span className="text-blue-900" data-testid="rental-carrying-cost">
                              ${Math.round(liveRental.monthlyPitia).toLocaleString()}
                            </span>
                          </div>
                          <div className="text-[11px] text-blue-600 -mt-1">
                            {lang === 'en'
                              ? 'Taxes + insurance + HOA. No loan payment, but not free to hold.'
                              : 'Impuestos + seguro + HOA. Sin pago de préstamo, pero no es gratis mantenerla.'}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ====== CLOSING COSTS Section (BUG 4) ======
                      These are the fees the user entered BEFORE running the analysis
                      (PropertyConfirmation). They were already being calculated into
                      cash-invested and cash-on-cash, but the analysis page never showed
                      them — so the figures looked wrong and there was nothing to correct.
                      Surfaced here, seeded from what was entered, and editable. */}
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">
                      {lang === 'en' ? 'CLOSING COSTS' : 'COSTOS DE CIERRE'}
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-3 space-y-2">
                      {/* Base closing costs % */}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-indigo-700">{lang === 'en' ? 'Base Closing Costs' : 'Costos de Cierre Base'}</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.25"
                            value={rentalField('rentalClosingCostsPct') ?? ''}
                            onChange={(e) => stageRental({ rentalClosingCostsPct: parseFloat(e.target.value) || 0 })}
                            className="w-16 px-2 py-1 text-right text-indigo-800 border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            data-testid="rental-closing-costs-pct-input"
                          />
                          <span className="text-indigo-600">%</span>
                          <span className="text-xs text-indigo-500">
                            (${Math.round(liveRental.closingBreakdown.base_closing_costs).toLocaleString()})
                          </span>
                        </div>
                      </div>

                      {/* Origination points — a LOAN fee: meaningless on a cash purchase */}
                      {liveRental.financingType === 'dscr' && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-indigo-700">{lang === 'en' ? 'Origination Points' : 'Puntos de Originación'}</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step="0.25"
                              value={rentalField('rentalOriginationPoints') ?? ''}
                              onChange={(e) => stageRental({ rentalOriginationPoints: parseFloat(e.target.value) || 0 })}
                              className="w-16 px-2 py-1 text-right text-indigo-800 border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              data-testid="rental-origination-points-input"
                            />
                            <span className="text-indigo-600">pts</span>
                            <span className="text-xs text-indigo-500">
                              (${Math.round(liveRental.closingBreakdown.origination_points).toLocaleString()})
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Lender / underwriting fees — also loan-only */}
                      {liveRental.financingType === 'dscr' && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-indigo-700">{lang === 'en' ? 'Lender / Underwriting Fees' : 'Comisiones del Prestamista'}</span>
                          <FormattedCurrencyInput
                            value={rentalField('rentalLenderFees') || ''}
                            onChange={(val) => stageRental({ rentalLenderFees: parseFormattedNumber(val) || 0 })}
                            prefix="$"
                            className="w-24 px-2 py-1 text-right text-indigo-800 border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="0"
                            data-testid="rental-lender-fees-input"
                          />
                        </div>
                      )}

                      {/* Title / attorney / settlement — applies to CASH purchases too */}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-indigo-700">{lang === 'en' ? 'Title / Attorney' : 'Título / Abogado'}</span>
                        <FormattedCurrencyInput
                          value={rentalField('rentalTitleAttorneyFees') || ''}
                          onChange={(val) => stageRental({ rentalTitleAttorneyFees: parseFormattedNumber(val) || 0 })}
                          prefix="$"
                          className="w-24 px-2 py-1 text-right text-indigo-800 border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="0"
                          data-testid="rental-title-attorney-input"
                        />
                      </div>

                      {/* Escrow / reserves */}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-indigo-700">{lang === 'en' ? 'Escrow / Reserves' : 'Depósito / Reservas'}</span>
                        <FormattedCurrencyInput
                          value={rentalField('rentalEscrowReserves') || ''}
                          onChange={(val) => stageRental({ rentalEscrowReserves: parseFormattedNumber(val) || 0 })}
                          prefix="$"
                          className="w-24 px-2 py-1 text-right text-indigo-800 border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="0"
                          data-testid="rental-escrow-reserves-input"
                        />
                      </div>

                      {/* Processing / other */}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-indigo-700">{lang === 'en' ? 'Processing / Other' : 'Procesamiento / Otros'}</span>
                        <FormattedCurrencyInput
                          value={rentalField('rentalOtherClosingCosts') || ''}
                          onChange={(val) => stageRental({ rentalOtherClosingCosts: parseFormattedNumber(val) || 0 })}
                          prefix="$"
                          className="w-24 px-2 py-1 text-right text-indigo-800 border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="0"
                          data-testid="rental-other-closing-input"
                        />
                      </div>

                      {/* Total — the SAME liveRental.closingCosts the Closing Costs
                          Breakdown panel below totals, so the two cannot disagree. */}
                      <div className="flex justify-between text-sm border-t border-indigo-200 pt-2 font-medium">
                        <span className="text-indigo-700">{lang === 'en' ? 'Total Closing Costs' : 'Total Costos de Cierre'}</span>
                        <span className="font-bold text-indigo-800" data-testid="rental-total-closing-costs">
                          ${Math.round(liveRental.closingCosts).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ====== RESULTS Section (read-only outputs) ====== */}
                  <div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
                      {lang === 'en' ? 'RESULTS' : 'RESULTADOS'}
                    </div>
                    <div className="bg-slate-100 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{lang === 'en' ? 'Net Operating Income (NOI)' : 'Ingreso Operativo Neto'}</span>
                        <span className="font-semibold text-slate-800" data-testid="rental-noi">${Math.round(liveRental.noi).toLocaleString()}/yr</span>
                      </div>
                      <div className={`flex justify-between text-sm border-t border-slate-300 pt-2 ${
                        liveRental.monthlyCashFlow >= 0 ? 'bg-green-100' : 'bg-red-100'
                      } -mx-3 px-3 py-2 rounded-b-lg`}>
                        <span className={`font-semibold ${liveRental.monthlyCashFlow >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                          {lang === 'en' ? 'Monthly Cash Flow' : 'Flujo de Caja Mensual'}
                        </span>
                        <span className={`font-bold text-lg ${liveRental.monthlyCashFlow >= 0 ? 'text-green-800' : 'text-red-800'}`}
                              data-testid="rental-monthly-cash-flow">
                          ${Math.round(liveRental.monthlyCashFlow).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-slate-600">{lang === 'en' ? 'Annual Cash Flow' : 'Flujo de Caja Anual'}</span>
                        <span className={`font-semibold ${liveRental.annualCashFlow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          ${Math.round(liveRental.annualCashFlow).toLocaleString()}/yr
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-300 mt-2">
                        {liveRental.financingType === 'dscr' && (
                          <div className="text-center">
                            <div className="text-xs text-slate-500">DSCR</div>
                            <div className={`font-bold ${
                              liveRental.dscr >= 1.25 ? 'text-green-700' :
                              liveRental.dscr >= 1.0 ? 'text-amber-600' : 'text-red-600'
                            }`} data-testid="rental-dscr-live">{liveRental.dscr.toFixed(2)}</div>
                          </div>
                        )}
                        <div className="text-center">
                          <div className="text-xs text-slate-500">{lang === 'en' ? 'Cap Rate' : 'Cap Rate'}</div>
                          <div className="font-bold text-blue-700" data-testid="rental-cap-rate-live">{liveRental.capRate.toFixed(2)}%</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-slate-500">{lang === 'en' ? 'Cash-on-Cash' : 'Cash-on-Cash'}</div>
                          {isRefinance ? (
                            <div className="font-bold text-slate-400" data-testid="rental-coc-na">
                              N/A
                              <div className="text-[10px] font-normal text-slate-500 leading-tight">
                                {lang === 'en' ? 'no cash invested' : 'sin efectivo invertido'}
                              </div>
                            </div>
                          ) : (
                            <div className="font-bold text-purple-700">{liveRental.cashOnCash.toFixed(2)}%</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cash summary. A REFINANCE is not an acquisition: the owner already holds
                      the property, invests nothing, and RECEIVES the loan proceeds net of the
                      old payoff and refinance costs. Showing "Total Cash Invested" here framed
                      money coming OUT as money going IN. */}
                  {isRefinance ? (
                    <div className="mt-3 pt-3 border-t border-slate-200" data-testid="refi-cash-summary">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">
                          {lang === 'en' ? 'Cash Out Received' : 'Efectivo Recibido'}
                        </span>
                        <span className="font-bold text-green-700" data-testid="refi-cash-out">
                          ${formatWholeDollars(liveRental.refiCashOut)}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        {lang === 'en'
                          ? 'New loan less the existing payoff and refinance costs. You already own this property — there is no down payment and no cash invested.'
                          : 'Nuevo préstamo menos el saldo existente y los costos de refinanciamiento. Ya eres dueño — no hay enganche ni efectivo invertido.'}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
                      <span className="text-sm text-slate-600">{lang === 'en' ? 'Total Cash Invested' : 'Total Efectivo Invertido'}</span>
                      <span className="font-bold text-slate-800" data-testid="rental-total-cash-invested">${formatWholeDollars(liveRental.cashInvested)}</span>
                    </div>
                  )}

                  {/* ====== APPLY CHANGES (BUG 3) ======
                      Rental edits above are STAGED. Nothing recalculates until this is
                      clicked — same contract as the Fix & Flip assumptions panel. The
                      results above therefore always show a consistent, fully-recalculated
                      state rather than churning field-by-field as you type. */}
                  <div className="mt-4 pt-4 border-t border-slate-200" data-testid="rental-recalc-bar">
                    {hasRentalDraft && (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                        {lang === 'en'
                          ? 'Totals above update as you type. Apply to re-run the full analysis (property value and comparables).'
                          : 'Los totales de arriba se actualizan mientras escribes. Aplica para volver a ejecutar el análisis completo (valor y comparables).'}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={applyRentalDraft}
                        disabled={recalculating || !hasRentalDraft}
                        className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        data-testid="rental-apply-changes"
                      >
                        {recalculating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        {lang === 'en' ? 'Apply Changes & Recalculate' : 'Aplicar Cambios y Recalcular'}
                      </button>
                      {hasRentalDraft && (
                        <button
                          onClick={discardRentalDraft}
                          disabled={recalculating}
                          className="flex items-center gap-2 px-4 py-2 border border-border text-muted-foreground rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 text-sm"
                          data-testid="rental-discard-changes"
                        >
                          <RotateCcw className="w-4 h-4" />
                          {lang === 'en' ? 'Discard Changes' : 'Descartar Cambios'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* EXPLAIN THIS DEAL - Simple Bullets */}
                {analysis.rental_classification?.factors && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-4" data-testid="rental-explain-deal">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">
                      {lang === 'en' ? 'Why This Deal?' : '¿Por Qué Este Trato?'}
                    </h4>
                    <ul className="space-y-2">
                      {analysis.rental_classification.factors.slice(0, 4).map((factor, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          {factor.passed ? (
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : factor.within_tolerance ? (
                            <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={factor.passed ? 'text-slate-700' : factor.within_tolerance ? 'text-blue-700' : 'text-red-700'}>
                            {factor.passed 
                              ? (lang === 'en' 
                                  ? `${factor.label} looks good at ${factor.value}`
                                  : `${factor.label} se ve bien en ${factor.value}`)
                              : factor.within_tolerance
                                ? (lang === 'en'
                                    ? `${factor.label} is close at ${factor.value}`
                                    : `${factor.label} está cerca en ${factor.value}`)
                                : (lang === 'en'
                                    ? `${factor.label} misses target at ${factor.value}`
                                    : `${factor.label} no alcanza objetivo en ${factor.value}`)
                            }
                          </span>
                        </li>
                      ))}
                    </ul>
                    {analysis.rental_classification.user_criteria?.tolerance_mode && (
                      <div className="mt-3 pt-2 border-t border-slate-200 text-xs text-slate-500">
                        {lang === 'en' 
                          ? `Using ${analysis.rental_classification.user_criteria.tolerance_mode} tolerance` 
                          : `Usando tolerancia ${analysis.rental_classification.user_criteria.tolerance_mode}`}
                      </div>
                    )}
                  </div>
                )}

                {/* DSCR Ineligibility Warning */}
                {analysis.rental_analysis?.condition_warning && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{analysis.rental_analysis.condition_warning}</p>
                  </div>
                )}

                {/* Collapsible Details - Enhanced Visibility */}
                <details className="group mt-4">
                  <summary className="flex items-center justify-between cursor-pointer py-3 px-4 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
                    <span className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      {lang === 'en' ? 'See Full Details' : 'Ver Detalles Completos'}
                    </span>
                    <ChevronDown className="w-5 h-5 transition-transform group-open:rotate-180" />
                  </summary>
                  
                  <div className="pt-4 space-y-4">
                    {/* Additional Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="text-xs text-blue-600 mb-1">{lang === 'en' ? 'Cap Rate' : 'Cap Rate'}</div>
                        <div className="text-lg font-bold text-blue-800">{(liveRental.capRate).toFixed(2)}%</div>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="text-xs text-purple-600 mb-1">{lang === 'en' ? 'Cash-on-Cash' : 'Cash-on-Cash'}</div>
                        <div className="text-lg font-bold text-purple-800">{(liveRental.cashOnCash).toFixed(2)}%</div>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                        <div className="text-xs text-emerald-600 mb-1">{lang === 'en' ? 'Monthly Rent' : 'Renta Mensual'}</div>
                        <div className="text-lg font-bold text-emerald-800">${formatWholeDollars(analysis.rental_analysis?.monthly_rent || 0)}</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs text-slate-600 mb-1">{lang === 'en' ? 'Annual NOI' : 'NOI Anual'}</div>
                        <div className="text-lg font-bold text-slate-800">${Math.round(liveRental.noi).toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Income & Expenses */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                        <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          {lang === 'en' ? 'Income' : 'Ingresos'}
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-green-700">{lang === 'en' ? 'Monthly Rent' : 'Renta Mensual'}</span>
                            <span className="font-semibold text-green-800">${formatWholeDollars(analysis.rental_analysis?.monthly_rent || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-700">{lang === 'en' ? 'Annual Rent' : 'Renta Anual'}</span>
                            <span className="font-semibold text-green-800">${formatWholeDollars(analysis.rental_analysis?.annual_rent || 0)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                        <h4 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
                          <TrendingDown className="w-4 h-4" />
                          {lang === 'en' ? 'Monthly Expenses' : 'Gastos Mensuales'}
                        </h4>
                        <div className="space-y-2 text-sm">
                          {analysis.rental_analysis?.monthly_costs_breakdown && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-red-700">{lang === 'en' ? 'Taxes' : 'Impuestos'}</span>
                                <span className="text-red-800">${Math.round(analysis.rental_analysis.monthly_costs_breakdown.property_taxes || 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-red-700">{lang === 'en' ? 'Insurance' : 'Seguro'}</span>
                                <span className="text-red-800">${Math.round(analysis.rental_analysis.monthly_costs_breakdown.insurance || 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-red-700">{lang === 'en' ? 'Maintenance' : 'Mantenimiento'}</span>
                                <span className="text-red-800">${Math.round(analysis.rental_analysis.monthly_costs_breakdown.maintenance_reserve || 0).toLocaleString()}</span>
                              </div>
                            </>
                          )}
                          {liveRental.financingType === 'dscr' && (
                            <div className="flex justify-between pt-2 border-t border-red-200">
                              <span className="text-red-700 font-medium">{lang === 'en' ? 'Debt Service' : 'Servicio de Deuda'}</span>
                              <span className="font-semibold text-red-800">${Math.round(liveRental.monthlyDebtService).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Financing Summary */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">
                        {lang === 'en' ? 'Financing Summary' : 'Resumen de Financiamiento'}
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-slate-500">{lang === 'en' ? 'Type' : 'Tipo'}</div>
                          {/* Read the LIVE financing type, not the stored one. These
                              disagreed whenever the condition rule forced a DSCR request
                              to cash: the numbers on this screen were financed while this
                              label said "Cash". And when it IS forced, say so — the user
                              chose financing; we overrode it. */}
                          <div className="font-semibold text-slate-800" data-testid="rental-financing-type">
                            {liveRental.financingType === 'dscr'
                              ? 'DSCR Loan'
                              : (lang === 'en' ? 'Cash' : 'Efectivo')}
                          </div>
                          {liveRental.financingForcedToCash && (
                            <div className="text-xs text-amber-700 mt-1" data-testid="financing-forced-note">
                              {lang === 'en'
                                ? 'Condition is not DSCR-eligible — analyzed as cash.'
                                : 'La condición no califica para DSCR — analizado como efectivo.'}
                            </div>
                          )}
                        </div>
                        {liveRental.financingType === 'dscr' && (
                          <>
                            {analysis.rental_analysis?.rental_purchase_type !== 'refinancing' && (
                              <div>
                                <div className="text-slate-500">{lang === 'en' ? 'Down Payment' : 'Enganche'}</div>
                                <div className="font-semibold text-slate-800">
                                  {analysis.rental_analysis?.down_payment_pct}%
                                </div>
                              </div>
                            )}
                            {analysis.rental_analysis?.rental_purchase_type === 'refinancing' && (
                              <div>
                                <div className="text-slate-500">{lang === 'en' ? 'LTV' : 'LTV'}</div>
                                <div className="font-semibold text-slate-800">
                                  {analysis.rental_analysis?.refi_ltv || 75}%
                                </div>
                              </div>
                            )}
                            <div>
                              <div className="text-slate-500">{lang === 'en' ? 'Loan Amount' : 'Préstamo'}</div>
                              <div className="font-semibold text-slate-800">
                                ${formatWholeDollars(analysis.rental_analysis?.loan_amount || 0)}
                              </div>
                            </div>
                            {analysis.rental_analysis?.rental_purchase_type === 'refinancing' && (
                              <div>
                                <div className="text-slate-500">{lang === 'en' ? 'Cash Out' : 'Cash Out'}</div>
                                {/* GAP #2: refi cash-out = refi_ltv × ARV; unreliable when ARV is blocked. */}
                                {arvBlocked ? (
                                  <div className="font-semibold text-amber-700 text-xs">
                                    {lang === 'en' ? 'Needs reliable ARV' : 'Necesita ARV confiable'}
                                  </div>
                                ) : (
                                  <div className="font-semibold text-green-600">
                                    ${formatWholeDollars(liveRental.refiCashOut)}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        <div>
                          <div className="text-slate-500 flex items-center gap-1">
                            {lang === 'en' ? 'Cash Required' : 'Efectivo Requerido'}
                            <span className="cursor-help" title={lang === 'en' 
                              ? (isRefinance ? 'Cash received at closing: new loan less payoff and refinance costs' : 'Down Payment + Closing Costs = Your out-of-pocket investment') 
                              : 'Enganche + Costos de Cierre = Tu inversión de bolsillo'}>
                              <HelpCircle className="w-3 h-3 text-slate-400" />
                            </span>
                          </div>
                          <div className="font-semibold text-slate-800">
                            ${formatWholeDollars(liveRental.cashInvested)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* COMPLETE FINANCIAL MODEL - Closing Costs Breakdown
                        ONE SOURCE. This panel used to read the stored backend breakdown
                        while the CLOSING COSTS block above read liveRental — two engines
                        for one number, so the same financed deal showed $16,816 here and
                        $21,754 there. Both now read liveRental.closingBreakdown, and the
                        total is the SUM of the lines shown rather than a separately
                        stored figure that could disagree with them. */}
                    {liveRental.closingCosts > 0 && (
                      <div className="p-4 bg-amber-50 rounded-xl border border-amber-200" data-testid="rental-closing-costs-breakdown">
                        <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          {lang === 'en' ? 'Closing Costs Breakdown' : 'Desglose de Costos de Cierre'}
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {[
                            ['base_closing_costs', 'Base Closing Costs', 'Costos Base'],
                            ['title_attorney_fees', 'Title/Attorney', 'Título/Abogado'],
                            ['origination_points', 'Loan Points', 'Puntos del Préstamo'],
                            ['lender_fees', 'Lender Fees', 'Tarifas Prestamista'],
                            ['escrow_reserves', 'Escrow/Reserves', 'Reservas/Escrow'],
                            ['other_closing_costs', 'Other Costs', 'Otros Costos'],
                            ['seller_agent_commission', 'Seller Commission', 'Comisión Vendedor'],
                          ].map(([key, en, es]) => (
                            (liveRental.closingBreakdown[key] || 0) > 0 && (
                              <div className="flex justify-between" key={key}>
                                <span className="text-amber-700">{lang === 'en' ? en : es}</span>
                                <span className="font-semibold text-amber-800" data-testid={`closing-line-${key}`}>
                                  ${Math.round(liveRental.closingBreakdown[key]).toLocaleString()}
                                </span>
                              </div>
                            )
                          ))}
                        </div>
                        <div className="mt-3 pt-2 border-t border-amber-300 flex justify-between">
                          <span className="font-semibold text-amber-800">{lang === 'en' ? 'Total Closing Costs' : 'Total Costos de Cierre'}</span>
                          <span className="font-bold text-amber-900" data-testid="closing-total-b">${Math.round(liveRental.closingCosts).toLocaleString()}</span>
                        </div>

                        {/* PRIMARY METRIC FOR DSCR: Cash Required to Close */}
                        {liveRental.financingType === 'dscr' ? (
                          <div className="mt-2 pt-2 border-t border-green-300 flex flex-col bg-green-100 -mx-4 -mb-4 px-4 py-3 rounded-b-xl">
                            <div className="flex justify-between items-center">
                              <span className="font-semibold text-green-900 flex items-center gap-1">
                                {lang === 'en' ? 'Cash Required to Close' : 'Efectivo Necesario para Cerrar'}
                                <span className="cursor-help" title={lang === 'en' 
                                  ? (isRefinance ? 'Cash received at closing: new loan less payoff and refinance costs' : 'Down Payment + Closing Costs = Your actual out-of-pocket investment') 
                                  : 'Enganche + Costos de Cierre = Tu inversión real de bolsillo'}>
                                  <HelpCircle className="w-3.5 h-3.5 text-green-600" />
                                </span>
                              </span>
                              <span className="font-bold text-green-900 text-lg">${Math.round(liveRental.cashInvested).toLocaleString()}</span>
                            </div>
                            <div className="text-xs text-green-700 mt-1">
                              = ${Math.round(liveRental.downPaymentAmount).toLocaleString()} {lang === 'en' ? '(down payment)' : '(enganche)'} + ${Math.round(liveRental.closingCosts).toLocaleString()} {lang === 'en' ? '(closing)' : '(cierre)'}
                            </div>
                          </div>
                        ) : (
                          /* Cash Purchase: Show Total Cash Required */
                          <div className="mt-2 pt-2 border-t border-amber-300 flex flex-col bg-amber-100 -mx-4 -mb-4 px-4 py-3 rounded-b-xl">
                            <div className="flex justify-between items-center">
                              <span className="font-semibold text-amber-900 flex items-center gap-1">
                                {lang === 'en' ? 'Total Cash Required' : 'Efectivo Total Requerido'}
                                <span className="cursor-help" title={lang === 'en' 
                                  ? 'Purchase Price + Closing Costs (all-cash purchase)' 
                                  : 'Precio de Compra + Costos de Cierre (compra en efectivo)'}>
                                  <HelpCircle className="w-3.5 h-3.5 text-amber-600" />
                                </span>
                              </span>
                              <span className="font-bold text-amber-900 text-lg">${Math.round(effectivePurchasePrice + liveRental.closingCosts).toLocaleString()}</span>
                            </div>
                            <div className="text-xs text-amber-700 mt-1">
                              = ${Math.round(effectivePurchasePrice).toLocaleString()} + ${Math.round(liveRental.closingCosts).toLocaleString()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Full Factor Breakdown - Optional */}
                    {analysis.rental_classification?.factors && analysis.rental_classification.factors.length > 0 && (
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">
                          {lang === 'en' ? 'Criteria Breakdown' : 'Desglose de Criterios'}
                        </h4>
                        <div className="space-y-2">
                          {analysis.rental_classification.factors.map((factor, idx) => (
                            <div 
                              key={idx} 
                              className={`flex items-center justify-between p-2 rounded-lg ${
                                factor.passed 
                                  ? 'bg-green-50 border border-green-100' 
                                  : factor.within_tolerance
                                    ? 'bg-blue-50 border border-blue-100'
                                    : 'bg-red-50 border border-red-100'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {factor.passed ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : factor.within_tolerance ? (
                                  <AlertCircle className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-600" />
                                )}
                                <span className="text-sm font-medium text-slate-700">{factor.label}</span>
                              </div>
                              <div className="text-right">
                                <div className={`text-sm font-semibold ${
                                  factor.passed ? 'text-green-700' : 
                                  factor.within_tolerance ? 'text-blue-700' : 'text-red-700'
                                }`}>
                                  {factor.value}
                                </div>
                                {factor.target !== 'Not set' && (
                                  <div className="text-xs text-slate-500">
                                    {lang === 'en' ? 'Target: ' : 'Objetivo: '}{factor.target}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              </>
            )}
            
            {/* BRRRR Analysis - Keep existing basic metrics but add note about future expansion */}
            {analysis.strategy === 'brrrr' && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="text-xs text-green-700 mb-1">{lang === 'en' ? 'Monthly Rent' : 'Renta Mensual'}</div>
                    <div className="text-lg font-bold text-green-800">${formatWholeDollars(analysis.rental_analysis?.monthly_rent)}</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="text-xs text-green-700 mb-1">{lang === 'en' ? 'Monthly Cashflow' : 'Flujo Mensual'}</div>
                    <div className={`text-lg font-bold ${financialTextColor(liveRental.monthlyCashFlow)}`}>
                      ${formatWholeDollars(liveRental.monthlyCashFlow)}
                    </div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="text-xs text-green-700 mb-1">{lang === 'en' ? 'Cap Rate' : 'Tasa de Cap.'}</div>
                    <div className="text-lg font-bold text-green-800">{liveRental.capRate?.toFixed(2)}%</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="text-xs text-green-700 mb-1">{lang === 'en' ? 'Annual Income' : 'Ingreso Anual'}</div>
                    <div className="text-lg font-bold text-green-800">${formatWholeDollars(analysis.rental_analysis?.annual_rent)}</div>
                  </div>
                </div>
            
                {/* BRRRR Specific Metrics */}
                <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <h3 className="text-sm font-semibold text-purple-800 mb-2">
                    {lang === 'en' ? 'BRRRR Refinance Potential' : 'Potencial de Refinanciamiento BRRRR'}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-purple-600">{lang === 'en' ? 'Post-Repair Value' : 'Valor Post-Reparación'}</div>
                      <div className="text-lg font-bold text-purple-800">${formatWholeDollars(analysis.arv)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-purple-600">{lang === 'en' ? '75% LTV Refi' : 'Refi 75% LTV'}</div>
                      <div className="text-lg font-bold text-purple-800">${Math.round((analysis.arv || 0) * 0.75).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-purple-600">{lang === 'en' ? 'Cash Left in Deal' : 'Efectivo en Trato'}</div>
                      <div className={`text-lg font-bold ${((analysis.purchase_price || 0) + (analysis.rehab_estimate || 0) - (analysis.arv || 0) * 0.75) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ${Math.round((analysis.purchase_price || 0) + (analysis.rehab_estimate || 0) - (analysis.arv || 0) * 0.75).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* NOTE: Target Offer vs Asking Price section MOVED to appear after Deal Decision card */}

        {/* Investment Criteria & Inputs Section - Controls placed BELOW Core Numbers */}
        {/* Investment Criteria Section - Show strategy-appropriate criteria only */}
        {!isValuationMode && criteriaLoaded && investmentCriteria && (
          <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-xl" data-testid="criteria-used-section">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-600" />
                {lang === 'en' ? 'Investment Criteria (Your Inputs)' : 'Criterios de Inversión (Tus Entradas)'}
              </h4>
              <button
                onClick={() => {
                  // Preserve the FULL in-progress deal (rehab/price/financing edits) so the
                  // criteria round-trip returns here exactly as left — NOT reset to the
                  // original analysis. Mirrors the Compare-return pattern (CASE 4/0 in
                  // getInitialAnalysis). New criteria are re-read + re-evaluated on return.
                  const stateToSave = currentAnalysisState || analysis;
                  if (stateToSave) {
                    try {
                      sessionStorage.setItem('chekdeal_criteria_return_state', JSON.stringify(stateToSave));
                      sessionStorage.setItem('chekdeal_criteria_return_id', String(stateToSave.analysis_id || stateToSave.id || urlAnalysisId || 'unsaved'));
                    } catch (e) {
                      console.warn('Failed to save criteria return state:', e);
                    }
                  }
                  navigate('/criteria');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                data-testid="edit-criteria-btn"
              >
                <Edit3 className="w-3.5 h-3.5" />
                {lang === 'en' ? 'Edit My Criteria' : 'Editar Mis Criterios'}
              </button>
            </div>
            
            {/* FLIP CRITERIA - Only show for flip strategy */}
            {analysis.strategy === 'flip' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div className="bg-white p-2 rounded-lg border border-slate-200">
                  <div className="text-slate-500">{lang === 'en' ? 'Min Profit' : 'Gan. Mín'}</div>
                  <div className="font-semibold text-slate-700">${formatWholeDollars(investmentCriteria.flip?.min_net_profit)}</div>
                </div>
                <div className="bg-white p-2 rounded-lg border border-slate-200">
                  <div className="text-slate-500">{lang === 'en' ? 'Min Margin' : 'Margen Mín'}</div>
                  <div className="font-semibold text-slate-700">{investmentCriteria.flip?.min_profit_margin}%</div>
                </div>
                <div className="bg-white p-2 rounded-lg border border-slate-200">
                  <div className="text-slate-500">{lang === 'en' ? 'Min ROI' : 'ROI Mín'}</div>
                  <div className="font-semibold text-slate-700">{investmentCriteria.flip?.min_roi}%</div>
                </div>
              </div>
            )}
            
            {/* RENTAL CRITERIA - Only show for rental strategy */}
            {analysis.strategy === 'rental' && analysis.deal_decision?.user_criteria && (
              /* These tiles are the ONLY place the page states which thresholds the deal
                 was judged against, so they carry test ids — a verdict is not checkable
                 without them. `??` not `||`: a saved threshold of 0 (accept any
                 non-negative cash flow, no minimum cap rate) is a real setting and must
                 not be redrawn as the default. */
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid="rental-criteria-tiles">
                <div className="bg-white p-2 rounded-lg border border-slate-200">
                  <div className="text-slate-500">{lang === 'en' ? 'Min Cash Flow' : 'Flujo Mín'}</div>
                  <div className="font-semibold text-slate-700" data-testid="criterion-min-cash-flow">
                    ${formatWholeDollars(analysis.deal_decision.user_criteria.min_monthly_cash_flow)}/mo
                  </div>
                </div>
                {liveRental.financingType === 'dscr' && (
                  <div className="bg-white p-2 rounded-lg border border-slate-200">
                    <div className="text-slate-500">{lang === 'en' ? 'Min DSCR' : 'DSCR Mín'}</div>
                    <div className="font-semibold text-slate-700" data-testid="criterion-min-dscr">
                      {(analysis.deal_decision.user_criteria.min_dscr ?? 1.25).toFixed(2)}
                    </div>
                  </div>
                )}
                <div className="bg-white p-2 rounded-lg border border-slate-200">
                  <div className="text-slate-500">{lang === 'en' ? 'Min Cap Rate' : 'Cap Rate Mín'}</div>
                  <div className="font-semibold text-slate-700" data-testid="criterion-min-cap-rate">
                    {analysis.deal_decision.user_criteria.min_cap_rate ?? 6}%
                  </div>
                </div>
                <div className="bg-white p-2 rounded-lg border border-slate-200">
                  <div className="text-slate-500">{lang === 'en' ? 'Max % Market' : 'Máx % Mercado'}</div>
                  <div className="font-semibold text-slate-700" data-testid="criterion-max-price-pct">
                    {analysis.deal_decision.user_criteria.max_price_vs_market_pct ?? 100}%
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Comparable Sales Used for Estimated Value */}
        {analysis.sales_comps && analysis.sales_comps.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
                  <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                  {lang === 'en' ? 'Comparable Sales' : 'Ventas Comparables'}
                  <div className="relative group">
                    <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                    <div className="absolute z-50 left-0 bottom-full mb-2 w-72 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      {lang === 'en' 
                        ? 'Comparable properties directly affect your estimated value and deal results. Custom comparables will update your analysis.'
                        : 'Las propiedades comparables afectan directamente tu valor estimado y resultados del trato. Los comparables personalizados actualizarán tu análisis.'}
                    </div>
                  </div>
                </h2>
                <p className="text-sm text-muted-foreground mt-1" data-testid="comps-used-line">
                  {(() => {
                    // The count of comps the ARV actually used — or, when there is none, the
                    // fact that there is none.
                    //
                    // This read `Math.min(analysis.sales_comps.length, 5)` when comps_used was
                    // empty, so a property whose ARV came from the market floor with ZERO
                    // usable comps announced "5 comparable sales used to estimate this value".
                    // The 5 was a cap, not a count: it appeared for any pool of five or more.
                    // On 5060 Tireman it sat beside "0 Sold Comps" and an empty table.
                    const n = (analysis.comps_used && analysis.comps_used.length) || 0;
                    const esPiso = String(analysis.arv_details?.arv_warning_key || '')
                      .startsWith('floor_anchored');
                    if (esPiso || n === 0) {
                      // Naming the method beats naming a number nobody can check. A floor
                      // estimate moves with SQUARE FOOTAGE ALONE — the value is
                      // floor $/sqft x sqft — so a user editing type, lot or condition sees
                      // nothing change and concludes the app ignored them. It did not; the
                      // edit genuinely cannot reach a figure derived this way.
                      return lang === 'en'
                        ? 'No comparable sales were usable — this value comes from the market floor. Only square footage changes it; adding your own comparables is what makes it comp-based.'
                        : 'Ninguna venta comparable resultó utilizable — este valor sale del piso de mercado. Solo los pies cuadrados lo cambian; agregar tus propios comparables es lo que lo vuelve basado en comps.';
                    }
                    return lang === 'en'
                      ? `${n} comparable ${n === 1 ? 'sale' : 'sales'} used to estimate this value`
                      : `${n} ${n === 1 ? 'venta comparable usada' : 'ventas comparables usadas'} para estimar este valor`;
                  })()}
                </p>
              </div>
              {/* Estimated Value removed from here - shown only in Core Numbers cards to avoid duplication */}
            </div>

            {/* Comp Dispersion Warning Banner - shown when price/sqft varies widely */}
            {analysis.arv_details?.comp_dispersion?.level === 'high' && (
              <div 
                className="rounded-xl border-2 p-4 bg-amber-50 border-amber-200 mb-6"
                data-testid="comp-dispersion-warning"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-800">
                      {lang === 'en' ? 'Comparable Prices Vary Widely' : 'Precios Comparables Muy Variables'}
                    </h3>
                    <p className="text-sm mt-1 text-amber-700">
                      {lang === 'en' 
                        ? `These comps range from $${Math.round(analysis.arv_details.comp_dispersion.ppsf_min)} to $${Math.round(analysis.arv_details.comp_dispersion.ppsf_max)}/sqft. When comparable prices vary this much, the automated estimate is less reliable. Add your own local comps below to improve accuracy.`
                        : `Estos comparables van de $${Math.round(analysis.arv_details.comp_dispersion.ppsf_min)} a $${Math.round(analysis.arv_details.comp_dispersion.ppsf_max)}/sqft. Cuando los precios varían tanto, el valor automático es menos confiable. Agrega tus propios comparables locales abajo para mejorar la exactitud.`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Honesty safeguards: verify-property-type (last sale << ARV) and single
                distant-comp low support. Both cap ARV confidence to LOW upstream; here we
                surface WHY, so an inflated number is never presented as trustworthy. */}
            {/* BUG 2: the "this ARV may be inflated" banner is GONE. It fired purely on a
                last-sale/ARV price ratio and knew nothing about comp quality — 1754 Crow
                Lane (3 tight comps at $349,900/$339,900/$360,000) was told its own ARV
                might be inflated. CHEKSTE publishes a VALUATION; undermining it destroys
                trust, and it reads as "we were wrong" when our conservative number later
                comes in under a real appraisal. The type nudge below is NEUTRAL and only
                appears when type DETECTION is genuinely uncertain. */}
            {(noMultifamilyComps ||
              needsTypeVerification ||
              analysis.arv_details?.low_comp_support_flag?.triggered ||
              noRenovatedComps) && (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="arv-honesty-banner">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-900">
                    {noMultifamilyComps
                      ? (lang === 'en'
                          ? '⚠️ Valued with single-family comps — no multifamily sales nearby'
                          : '⚠️ Valuado con comps unifamiliares — no hay ventas multifamiliares cerca')
                      : noRenovatedComps
                      ? (lang === 'en'
                          ? '⚠️ Limited renovated comps — add yours for an accurate ARV'
                          : '⚠️ Comps renovados limitados — agregue los suyos para un ARV preciso')
                      : needsTypeVerification
                      ? (lang === 'en'
                          ? 'Confirm the property type for greater accuracy'
                          : 'Confirma el tipo de propiedad para mayor precisión')
                      : (lang === 'en'
                          ? '⚠️ Low-confidence ARV — insufficient comparables'
                          : '⚠️ ARV de baja confianza — comparables insuficientes')}
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    {noMultifamilyComps
                      ? (lang === 'en'
                          ? 'No multifamily sales were found near this property, so the value comes from single-family comparables. Add your own sold comps for a reliable ARV.'
                          : 'No se encontraron ventas multifamiliares cerca de esta propiedad, así que el valor proviene de comparables unifamiliares. Agrega tus propios comps vendidos para un ARV confiable.')
                      : noRenovatedComps
                      ? noRenoMessage
                      : needsTypeVerification
                        ? (lang === 'en'
                            ? 'We could not confirm the property type with high certainty. Confirming it sharpens the comp match.'
                            : 'No pudimos confirmar el tipo de propiedad con certeza. Confirmarlo mejora la coincidencia de comps.')
                        : analysis.arv_details?.low_comp_support_flag?.message}
                  </p>
                </div>
              </div>
            )}

            {/* Contextual Guidance: Manual Comparables - More Prominent CTA */}
            {(analysis.arv_confidence !== 'high' || analysis.comparable_count < 4 ||
              analysis.sales_comps?.some(c => c.distressed || c.sale_type === 'foreclosure' || c.sale_type === 'reo')) && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="manual-comps-guidance">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    {lang === 'en' ? 'Improve ARV Accuracy' : 'Mejorar Precisión del ARV'}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {lang === 'en' 
                      ? 'Add your own comps to get closer to a professional appraisal'
                      : 'Agrega tus propios comparables para acercarte a un appraisal profesional'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    const compManager = document.querySelector('[data-testid="comparable-manager"]');
                    if (compManager) {
                      compManager.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  data-testid="add-comps-cta-btn"
                >
                  {lang === 'en' ? '+ Add Comps' : '+ Agregar Comps'}
                </button>
              </div>
            )}

            {/* BUG 2 transparency: the multi-family ARV is adjusted DOWN when the subject is
                smaller than the comps (appraiser-style, down-only). Show where it came from so
                a ~2% move reads as a deliberate adjustment, not a mysterious drift. */}
            {analysis.arv_details?.v5_arv_calculation?.size_adjusted_from > 0 && (
              <div className="text-xs text-slate-500 mb-4 flex items-start gap-1.5" data-testid="size-adjustment-note">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  {/* The reason now arrives already in the reader's language (the engine
                      restates arv_details on the way out), so both branches use it. The
                      Spanish branch used to drop it and hardcode one sentence, which meant
                      a reason other than "smaller than the comparables" was shown in
                      English to an English reader and silently replaced for a Spanish one. */}
                  {lang === 'en'
                    ? `ARV size-adjusted from $${formatWholeDollars(analysis.arv_details.v5_arv_calculation.size_adjusted_from)} — ${analysis.arv_details.v5_arv_calculation.size_adjustment_reason || 'subject is smaller than the comparables'}.`
                    : `ARV ajustado por tamaño desde $${formatWholeDollars(analysis.arv_details.v5_arv_calculation.size_adjusted_from)} — ${analysis.arv_details.v5_arv_calculation.size_adjustment_reason || 'el inmueble es más pequeño que los comparables'}.`}
                </span>
              </div>
            )}

            {/* Multi-Family Valuation — AS-IS vs AFTER-REPAIR (ARV) */}
            {analysis.arv_details?.income_approach && (() => {
              const ia = analysis.arv_details.income_approach;
              const vb = analysis.arv_details.value_basis || analysis.arv_details.blended_arv?.value_basis || 'as_is';
              const isARV = vb === 'after_repair';
              const grmSrc = ia.grm_source || analysis.arv_details.blended_arv?.grm_source;
              const asIs = analysis.arv_details.as_is_value ?? analysis.arv_details.sales_approach_value;
              const arvVal = analysis.arv_details.arv_value ?? analysis.arv_details.blended_arv?.blended_arv;
              const grmBadge = grmSrc === 'user_override'
                ? (lang === 'en' ? 'your input' : 'tu valor')
                : grmSrc === 'derived_from_comps'
                  ? (lang === 'en' ? 'market estimate' : 'estimado de mercado')
                  : (lang === 'en' ? 'n/a — no rent data' : 'n/d — sin datos de renta');
              return (
              <div className={`border rounded-xl p-4 mb-6 ${isARV ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300'}`} data-testid="income-approach-details">
                {/* Unmistakable AS-IS vs ARV banner */}
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${isARV ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}>
                      {isARV ? (lang === 'en' ? 'After-Repair (ARV)' : 'Tras Reparación (ARV)') : (lang === 'en' ? 'As-Is Value' : 'Valor Actual')}
                    </span>
                    <span className={`text-xl font-bold ${isARV ? 'text-emerald-700' : 'text-amber-700'}`}>
                      ${formatWholeDollars(isARV ? arvVal : asIs)}
                    </span>
                  </div>
                  {/* ONE value, labelled — never the same figure under two names.
                      This used to print "As-Is $186,392 → ARV $186,392" whenever the basis
                      was after_repair. The arrow reads as a renovation uplift, and there
                      is none to read: the engine assigns `as_is_value` and `arv_value` the
                      SAME sales_arv (analysis_engine.py ~7884) and then labels it by
                      whether the comps it used were renovated. There is no step that adds
                      rehab to an as-is figure. So the pair is shown only when the two
                      numbers genuinely differ, which is the only case where the arrow
                      means anything. See BACKLOG.md, "the engine does not compute as-is
                      and ARV separately". */}
                  {isARV && Math.round(asIs) !== Math.round(arvVal) && (
                    <div className="text-xs text-slate-600" data-testid="as-is-to-arv">
                      {lang === 'en' ? 'As-Is' : 'Actual'}: <span className="font-semibold">${formatWholeDollars(asIs)}</span>
                      <span className="mx-1">→</span>
                      {lang === 'en' ? 'ARV' : 'ARV'}: <span className="font-semibold text-emerald-700">${formatWholeDollars(arvVal)}</span>
                    </div>
                  )}
                </div>

                {/* As-Is state: rents are THE lever to ARV */}
                {!isARV && (
                  <p className="text-xs text-amber-800 mb-3">
                    {lang === 'en'
                      ? 'This is the current value from recent area sales (as-is condition). Multi-family after-repair value (ARV) is driven by rents — enter your expected post-renovation rent per unit (and GRM) below to estimate ARV.'
                      : 'Este es el valor actual según ventas recientes (condición actual). El valor tras reparación (ARV) multifamiliar se basa en rentas — ingresa tu renta esperada por unidad tras renovación (y GRM) abajo para estimar el ARV.'}
                  </p>
                )}

                {/* Sales / Income / Final comparison — stack on phones so the three
                    6-figure values don't overflow; 3-up unchanged at >=640px */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white/70 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-blue-600">${formatWholeDollars(analysis.arv_details.sales_approach_value)}</div>
                    <div className="text-xs text-blue-700">{lang === 'en' ? 'Sales (per-unit)' : 'Ventas (por unidad)'}</div>
                    <div className="text-xs text-blue-500 mt-1">{analysis.arv_details.blended_arv?.sales_weight_pct || '—'} {lang === 'en' ? 'weight' : 'peso'}</div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-emerald-600">{ia.income_approach_value != null ? `$${formatWholeDollars(ia.income_approach_value)}` : '—'}</div>
                    <div className="text-xs text-emerald-700">{lang === 'en' ? 'Income (GRM)' : 'Ingreso (GRM)'}</div>
                    <div className="text-xs text-emerald-500 mt-1">{analysis.arv_details.blended_arv?.income_weight_pct || '—'} {lang === 'en' ? 'weight' : 'peso'}</div>
                  </div>
                  <div className={`rounded-lg p-3 text-center border-2 ${isARV ? 'bg-emerald-100 border-emerald-300' : 'bg-amber-100 border-amber-300'}`}>
                    <div className={`text-lg font-bold ${isARV ? 'text-emerald-700' : 'text-amber-700'}`}>${formatWholeDollars(isARV ? arvVal : asIs)}</div>
                    <div className={`text-xs font-medium ${isARV ? 'text-emerald-800' : 'text-amber-800'}`}>{lang === 'en' ? 'Final Value' : 'Valor Final'}</div>
                    <div className={`text-xs mt-1 ${isARV ? 'text-emerald-600' : 'text-amber-600'}`}>{isARV ? (lang === 'en' ? 'After-repair' : 'Tras reparación') : (lang === 'en' ? 'As-is' : 'Actual')}</div>
                  </div>
                </div>

                {/* Income breakdown (correct GRM keys) */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-emerald-700 font-medium">{ia.unit_count} {lang === 'en' ? 'units' : 'unidades'}</div>
                    <div className="text-slate-500 text-xs">{lang === 'en' ? 'Unit Count' : 'Unidades'}</div>
                  </div>
                  <div>
                    <div className="text-emerald-700 font-medium" data-testid="mf-rent-per-unit">${formatWholeDollars(ia.monthly_rent_per_unit)}/mo{ia.rent_source === 'user' ? (lang === 'en' ? ' (your rent)' : ' (tu renta)') : ''}</div>
                    <div className="text-slate-500 text-xs">{lang === 'en' ? 'Rent / Unit' : 'Renta / Unidad'}</div>
                  </div>
                  <div>
                    <div className="text-emerald-700 font-medium" data-testid="mf-rent-total">${formatWholeDollars(ia.total_monthly_rent)}/mo</div>
                    <div className="text-slate-500 text-xs">{lang === 'en' ? 'Total Monthly Rent' : 'Renta Mensual Total'}</div>
                  </div>
                  <div>
                    <div className="text-emerald-700 font-medium">{ia.grm != null ? ia.grm : '—'}</div>
                    <div className="text-slate-500 text-xs">{lang === 'en' ? 'GRM' : 'GRM'} <span className="text-[10px] italic">({grmBadge})</span></div>
                  </div>
                </div>

                {/* ARV state: Add Comps refinement + honesty line */}
                {isARV && (
                  <>
                    <p className="text-xs text-emerald-700 mt-3">
                      {lang === 'en'
                        ? 'Add recent comps to sharpen the ARV.'
                        : 'Agrega comps recientes para afinar el ARV.'}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-2 italic">
                      {lang === 'en'
                        ? 'CHEKSTE’s ARV is an estimate based on comparable sales. Its accuracy depends on the quality and proximity of the comps — that’s why we show you the confidence of each analysis and let you refine it with your own comps.'
                        : 'El ARV de CHEKSTE es una estimación basada en ventas comparables. Su exactitud depende de la calidad y cercanía de los comparables — por eso te mostramos la confianza de cada análisis y te dejamos afinarlo con tus propios comparables.'}
                    </p>
                  </>
                )}
              </div>
              );
            })()}

            {/* No qualifying sold comps: say so, rather than showing an empty table or —
                as before — quietly backfilling with raw-pool rows the engine rejected. */}
            {arvTableComps.length === 0 && (
              <div
                className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 mb-4"
                data-testid="no-qualifying-comps"
              >
                <p className="text-sm font-semibold text-amber-900">
                  {lang === 'en'
                    ? 'No qualifying sold comparables for this property'
                    : 'No hay comparables vendidos que califiquen para esta propiedad'}
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  {lang === 'en'
                    ? 'Active listings and distressed sales are excluded from the valuation and are never shown here as comparables. Add your own recent sold comps below for an accurate value.'
                    : 'Los listados activos y las ventas en dificultades se excluyen de la valoración y nunca se muestran aquí como comparables. Agregue sus propias ventas recientes abajo para un valor preciso.'}
                </p>
              </div>
            )}

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto" data-testid="comps-table-desktop">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 text-sm font-semibold text-slate-600">
                      {lang === 'en' ? 'Comparable' : 'Comparable'}
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-semibold text-slate-600">
                      {lang === 'en' ? 'Sold Price' : 'Precio Venta'}
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-semibold text-slate-600">
                      {lang === 'en' ? 'Sq Ft' : 'Pies²'}
                    </th>
                    <th className="text-center py-3 px-2 text-sm font-semibold text-slate-600">
                      {lang === 'en' ? 'Beds' : 'Hab.'}
                    </th>
                    <th className="text-center py-3 px-2 text-sm font-semibold text-slate-600">
                      {lang === 'en' ? 'Baths' : 'Baños'}
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-semibold text-slate-600">
                      {lang === 'en' ? 'Distance' : 'Distancia'}
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-semibold text-slate-600">
                      {lang === 'en' ? 'Sold Date' : 'Fecha Venta'}
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-semibold text-slate-600">
                      $/SqFt
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {arvTableComps.map((comp, index) => {
                    // Generate display address with fallback
                    const displayAddr = comp.display_address || comp.address || 
                      (lang === 'es' 
                        ? `Comparable #${index + 1} (${comp.distance?.toFixed(2)} mi)` 
                        : `Comparable #${index + 1} (${comp.distance?.toFixed(2)} mi away)`);
                    
                    return (
                      <tr 
                        key={index} 
                        className={`border-b border-border last:border-b-0 hover:bg-slate-50 transition-colors ${
                          index === 0 ? 'bg-blue-50/50' : ''
                        }`}
                      >
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            {index === 0 && (
                              <span className="px-1.5 py-0.5 bg-secondary text-white text-xs rounded">
                                {lang === 'en' ? 'Best' : 'Mejor'}
                              </span>
                            )}
                            <span className="font-medium text-sm">
                              {displayAddr}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className="font-semibold text-primary">${formatWholeDollars(comp.sale_price)}</span>
                        </td>
                        <td className="py-3 px-2 text-right text-sm">
                          {comp.sqft?.toLocaleString() || '—'}
                        </td>
                        <td className="py-3 px-2 text-center text-sm">
                          {comp.beds || '—'}
                        </td>
                        <td className="py-3 px-2 text-center text-sm">
                          {comp.baths || '—'}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className={`text-sm ${comp.distance <= 0.5 ? 'text-green-600 font-medium' : 'text-slate-600'}`}>
                            {comp.distance?.toFixed(2)} mi
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right text-sm text-muted-foreground">
                          {comp.sale_date ? new Date(comp.sale_date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          }) : '—'}
                        </td>
                        <td className="py-3 px-2 text-right text-sm font-medium">
                          {comp.adjusted_ppsf != null ? (
                            /* These are NOT the same figure twice: the struck-through one is
                               the comp's own $/sqft, the second is what it becomes after the
                               size adjustment, and the second is what the ARV uses. With only
                               a 4px margin they read as one run-on number ("$218$174"), so
                               the relationship is now spelled with an arrow. */
                            <span
                              className="whitespace-nowrap"
                              title={lang === 'en' ? 'raw $/sqft for this comp → after the size adjustment, which is what the ARV uses' : '$/sqft crudo de este comp → tras el ajuste por tamaño, que es el que usa el ARV'}
                            >
                              <span className="text-slate-400 line-through">${Math.round(comp.raw_ppsf || 0)}</span>
                              <span className="text-slate-400 mx-1.5">→</span>
                              <span className="text-primary font-semibold">${Math.round(comp.adjusted_ppsf)}</span>
                            </span>
                          ) : (
                            <span>${comp.price_per_sqft?.toFixed(0) || (comp.sqft ? Math.round(comp.sale_price / comp.sqft) : '—')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3" data-testid="comps-cards-mobile">
              {arvTableComps.map((comp, index) => {
                // Generate display address with fallback
                const displayAddr = comp.display_address || comp.address || 
                  (lang === 'es' 
                    ? `Comparable #${index + 1} (${comp.distance?.toFixed(2)} mi)` 
                    : `Comparable #${index + 1} (${comp.distance?.toFixed(2)} mi away)`);
                
                return (
                  <div 
                    key={index}
                    className={`p-4 rounded-lg border ${
                      index === 0 ? 'border-secondary bg-blue-50/50' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {index === 0 && (
                          <span className="px-1.5 py-0.5 bg-secondary text-white text-xs rounded">
                            {lang === 'en' ? 'Best' : 'Mejor'}
                          </span>
                        )}
                        <span className="font-medium text-sm">
                          {displayAddr}
                        </span>
                      </div>
                      <span className="font-semibold text-primary">${formatWholeDollars(comp.sale_price)}</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">{lang === 'en' ? 'Distance' : 'Dist.'}:</span>
                        <span className={`ml-1 ${comp.distance <= 0.5 ? 'text-green-600 font-medium' : ''}`}>
                          {comp.distance?.toFixed(2)} mi
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">$/SqFt:</span>
                        <span className="ml-1 font-medium">
                          ${comp.price_per_sqft?.toFixed(0) || (comp.sqft ? Math.round(comp.sale_price / comp.sqft) : '—')}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sq Ft:</span>
                        <span className="ml-1">{comp.sqft?.toLocaleString() || '—'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{lang === 'en' ? 'Beds/Baths' : 'Hab/Baños'}:</span>
                        <span className="ml-1">{comp.beds || '—'}/{comp.baths || '—'}</span>
                      </div>
                    </div>
                    
                    <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {lang === 'en' ? 'Sold' : 'Vendido'}: {comp.sale_date ? new Date(comp.sale_date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      }) : '—'}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* P3: Excluded comps (and WHY) — one click away, builds trust in the ARV */}
            {(() => {
              // THE COMPS SET ASIDE, WITH THE REASON THE ENGINE WROTE — not one guessed after
              // the fact. Closest first, because the comp a user looks for is the one next
              // door, and that is where the explanation has to be; fifteen rows, because a
              // pool of 378 rendered whole is a wall nobody reads (973 E Guthrie). The
              // subject property is not a comp and is not listed.
              const ordered = orderExcluded(analysis.comps_excluded);
              if (ordered.length === 0) return null;
              const { shown, hidden } = splitExcluded(ordered, showAllExcluded ? Infinity : EXCLUDED_ROWS_SHOWN);
              return (
                <details className="mt-4" data-testid="comps-excluded">
                  <summary className="cursor-pointer text-sm text-slate-600 hover:text-slate-800 select-none">
                    {lang === 'en'
                      ? `Show ${ordered.length} comps set aside (and why)`
                      : `Mostrar ${ordered.length} comparables descartados (y por qué)`}
                  </summary>
                  <div className="mt-2 max-h-80 overflow-auto rounded border border-slate-200 divide-y divide-slate-100">
                    {shown.map((c, i) => {
                      const { label, note } = exclusionText(c, lang);
                      const dist = Number.isFinite(Number(c.distance)) && c.distance != null
                        ? `${Number(c.distance).toFixed(2)} mi` : '';
                      return (
                        <div key={`${c.address}-${i}`} className="px-2 py-1.5 text-xs" data-exclusion-reason={c.exclusion_reason}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-slate-700">{c.address}</span>
                            <span className="text-slate-500 whitespace-nowrap">
                              {dist}{dist ? ' · ' : ''}${(c.sale_price || 0).toLocaleString()}{c.raw_ppsf ? ` · $${Math.round(c.raw_ppsf)}/sf` : ''}
                            </span>
                            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 whitespace-nowrap">
                              {label}
                            </span>
                          </div>
                          {note && <div className="mt-0.5 text-slate-500">{note}</div>}
                        </div>
                      );
                    })}
                  </div>
                  {hidden > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAllExcluded(true)}
                      className="mt-2 text-xs text-slate-600 hover:text-slate-800 underline"
                      data-testid="comps-excluded-more"
                    >
                      {lang === 'en' ? `and ${hidden} more` : `y ${hidden} más`}
                    </button>
                  )}
                </details>
              );
            })()}

            {/* Average Price Per Sq Ft */}
            {analysis.sales_comps.some(c => c.sqft && c.sale_price) && (
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  {/* Says WHICH population and WHICH statistic. This is the mean over
                      the whole delivered pool; "Median $/Sqft" elsewhere is the median of
                      the few comps the ARV actually used. Two different numbers for two
                      different things — the labels have to carry that. */}
                  {lang === 'en' ? 'Average $/Sq Ft (all comps received):' : 'Promedio $/Pie² (todos los comps recibidos):'}
                  <span className="ml-2 font-semibold text-primary">
                    ${(
                      analysis.sales_comps
                        .filter(c => c.sqft && c.sale_price)
                        .reduce((sum, c) => sum + (c.sale_price / c.sqft), 0) / 
                      analysis.sales_comps.filter(c => c.sqft && c.sale_price).length
                    ).toFixed(0)}/sq ft
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {lang === 'en' ? 'Subject Property:' : 'Propiedad Sujeta:'}
                  <span className="ml-2 font-medium">
                    {analysis.sqft?.toLocaleString()} sq ft • {analysis.beds} bed • {analysis.baths} bath
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Data Coverage Warning */}
        {analysis.arv_details && (
          <DataCoverageWarning 
            warningData={analysis.arv_details.data_coverage_warning}
            distressedWarning={analysis.arv_details.distressed_market_warning}
            arvDataSource={analysis.arv_details?.arv_data_source || analysis.diagnostics?.arv_data_source}
            lang={lang}
          />
        )}

        {/* ARV Explanation Panel - Now handled by ComparableManager component below */}

        {/* User Comparable Sales Manager */}
        {analysis.analysis_id && (
          <div className="mb-4 sm:mb-6">
            {isSample ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="sample-comps-locked">
                {lang === 'en'
                  ? 'Comparables cannot be edited on an example. Analyze a property of your own to add or remove comps.'
                  : 'En un ejemplo no se editan los comparables. Analiza una propiedad tuya para agregar o quitar comps.'}
              </div>
            ) : (
            <ComparableManager
              analysisId={analysis.analysis_id}
              arvUsedComps={analysis?.comps_used || []}
              initialMode={analysis?.active_arv_mode || 'both'}
              arvByMode={analysis?.arv_by_mode || null}
              outlierComps={(activeArvDetails?.user_comp_assessment?.outliers || []).map(o => o.address)}
              sizeNoteComps={(activeArvDetails?.user_comp_assessment?.size_notes || []).map(n => ({ address: n.address, pct: n.size_pct, direction: n.direction }))}
              lang={lang}
              user={user}
              onLockedFeature={() => setShowFeatureLockedModal(true)}
              onCompsChanged={() => setCompsModifiedSinceRecalc(true)}
              onARVUpdate={(newARVData) => {
                // Update current ARV state for deal recalculation
                const newARV = newARVData.arv;
                const newConfidence = newARVData.arv_confidence;
                const newScore = newARVData.arv_confidence_score;
                const modeLabel = newARVData.arv_details?.mode_label || null;
                const activeComps = newARVData.active_comps || [];
                const activeMode = newARVData.active_arv_mode || 'both';
                
                setCurrentARV(newARV);
                setCurrentARVConfidence(newConfidence);
                setCurrentARVConfidenceScore(newScore);
                setArvModeLabel(modeLabel);
                
                // G: adopt the FULL result of the mode (arv, confidence, arv_details,
                // comps_used, comps_excluded, comparable_count, counts, verdict) plus the
                // active comps for PDF export.
                setAnalysis(prev => adoptArvResult(prev, newARVData, {
                  active_arv_mode: activeMode,
                  arv_mode_label: modeLabel,
                  // Store active comps for PDF export - maps to sales_comps for PDF service
                  sales_comps: activeComps.map(comp => ({
                    address: comp.address || comp.display_address || '',
                    display_address: comp.display_address || comp.address || '',
                    sale_price: comp.sale_price || comp.price || 0,
                    sqft: comp.sqft || comp.living_area || 0,
                    price_per_sqft: comp.price_per_sqft || (comp.sale_price && comp.sqft ? Math.round(comp.sale_price / comp.sqft) : 0),
                    distance: comp.distance || 0,
                    sale_date: comp.sold_date || comp.sale_date || '',
                    beds: comp.beds || 0,
                    baths: comp.baths || 0,
                    source: comp.source || 'system'
                  })),
                }));
                
                // Trigger full deal recalculation with new ARV — FLIP/RENTAL ONLY.
                // A valuation has no flip/rental financials to recompute, and that
                // path re-derives ARV from the cached comp pool (WITHOUT the user
                // comp), which would overwrite ComparableManager's blended result
                // and zero analysis.arv. So in valuation mode the setAnalysis above
                // (arv + arv_details from the same ComparableManager result) is the
                // authoritative, single ARV source — top card and section can't diverge.
                if (!isValuationMode) {
                  recalculateAnalysis(assumptions, propertyOverrides, {
                    arvOverride: newARV,
                    arvStatusOverride: newARVData.arv_details?.arv_status ?? null,
                    activeModeOverride: activeMode,
                  });
                }

                // Reset comps modified flag since we just recalculated
                setCompsModifiedSinceRecalc(false);
                
                toast.success(
                  lang === 'en' 
                    ? `Deal analysis updated with new ARV: $${newARV?.toLocaleString()}`
                    : `Análisis actualizado con nuevo ARV: $${newARV?.toLocaleString()}`
                );
              }}
            />
            )}
          </div>
        )}

        {/* Editable Deal Assumptions - FLIP ONLY - Hidden in valuation mode */}
        {!isValuationMode && analysis.strategy === 'flip' && (
        <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 md:p-8 mb-4 sm:mb-6" data-testid="deal-assumptions">
          <button
            onClick={() => setShowAssumptions(!showAssumptions)}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
              <Settings2 className="w-6 h-6" />
              {lang === 'en' ? 'Deal Assumptions' : 'Supuestos del Trato'}
              {recalculating && <RefreshCw className="w-4 h-4 animate-spin text-secondary" />}
            </h2>
            {showAssumptions ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
          
          {showAssumptions && (
            <div className="mt-6 space-y-6 animate-fade-in">
              {/* REHAB BUDGET — the presets (Auto/Light/Medium/Heavy) were removed on a
                  lender's recommendation: a $/sqft band is not an investment-grade number,
                  and the investor's own budget is. The manual amount is now the only path.

                  This is a UI change, not an engine one. `rehabCondition` stays in state at
                  'auto' and is still sent, so:
                    - a blank amount still produces the auto-detected estimate, exactly as
                      the default preset did. A blank read as $0 would make every deal look
                      profitable, which is the opposite of the reason for removing them.
                    - a saved analysis that carries a declared condition still opens on it,
                      and the backend still accepts rehab_condition unchanged.
                  The engine already subordinated the label to the amount — the precedence
                  in analysis_engine.py is custom_rehab_total > custom_rehab_per_sqft >
                  condition — so a typed budget produced the same number before and after. */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-primary mb-1">
                  <Wrench className="w-4 h-4" />
                  {lang === 'en' ? 'Rehab Budget' : 'Presupuesto de Remodelación'}
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  {lang === 'en'
                    ? 'Enter your real budget. Left blank, it is estimated from the property.'
                    : 'Ingresa tu presupuesto real. En blanco, se estima a partir de la propiedad.'}
                </p>

                <div className="mt-3 space-y-3">
                    {/* Input Mode Toggle */}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        {lang === 'en' ? 'Input Type:' : 'Tipo de Entrada:'}
                      </span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="rehabInputMode"
                          checked={assumptions.customRehabInputMode === 'total'}
                          onChange={() => updateAssumptions(prev => ({ 
                            ...prev, 
                            customRehabInputMode: 'total',
                            // Clear the per sqft value when switching to total
                            customRehabPerSqft: ''
                          }))}
                          className="w-4 h-4 text-secondary focus:ring-secondary"
                          data-testid="rehab-mode-total"
                        />
                        <span className={assumptions.customRehabInputMode === 'total' ? 'font-medium text-foreground' : 'text-muted-foreground'}>
                          {lang === 'en' ? 'Total Budget' : 'Presupuesto Total'}
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="rehabInputMode"
                          checked={assumptions.customRehabInputMode === 'persqft'}
                          onChange={() => updateAssumptions(prev => ({ 
                            ...prev, 
                            customRehabInputMode: 'persqft',
                            // Clear the total value when switching to per sqft
                            customRehabTotal: ''
                          }))}
                          className="w-4 h-4 text-secondary focus:ring-secondary"
                          data-testid="rehab-mode-persqft"
                        />
                        <span className={assumptions.customRehabInputMode === 'persqft' ? 'font-medium text-foreground' : 'text-muted-foreground'}>
                          {lang === 'en' ? 'Cost per SqFt' : 'Costo por Pie²'}
                        </span>
                      </label>
                    </div>
                    
                    {/* Total Budget Input */}
                    {assumptions.customRehabInputMode === 'total' && (
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">
                          {lang === 'en' ? 'Total Rehab Budget' : 'Presupuesto Total de Remodelación'}
                        </label>
                        <div className="flex items-center gap-3">
                          <FormattedCurrencyInput
                            value={assumptions.customRehabTotal}
                            onChange={(val) => updateAssumptions(prev => ({ ...prev, customRehabTotal: val }))}
                            placeholder="e.g., 45,000"
                            className="w-full pr-4 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm max-w-[200px]"
                            data-testid="results-rehab-total-input"
                          />
                          {/* Show equivalent $/sqft */}
                          {assumptions.customRehabTotal && analysis?.sqft > 0 && (
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              ≈ ${Math.round(parseFloat(assumptions.customRehabTotal.replace(/,/g, '')) / analysis.sqft)}/sqft
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Per SqFt Input */}
                    {assumptions.customRehabInputMode === 'persqft' && (
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">
                          {lang === 'en' ? 'Rehab Cost per SqFt' : 'Costo de Remodelación por Pie²'}
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="relative max-w-[200px]">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                              type="number"
                              value={assumptions.customRehabPerSqft}
                              onChange={(e) => updateAssumptions(prev => ({ ...prev, customRehabPerSqft: e.target.value }))}
                              placeholder="e.g., 35"
                              className="w-full pl-8 pr-16 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm"
                              data-testid="results-custom-rehab-persqft"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">/sqft</span>
                          </div>
                          {/* Show calculated total */}
                          {assumptions.customRehabPerSqft && analysis?.sqft > 0 && (
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              = ${(parseFloat(assumptions.customRehabPerSqft) * analysis.sqft).toLocaleString()} total
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
              
              {/* Financial Assumptions Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {/* Holding Period */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">
                    {lang === 'en' ? 'Holding Period' : 'Período de Retención'}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={assumptions.holdingPeriod}
                      onChange={(e) => updateAssumptions(prev => ({ ...prev, holdingPeriod: e.target.value }))}
                      min="1"
                      max="36"
                      className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm pr-16"
                      data-testid="holding-period-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      {lang === 'en' ? 'mo' : 'mes'}
                    </span>
                  </div>
                </div>
                
                {/* Closing Costs */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">
                    {lang === 'en' ? 'Closing Costs' : 'Costos de Cierre'}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={assumptions.closingCostsPercent}
                      onChange={(e) => updateAssumptions(prev => ({ ...prev, closingCostsPercent: e.target.value }))}
                      min="0"
                      max="10"
                      step="0.5"
                      className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm pr-8"
                      data-testid="closing-costs-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                  </div>
                </div>
                
                {/* Agent Commission */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">
                    {lang === 'en' ? 'Agent Commission' : 'Comisión Agente'}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={assumptions.agentCommission}
                      onChange={(e) => updateAssumptions(prev => ({ ...prev, agentCommission: e.target.value }))}
                      min="0"
                      max="10"
                      step="0.5"
                      className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm pr-8"
                      data-testid="agent-commission-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                  </div>
                </div>
                
                {/* Target Offer Rule (formerly MAO Rule) - Hide for Rental */}
                {analysis.strategy !== 'rental' && (
                <div>
                  <label className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    {lang === 'en' ? 'Target % Rule' : 'Regla de % Objetivo'}
                    <div className="relative group">
                      <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                      <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20 w-56 text-center">
                        {lang === 'en' 
                          ? 'Target offer rule: (Est. Value × %) - Rehab = Target Price. Lower % = more conservative, higher % = more aggressive.' 
                          : 'Regla de oferta objetivo: (Valor Est. × %) - Rehab = Precio Objetivo. Menor % = más conservador, mayor % = más agresivo.'}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                      </div>
                    </div>
                  </label>
                  <div className="relative">
                    <select
                      value={assumptions.maoRule}
                      onChange={(e) => updateAssumptions(prev => ({ ...prev, maoRule: parseFloat(e.target.value) }))}
                      className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm appearance-none bg-white"
                      data-testid="mao-rule-select"
                    >
                      <option value="65">65% ({lang === 'en' ? 'Conservative' : 'Conservador'})</option>
                      <option value="70">70% ({lang === 'en' ? 'Balanced' : 'Equilibrado'})</option>
                      <option value="75">75% ({lang === 'en' ? 'Moderate' : 'Moderado'})</option>
                      <option value="80">80% ({lang === 'en' ? 'Aggressive' : 'Agresivo'})</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                )}
              </div>
              
              {/* Deal Tolerance Section - NEW */}
              <div className="mt-6 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  {lang === 'en' ? 'Deal Tolerance' : 'Tolerancia del Trato'}
                  <div className="relative group ml-1">
                    <HelpCircle className="w-3.5 h-3.5 text-amber-500 cursor-help" />
                    <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20 w-64 text-center">
                      {lang === 'en' 
                        ? 'Allows near-target deals to be classified more favorably when they are close to your criteria but still acceptable.' 
                        : 'Permite clasificar más favorablemente los tratos cercanos a tu objetivo cuando están cerca de tus criterios pero aún son aceptables.'}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                    </div>
                  </div>
                </h4>
                
                {/* Tolerance Mode Selector */}
                <div className="mb-4">
                  <label className="text-xs text-amber-700 mb-1 block">
                    {lang === 'en' ? 'Tolerance Mode' : 'Modo de Tolerancia'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'strict', label: lang === 'en' ? 'Strict' : 'Estricto', pricePct: 0, profitDollars: 0 },
                      { value: 'balanced', label: lang === 'en' ? 'Balanced' : 'Equilibrado', pricePct: 1, profitDollars: 2500 },
                      { value: 'aggressive', label: lang === 'en' ? 'Aggressive' : 'Agresivo', pricePct: 3, profitDollars: 5000 },
                      { value: 'custom', label: lang === 'en' ? 'Custom' : 'Personalizado', pricePct: null, profitDollars: null },
                    ].map(mode => (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => {
                          const newState = { toleranceMode: mode.value };
                          if (mode.pricePct !== null) {
                            newState.tolerancePricePct = mode.pricePct;
                            newState.toleranceProfitDollars = mode.profitDollars;
                          }
                          updateAssumptions(prev => ({ ...prev, ...newState }));
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          assumptions.toleranceMode === mode.value
                            ? 'bg-amber-500 text-white'
                            : 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-100'
                        }`}
                        data-testid={`tolerance-${mode.value}-btn`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    {assumptions.toleranceMode === 'strict' && (lang === 'en' ? 'Exact targets only - no flexibility' : 'Solo objetivos exactos - sin flexibilidad')}
                    {assumptions.toleranceMode === 'balanced' && (lang === 'en' ? 'Small tolerance for near-target deals' : 'Pequeña tolerancia para tratos cercanos')}
                    {assumptions.toleranceMode === 'aggressive' && (lang === 'en' ? 'Wider tolerance for opportunity hunting' : 'Mayor tolerancia para buscar oportunidades')}
                    {assumptions.toleranceMode === 'custom' && (lang === 'en' ? 'Set your own tolerance values below' : 'Establece tus propios valores abajo')}
                  </p>
                </div>
                
                {/* Custom Tolerance Values - Only show when custom mode is selected */}
                {assumptions.toleranceMode === 'custom' && (
                  <div className="grid grid-cols-2 gap-4 mt-3 p-3 bg-white rounded-lg border border-amber-200">
                    <div>
                      <label className="text-xs text-slate-600 mb-1 block">
                        {lang === 'en' ? 'Price Tolerance' : 'Tolerancia de Precio'}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.5"
                          value={assumptions.tolerancePricePct}
                          onChange={(e) => updateAssumptions(prev => ({ ...prev, tolerancePricePct: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 pr-8 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          data-testid="tolerance-price-input"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">%</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {lang === 'en' ? 'Above target rule' : 'Por encima del objetivo'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 mb-1 block">
                        {lang === 'en' ? 'Profit Tolerance' : 'Tolerancia de Ganancia'}
                      </label>
                      <div className="relative">
                        {/* BUG 5: $ formatting; clean number stored. */}
                        <FormattedCurrencyInput
                          value={assumptions.toleranceProfitDollars}
                          onChange={(val) => updateAssumptions(prev => ({ ...prev, toleranceProfitDollars: parseFormattedNumber(val) || 0 }))}
                          prefix="$"
                          className="w-full pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          data-testid="tolerance-profit-input"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {lang === 'en' ? 'Below profit target' : 'Por debajo del objetivo'}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Current Tolerance Display */}
                {assumptions.toleranceMode !== 'custom' && (
                  <div className="flex gap-4 mt-2 text-xs text-amber-700">
                    <span>
                      {lang === 'en' ? 'Price:' : 'Precio:'} +{assumptions.tolerancePricePct}%
                    </span>
                    <span>
                      {lang === 'en' ? 'Profit:' : 'Ganancia:'} -${assumptions.toleranceProfitDollars.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
              
              {/* MULTI-FAMILY FLIP: Estimated rent per unit (feeds the GRM income side
                  of the sale-price ARV — investors value 2-4 unit buildings by income).
                  Shown for multi-family only; SFR flip uses sales comps only. */}
              {(((analysis?.arv_details?.income_approach?.unit_count) || (analysis?.property_snapshot?.unit_count) || (analysis?.rental_analysis?.unit_count) || 1) > 1) && (
                <div className="mt-6 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                  <h4 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    {lang === 'en' ? 'Estimated Rent / Unit (after renovation)' : 'Renta Estimada / Unidad (tras renovación)'}
                  </h4>
                  {/* This used to say "Investors value multi-family by income — this rent
                      feeds the sale-price ARV (GRM)". It was true only because the GRM box
                      below arrived pre-filled with the DERIVED GRM, which the payload sent
                      as an override. With that removed, the rent on its own reaches
                      nothing: no GRM, no income approach, and the ARV comes from the sales
                      comparables — the default validated against 12 appraisals. */}
                  <p className="text-xs text-emerald-700 mb-3">
                    {lang === 'en'
                      ? 'The ARV comes from comparable sales unless you enter a GRM below. This rent is used only together with a GRM, and is shared with Rental/BRRRR.'
                      : 'El ARV sale de las ventas comparables salvo que ingreses un GRM abajo. Esta renta se usa solo junto con un GRM, y se comparte con Renta/BRRRR.'}
                  </p>
                  <div className="flex items-center gap-2">
                    {/* BUG 5: $ formatting; store the CLEAN number so downstream parseFloat works. */}
                    <FormattedCurrencyInput
                      value={assumptions.flipRentPerUnit || ''}
                      onChange={(val) => updateAssumptions(prev => ({ ...prev, flipRentPerUnit: parseFormattedNumber(val) || '', flipRentIsUser: true }))}
                      onBlur={() => recalculateAnalysis(assumptions, propertyOverrides)}
                      prefix="$"
                      placeholder={lang === 'en' ? 'rent per unit / mo' : 'renta por unidad / mes'}
                      className="w-40 px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-emerald-400"
                      data-testid="flip-rent-per-unit-input"
                    />
                    {/* Unambiguous: the box is PER UNIT, and the total it makes is shown live. */}
                    {(() => {
                      const _u = (analysis?.arv_details?.income_approach?.unit_count) || (analysis?.property_snapshot?.unit_count) || (analysis?.rental_analysis?.unit_count) || 1;
                      const _r = parseFloat(assumptions.flipRentPerUnit) || 0;
                      return (
                        <span className="text-xs text-slate-600" data-testid="flip-rent-total">
                          {lang === 'en' ? '/unit/mo × ' : '/unidad/mes × '}{_u}{lang === 'en' ? ' units' : ' unidades'}
                          {_r > 0 && (
                            <span className="font-semibold text-slate-800"> = ${formatWholeDollars(_r * _u)}{lang === 'en' ? '/mo total' : '/mes en total'}</span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                  <RecommendedHint lang={lang}
                    en="Used only together with a GRM. On its own it does not change the ARV."
                    es="Se usa solo junto con un GRM. Por sí sola no cambia el ARV." />

                  {/* GRM input — the second ARV lever (rents × GRM). EMPTY until the user
                      types one; the market-derived GRM shows as the placeholder and in the
                      "market est. NN" label, where it informs without becoming an answer.
                      Typing here is the opt-in that moves the ARV from sales to income. */}
                  <div className="mt-3 pt-3 border-t border-emerald-200">
                    <label className="text-xs font-semibold text-emerald-800">
                      {lang === 'en' ? 'GRM (gross rent multiplier)' : 'GRM (multiplicador de renta bruta)'}
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="number"
                        step="1"
                        value={assumptions.flipGrm || ''}
                        onChange={(e) => updateAssumptions(prev => ({ ...prev, flipGrm: e.target.value }))}
                        onBlur={() => recalculateAnalysis(assumptions, propertyOverrides)}
                        placeholder={`${analysis?.arv_details?.income_approach?.grm || 95}`}
                        className="w-24 px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-emerald-400"
                        data-testid="flip-grm-input"
                      />
                      <span className="text-xs text-slate-400">
                        {analysis?.arv_details?.income_approach?.grm
                          ? (lang === 'en' ? `market est. ${analysis.arv_details.income_approach.grm}` : `est. mercado ${analysis.arv_details.income_approach.grm}`)
                          : ''}
                      </span>
                    </div>
                    {String(assumptions.flipGrm || '').trim() !== '' && (
                      <p className="mt-1 text-xs font-medium text-amber-700" data-testid="flip-grm-manual-label">
                        {lang === 'en'
                          ? 'Manual GRM — values by rents (after-repair), not sales comparables.'
                          : 'GRM manual — valora por rentas (tras reparación), no por comparables de venta.'}
                      </p>
                    )}
                    <RecommendedHint lang={lang}
                      en="Leave blank to value by sales comparables (default). Enter a GRM only to value by rents (after-repair)."
                      es="Déjalo en blanco para valorar por comparables de venta (predeterminado). Ingresa un GRM solo para valorar por rentas (tras reparación)." />
                  </div>
                </div>
              )}

              {/* Financing Structure Section */}
              <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-800 mb-4 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  {lang === 'en' ? 'Financing Structure' : 'Estructura de Financiamiento'}
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Purchase Financing */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium text-slate-700">
                        {lang === 'en' ? 'Purchase Financing' : 'Financiamiento de Compra'}
                      </label>
                      <div className="flex rounded-lg border border-slate-300 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => switchFinancingMode('cash')}
                          className={`px-3 py-1 text-xs font-medium transition-colors ${
                            assumptions.purchaseFinancing === 'cash'
                              ? 'bg-green-500 text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                          data-testid="purchase-cash-btn"
                        >
                          {lang === 'en' ? 'Cash' : 'Efectivo'}
                        </button>
                        <button
                          type="button"
                          onClick={() => switchFinancingMode('financed')}
                          className={`px-3 py-1 text-xs font-medium transition-colors border-l border-slate-300 ${
                            assumptions.purchaseFinancing === 'financed'
                              ? 'bg-blue-500 text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                          data-testid="purchase-financed-btn"
                        >
                          {lang === 'en' ? 'Financed' : 'Financiado'}
                        </button>
                      </div>
                    </div>
                    
                    {assumptions.purchaseFinancing === 'financed' ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          {/* Down Payment Mode Toggle */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs text-slate-500">
                                {/* BUG 2 FIX: in LTV mode the input holds the LTV (e.g. 80%), NOT
                                    the down payment. Label it "LTV" so 80% never reads as a down
                                    payment; the true down payment (100 − LTV) is shown below. */}
                                {downPaymentMode === 'ltv'
                                  ? (lang === 'en' ? 'LTV (loan-to-value)' : 'LTV (préstamo-valor)')
                                  : (lang === 'en' ? 'Down Payment' : 'Enganche')}
                              </label>
                              <div className="flex text-xs border border-slate-200 rounded overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => setDownPaymentMode('ltv')}
                                  className={`px-2 py-0.5 ${downPaymentMode === 'ltv' ? 'bg-blue-500 text-white' : 'text-slate-500'}`}
                                  data-testid="down-payment-mode-ltv"
                                >LTV %</button>
                                <button
                                  type="button"
                                  onClick={() => setDownPaymentMode('manual')}
                                  className={`px-2 py-0.5 ${downPaymentMode === 'manual' ? 'bg-blue-500 text-white' : 'text-slate-500'}`}
                                  data-testid="down-payment-mode-manual"
                                >$ Amount</button>
                              </div>
                            </div>
                            {downPaymentMode === 'ltv' ? (
                              <div>
                                <div className="relative">
                                  <input
                                    type="number"
                                    value={assumptions.purchaseLTV}
                                    onChange={(e) => updateAssumptions(prev => ({ ...prev, purchaseLTV: e.target.value }))}
                                    min="0" max="100" step="5"
                                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400 pr-6"
                                    data-testid="purchase-ltv-input"
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                                </div>
                                {/* True down payment = 100 − LTV, shown so the investor sees what
                                    they actually put in (e.g. LTV 80% → 20% down). */}
                                <div className="text-xs text-slate-500 mt-1">
                                  {lang === 'en' ? 'Down payment: ' : 'Enganche: '}
                                  <span className="font-semibold text-slate-700">
                                    {Math.max(0, 100 - (parseFloat(assumptions.purchaseLTV) || 0)).toFixed(0)}%
                                  </span>
                                  {analysis.purchase_price > 0 && (
                                    <span className="text-slate-400">
                                      {' '}(${Math.round(analysis.purchase_price * Math.max(0, 100 - (parseFloat(assumptions.purchaseLTV) || 0)) / 100).toLocaleString()})
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="relative">
                                {/* BUG 5: $ formatting; store the CLEAN number for calc.
                                    `||` collapsed an explicit $0 to '' — the same falsy-is-absent
                                    confusion as everywhere else in this file. `??` keeps 0 as 0
                                    (100% financing, a real structure) and only a CLEARED field,
                                    which parseFormattedNumber returns as null, becomes ''. */}
                                <FormattedCurrencyInput
                                  value={manualDownPayment}
                                  onChange={(val) => setManualDownPayment(parseFormattedNumber(val) ?? '')}
                                  prefix="$"
                                  placeholder="0"
                                  className="w-full pr-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400"
                                  data-testid="manual-down-payment-input"
                                />
                                </div>
                                {/* The mirror of the LTV mode's "Down payment: 20% ($17,800)" hint.
                                    It prints the LTV that will ACTUALLY be sent, so a blank field
                                    (which keeps the previous LTV) and a clamped one (down payment
                                    larger than the price) are visible instead of silently
                                    disagreeing with the amount on screen. */}
                                {(() => {
                                  const _ltvToSend = impliedPurchaseLtv({
                                    mode: 'manual',
                                    manualDownPayment,
                                    purchasePrice: effectivePurchasePrice,
                                    ltv: assumptions.purchaseLTV ?? 80,
                                  });
                                  const _effDown = effectiveDownPayment(effectivePurchasePrice, _ltvToSend);
                                  const _typed = parseFloat(manualDownPayment);
                                  const _clamped = Number.isFinite(_typed) && Math.abs(_typed - _effDown) > 1;
                                  return (
                                    <div className="text-xs text-slate-500 mt-1" data-testid="manual-down-payment-implied">
                                      LTV:{' '}
                                      <span className="font-semibold text-slate-700" data-testid="manual-implied-ltv">
                                        {_ltvToSend.toFixed(1)}%
                                      </span>
                                      {effectivePurchasePrice > 0 && (
                                        <span className="text-slate-400">
                                          {' '}({lang === 'en' ? 'loan ' : 'préstamo '}
                                          ${Math.round(effectivePurchasePrice - _effDown).toLocaleString()})
                                        </span>
                                      )}
                                      {_clamped && (
                                        <span className="block text-amber-600" data-testid="manual-down-payment-clamped">
                                          {lang === 'en'
                                            ? `Down payment cannot exceed the price — using $${Math.round(_effDown).toLocaleString()}.`
                                            : `El enganche no puede superar el precio — se usará $${Math.round(_effDown).toLocaleString()}.`}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Interest Rate' : 'Tasa'}
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                value={assumptions.purchaseInterestRate}
                                onChange={(e) => updateAssumptions(prev => ({ ...prev, purchaseInterestRate: e.target.value }))}
                                placeholder="9-15"
                                min="9"
                                max="15"
                                step="0.5"
                                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400 pr-6"
                                data-testid="purchase-interest-input"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Monthly Interest-Only Payment (backend-computed: purchase I/O + financed rehab I/O) */}
                        <div className="flex justify-between items-center text-sm mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                          <span className="text-blue-700 font-medium">
                            {lang === 'en' ? 'Est. Monthly Payment (I/O)' : 'Pago Mensual Est. (Solo Interés)'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-blue-800">
                              ${Math.round(analysis.flip_analysis?.monthly_loan_payment || 0).toLocaleString()}/mo
                            </span>
                            <span className="text-xs text-blue-500">
                              {lang === 'en' ? '(interest only)' : '(solo interés)'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Loan Points' : 'Puntos'}
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                value={assumptions.purchaseLoanPoints}
                                onChange={(e) => updateAssumptions(prev => ({ ...prev, purchaseLoanPoints: e.target.value }))}
                                min="0"
                                max="10"
                                step="0.5"
                                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400 pr-6"
                                data-testid="purchase-points-input"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Lender Fees' : 'Honorarios'}
                            </label>
                            <FormattedCurrencyInput
                              value={assumptions.purchaseLenderFees}
                              onChange={(val) => updateAssumptions(prev => ({ ...prev, purchaseLenderFees: val }))}
                              placeholder="0"
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400"
                              data-testid="purchase-fees-input"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-3 text-sm text-green-600 bg-green-50 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 inline mr-1" />
                        {lang === 'en' ? 'All cash purchase - no financing costs' : 'Compra en efectivo - sin costos de financiamiento'}
                      </div>
                    )}
                  </div>
                  
                  {/* Rehab Financing */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium text-slate-700">
                        {lang === 'en' ? 'Rehab Financing' : 'Financiamiento de Rehab'}
                      </label>
                      <div className="flex rounded-lg border border-slate-300 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => { setRehabFinancingTouched(true); updateAssumptions(prev => ({ ...prev, rehabFinancing: 'cash' })); }}
                          className={`px-3 py-1 text-xs font-medium transition-colors ${
                            assumptions.rehabFinancing === 'cash'
                              ? 'bg-green-500 text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                          data-testid="rehab-cash-btn"
                        >
                          {lang === 'en' ? 'Cash' : 'Efectivo'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRehabFinancingTouched(true); updateAssumptions(prev => ({ ...prev, rehabFinancing: 'financed' })); }}
                          className={`px-3 py-1 text-xs font-medium transition-colors border-l border-slate-300 ${
                            assumptions.rehabFinancing === 'financed'
                              ? 'bg-blue-500 text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                          data-testid="rehab-financed-btn"
                        >
                          {lang === 'en' ? 'Financed' : 'Financiado'}
                        </button>
                      </div>
                    </div>
                    
                    {assumptions.rehabFinancing === 'financed' ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? '% Financed' : '% Financiado'}
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                value={assumptions.rehabFinancedPct}
                                onChange={(e) => updateAssumptions(prev => ({ ...prev, rehabFinancedPct: e.target.value }))}
                                min="0"
                                max="100"
                                step="5"
                                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400 pr-6"
                                data-testid="rehab-financed-pct-input"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Interest Rate' : 'Tasa'}
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                value={assumptions.rehabInterestRate}
                                onChange={(e) => updateAssumptions(prev => ({ ...prev, rehabInterestRate: e.target.value }))}
                                min="0"
                                max="30"
                                step="0.5"
                                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400 pr-6"
                                data-testid="rehab-interest-input"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">
                            {lang === 'en' ? 'Loan Points' : 'Puntos'}
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={assumptions.rehabLoanPoints}
                              onChange={(e) => updateAssumptions(prev => ({ ...prev, rehabLoanPoints: e.target.value }))}
                              min="0"
                              max="10"
                              step="0.5"
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400 pr-6"
                              data-testid="rehab-points-input"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-3 text-sm text-green-600 bg-green-50 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 inline mr-1" />
                        {lang === 'en' ? 'Cash rehab - no financing costs' : 'Rehab en efectivo - sin costos'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Holding Costs Row — stack the currency inputs on phones so each is
                  full-width and tappable; 3-up unchanged at >=640px */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                {/* Property Taxes — ANNUAL (BUG 4), converted to monthly like insurance */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">
                    {lang === 'en' ? 'Property Taxes /yr' : 'Impuestos /año'}
                  </label>
                  <FormattedCurrencyInput
                    value={assumptions.propertyTaxes}
                    onChange={(val) => updateAssumptions(prev => ({ ...prev, propertyTaxes: val }))}
                    placeholder="auto"
                    className="w-full pr-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm"
                    data-testid="property-taxes-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {lang === 'en' ? 'Annual amount ÷ 12 → monthly' : 'Monto anual ÷ 12 → mensual'}
                  </p>
                </div>
                
                {/* Insurance - ANNUAL amount */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">
                    {lang === 'en' ? 'Insurance /yr' : 'Seguro /año'}
                  </label>
                  <FormattedCurrencyInput
                    value={assumptions.insurance}
                    onChange={(val) => updateAssumptions(prev => ({ ...prev, insurance: val }))}
                    placeholder="auto"
                    className="w-full pr-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm"
                    data-testid="insurance-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {lang === 'en' ? 'Annual amount ÷ holding months' : 'Monto anual ÷ meses de rehab'}
                  </p>
                </div>
                
                {/* Utilities - MONTHLY amount */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">
                    {lang === 'en' ? 'Utilities /mo' : 'Servicios /mes'}
                  </label>
                  <FormattedCurrencyInput
                    value={assumptions.utilities}
                    onChange={(val) => updateAssumptions(prev => ({ ...prev, utilities: val }))}
                    placeholder="auto"
                    className="w-full pr-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm"
                    data-testid="utilities-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {lang === 'en' ? 'Monthly × holding period' : 'Mensual × período de retención'}
                  </p>
                </div>
              </div>
              
              {/* Apply and Reset Buttons */}
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  onClick={() => recalculateAnalysis(assumptions)}
                  disabled={recalculating}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors disabled:opacity-50"
                  data-testid="apply-assumptions"
                >
                  {recalculating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {lang === 'en' ? 'Apply Changes & Recalculate' : 'Aplicar y Recalcular'}
                </button>
                <button
                  onClick={() => {
                    setAssumptions(defaultAssumptions);
                    setTimeout(() => recalculateAnalysis(defaultAssumptions), 100);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-border text-muted-foreground rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid="reset-to-defaults"
                >
                  <RotateCcw className="w-4 h-4" />
                  {lang === 'en' ? 'Reset to System Defaults' : 'Restaurar Predeterminados'}
                </button>
                
                {/* User Defaults Buttons */}
                <button
                  onClick={() => {
                    localStorage.setItem('chekdeal_user_defaults', JSON.stringify(assumptions));
                    toast.success(lang === 'en' ? 'Defaults saved!' : '¡Predeterminados guardados!');
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-green-500 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
                  data-testid="save-my-defaults"
                >
                  <Save className="w-4 h-4" />
                  {lang === 'en' ? 'Save as My Default' : 'Guardar como Mi Predeterminado'}
                </button>
                <button
                  onClick={() => {
                    const saved = localStorage.getItem('chekdeal_user_defaults');
                    if (saved) {
                      const savedAssumptions = JSON.parse(saved);
                      setAssumptions(savedAssumptions);
                      setTimeout(() => recalculateAnalysis(savedAssumptions), 100);
                      toast.success(lang === 'en' ? 'Defaults loaded!' : '¡Predeterminados cargados!');
                    } else {
                      toast.error(lang === 'en' ? 'No saved defaults found' : 'No se encontraron predeterminados guardados');
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                  data-testid="load-my-defaults"
                >
                  <Download className="w-4 h-4" />
                  {lang === 'en' ? 'Load My Defaults' : 'Cargar Mis Predeterminados'}
                </button>
              </div>
              
              {/* Current Calculated Values - FLIP ONLY (V4.0: Score/Rating removed) */}
              {analysis.strategy === 'flip' && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-border">
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {lang === 'en' ? 'Key Metrics:' : 'Métricas Clave:'}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Rehab Cost:' : 'Costo Rehab:'}</span>
                    <span className="ml-2 font-semibold">${formatWholeDollars(analysis.rehab_estimate)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Net Profit:' : 'Ganancia Neta:'}</span>
                    <span className={`ml-2 font-semibold ${financialTextColor(analysis.flip_analysis?.net_profit)}`}>
                      {arvDerived(`$${formatWholeDollars(analysis.flip_analysis?.net_profit)}`)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Profit Margin:' : 'Margen:'}</span>
                    <span className={`ml-2 font-semibold ${(analysis.flip_analysis?.profit_margin || 0) >= 10 ? 'text-success' : 'text-amber-600'}`}>
                      {arvDerived(`${(analysis.flip_analysis?.profit_margin || 0).toFixed(1)}%`)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'ARV:' : 'ARV:'}</span>
                    <span className="ml-2 font-semibold">${formatWholeDollars(analysis.arv)}</span>
                  </div>
                </div>
              </div>
              )}
              
              {/* Current Calculated Values - RENTAL ONLY */}
              {analysis.strategy === 'rental' && analysis.rental_analysis && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-border">
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {lang === 'en' ? 'Rental Metrics:' : 'Métricas de Alquiler:'}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Monthly Cash Flow:' : 'Flujo Mensual:'}</span>
                    <span className={`ml-2 font-semibold ${financialTextColor(liveRental.monthlyCashFlow)}`}>
                      ${Math.round(liveRental.monthlyCashFlow).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">DSCR:</span>
                    <span className={`ml-2 font-semibold ${(liveRental.dscr) >= 1.25 ? 'text-success' : 'text-amber-600'}`}>
                      {(liveRental.dscr).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Cap Rate:' : 'Cap Rate:'}</span>
                    <span className="ml-2 font-semibold">{(liveRental.capRate).toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Criteria Passed:' : 'Criterios:'}</span>
                    <span className="ml-2 font-semibold">
                      {analysis.deal_decision?.criteria_passed || 0}/{analysis.deal_decision?.total_criteria || 4}
                    </span>
                  </div>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Adjust Property Details Section - NEW */}
        <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 md:p-8 mb-4 sm:mb-6" data-testid="property-details">
          <button
            onClick={() => setShowPropertyEdit(!showPropertyEdit)}
            className="w-full flex items-center justify-between text-left"
          >
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
                <Building2 className="w-6 h-6" />
                {t('prop_adjust_title')}
                {hasPropertyOverrides && (
                  overridesApplied ? (
                    <span className="px-2 py-0.5 bg-blue-100 text-secondary text-xs rounded-full font-medium"
                          data-testid="prop-overrides-applied">
                      {t('prop_user_adjusted')}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full font-medium"
                          data-testid="prop-overrides-pending">
                      {lang === 'en' ? 'Unapplied changes' : 'Cambios sin aplicar'}
                    </span>
                  )
                )}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t('prop_adjust_subtitle')}
              </p>
            </div>
            {showPropertyEdit ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
          
          {showPropertyEdit && (
            <div className="mt-6 space-y-6 animate-fade-in">
              {/* Original vs Current Data Notice */}
              {hasPropertyOverrides && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  {lang === 'en' 
                    ? 'You have made adjustments to the property details. The analysis will use your values.'
                    : 'Has realizado ajustes a los detalles de la propiedad. El análisis usará tus valores.'}
                </div>
              )}
              
              {/* Property Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* PURCHASE PRICE — hidden entirely on a REFINANCE. You already own the
                    property; there is no purchase and no price. This is the field that
                    kept appearing top-right on refi deals. */}
                {!isRefinance && (
                <div className="sm:col-span-2 lg:col-span-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4"
                     data-testid="rental-purchase-price-field">
                  <label className="block text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    {/* nosemgrep: purchase-price-must-be-refinance-aware -- whole field is gated on !isRefinance */}
                    {lang === 'en' ? 'Purchase Price' : 'Precio de Compra'}
                    <span className="text-xs font-normal text-blue-600">
                      ({lang === 'en' ? 'Changes trigger full recalculation' : 'Los cambios activan recálculo completo'})
                    </span>
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 font-bold">$</span>
                      <input
                        type="text"
                        value={editedPurchasePrice !== null 
                          ? editedPurchasePrice.toLocaleString() 
                          : (formatWholeDollars(analysis.purchase_price) || '')}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/[^0-9]/g, '');
                          const numValue = rawValue ? parseInt(rawValue, 10) : 0;
                          setEditedPurchasePrice(numValue);
                        }}
                        className="w-full pl-8 pr-4 py-3 text-lg font-semibold border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        placeholder="89,000"
                        data-testid="purchase-price-input"
                      />
                    </div>
                    {editedPurchasePrice !== null && editedPurchasePrice !== analysis.purchase_price && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            handlePropertyOverride('purchase_price', editedPurchasePrice);
                            // Also update the analysis purchase price directly
                            setAnalysis(prev => ({ ...prev, purchase_price: editedPurchasePrice }));
                            // ARV stays comp-derived; only the purchase price changes. Named
                            // option prevents the earlier arvOverride/purchasePriceOverride mixup.
                            recalculateAnalysis({ ...assumptions }, propertyOverrides, { purchasePriceOverride: editedPurchasePrice });
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-1"
                          data-testid="apply-purchase-price-btn"
                        >
                          <Check className="w-4 h-4" />
                          {lang === 'en' ? 'Apply' : 'Aplicar'}
                        </button>
                        <button
                          onClick={() => setEditedPurchasePrice(null)}
                          className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {editedPurchasePrice !== null && editedPurchasePrice !== analysis.purchase_price && (
                    <p className="mt-2 text-sm text-blue-600">
                      {lang === 'en' 
                        ? `Original: $${formatWholeDollars(analysis.purchase_price) || 0}` 
                        : `Original: $${formatWholeDollars(analysis.purchase_price) || 0}`}
                    </p>
                  )}
                </div>
                )}

                {/* Bedrooms */}
                <NumericInputWithButtons
                  value={propertyOverrides.beds ?? analysis.beds ?? ''}
                  onChange={(val) => handlePropertyOverride('beds', val)}
                  min={1}
                  max={10}
                  step={1}
                  label={t('prop_beds')}
                  originalValue={originalAnalysis?.beds}
                  testId="prop-beds-input"
                  lang={lang}
                />
                
                {/* Bathrooms */}
                <NumericInputWithButtons
                  value={propertyOverrides.baths ?? analysis.baths ?? ''}
                  onChange={(val) => handlePropertyOverride('baths', val)}
                  min={1}
                  max={10}
                  step={0.5}
                  label={t('prop_baths')}
                  originalValue={originalAnalysis?.baths}
                  testId="prop-baths-input"
                  lang={lang}
                />
                
                {/* Square Feet */}
                <NumericInputWithButtons
                  value={propertyOverrides.sqft ?? analysis.sqft ?? ''}
                  onChange={(val) => handlePropertyOverride('sqft', val)}
                  min={100}
                  max={50000}
                  step={100}
                  label={t('prop_sqft')}
                  originalValue={originalAnalysis?.sqft}
                  testId="prop-sqft-input"
                  lang={lang}
                  formatValue={(v) => v?.toLocaleString()}
                />
                
                {/* Year Built */}
                <NumericInputWithButtons
                  value={propertyOverrides.year_built ?? analysis.year_built ?? ''}
                  onChange={(val) => handlePropertyOverride('year_built', val)}
                  min={1800}
                  max={2030}
                  step={1}
                  label={t('prop_year_built')}
                  originalValue={originalAnalysis?.year_built}
                  testId="prop-year-input"
                  lang={lang}
                />
                
                {/* Lot Size input REMOVED. It wrote propertyOverrides.lot_size, and
                    applyPropertyOverrides never included lot_size in edited_subject — so
                    the field could be edited and changed nothing, anywhere. Removed rather
                    than wired up: no valuation path consumes a lot size, so "making it
                    work" would mean inventing a use for it first. The key stays in the
                    overrides object so a restored session carrying one still loads. */}
                
                {/* Property Type */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block flex items-center justify-between">
                    {t('prop_property_type')}
                    {/* REMOVED: Confusing "(Orig: ...)" display - we use SINGLE SOURCE OF TRUTH */}
                  </label>
                  <select
                    value={(() => {
                      // SINGLE SOURCE OF TRUTH: Use currentAnalysisState property_type
                      const current = currentAnalysisState?.property_type || 
                                      propertyOverrides.property_type || 
                                      analysis.property_snapshot?.property_type || 
                                      'Single Family';
                      // Normalize to option value
                      const normalized = current.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
                      return normalized;
                    })()}
                    onChange={(e) => handlePropertyOverride('property_type', e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm bg-white"
                    data-testid="prop-type-input"
                  >
                    <option value="single_family">{lang === 'en' ? 'Single Family' : 'Unifamiliar'}</option>
                    <option value="duplex">{lang === 'en' ? 'Duplex' : 'Dúplex'}</option>
                    <option value="triplex">{lang === 'en' ? 'Triplex' : 'Tríplex'}</option>
                    <option value="fourplex">{lang === 'en' ? 'Fourplex' : 'Cuádruplex'}</option>
                    <option value="multi_family">{lang === 'en' ? 'Multifamily (5+)' : 'Multifamiliar (5+)'}</option>
                    <option value="condo">{lang === 'en' ? 'Condo' : 'Condominio'}</option>
                    <option value="townhouse">{lang === 'en' ? 'Townhouse' : 'Casa Adosada'}</option>
                  </select>
                </div>
              </div>
              
              {/* Differences from the comparables — the SAME wording as the confirmation
                  screen, on purpose. The adjustment is a difference against the local
                  market, not an attribute of the house: in a market where nothing has a
                  basement, not having one deducts nothing. Wording that said "this
                  property has no basement" would invite the wrong tick. */}
              <div className="mt-6 p-4 rounded-xl border border-border bg-muted/30"
                   data-testid="results-feature-gap-section">
                <h3 className="text-sm font-bold text-foreground mb-1">
                  {lang === 'en'
                    ? 'Differences from the comparables nearby'
                    : 'Diferencias con los comparables de la zona'}
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  {lang === 'en'
                    ? 'Only tick these if homes selling around this one have the feature and this one does not. If none of them have it either, leave them unticked.'
                    : 'Marca solo si las casas que se venden alrededor tienen la característica y esta no. Si ninguna la tiene, déjalas sin marcar.'}
                </p>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={missingBasement !== null}
                    disabled={gapRecalculating}
                    onChange={(e) => applyFeatureGap(e.target.checked ? 'unfinished' : null, missingGarage)}
                    className="mt-0.5 w-4 h-4"
                    data-testid="results-missing-basement-checkbox"
                  />
                  <span className="text-sm text-foreground">
                    {lang === 'en'
                      ? 'The comparables nearby have a basement and this property does not'
                      : 'Los comparables de la zona tienen sótano y esta propiedad no'}
                  </span>
                </label>
                {missingBasement !== null && (
                  <div className="flex gap-2 mt-2 ml-6" data-testid="results-missing-basement-options">
                    {[{ v: 'unfinished', en: 'Unfinished', es: 'Sin terminar' },
                      { v: 'finished', en: 'Finished', es: 'Terminado' }].map(o => (
                      <button
                        key={o.v}
                        type="button"
                        disabled={gapRecalculating}
                        onClick={() => applyFeatureGap(o.v, missingGarage)}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition-all disabled:opacity-50 ${missingBasement === o.v ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                        data-testid={`results-missing-basement-${o.v}`}
                      >
                        {lang === 'en' ? o.en : o.es}
                      </button>
                    ))}
                  </div>
                )}

                <label className="flex items-start gap-2 cursor-pointer mt-4">
                  <input
                    type="checkbox"
                    checked={missingGarage !== null}
                    disabled={gapRecalculating}
                    onChange={(e) => applyFeatureGap(missingBasement, e.target.checked ? 1 : null)}
                    className="mt-0.5 w-4 h-4"
                    data-testid="results-missing-garage-checkbox"
                  />
                  <span className="text-sm text-foreground">
                    {lang === 'en'
                      ? 'The comparables nearby have a garage and this property does not'
                      : 'Los comparables de la zona tienen garaje y esta propiedad no'}
                  </span>
                </label>
                {missingGarage !== null && (
                  <div className="flex gap-2 mt-2 ml-6" data-testid="results-missing-garage-options">
                    {[{ v: 1, en: '1 space', es: '1 plaza' },
                      { v: 2, en: '2 spaces', es: '2 plazas' }].map(o => (
                      <button
                        key={o.v}
                        type="button"
                        disabled={gapRecalculating}
                        onClick={() => applyFeatureGap(missingBasement, o.v)}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition-all disabled:opacity-50 ${missingGarage === o.v ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                        data-testid={`results-missing-garage-${o.v}`}
                      >
                        {lang === 'en' ? o.en : o.es}
                      </button>
                    ))}
                  </div>
                )}

                {gapRecalculating && (
                  <div className="text-xs text-muted-foreground mt-3 flex items-center gap-2">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    {lang === 'en' ? 'Recalculating the value...' : 'Recalculando el valor...'}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                <button
                  onClick={applyPropertyOverrides}
                  disabled={recalculating}
                  className="px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-all text-sm"
                  data-testid="apply-property-changes"
                >
                  {recalculating ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  {lang === 'en' ? 'Apply Changes & Recalculate' : 'Aplicar Cambios y Recalcular'}
                </button>
                
                {hasPropertyOverrides && (
                  <button
                    onClick={resetPropertyOverrides}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 flex items-center gap-2 transition-all text-sm"
                    data-testid="reset-property-changes"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {t('prop_reset_to_original')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Removed the 0-100 Deal Score / Score Breakdown from investor-facing results.
            Investors act on profit, margin (profit ÷ cash invested), and the verdict
            (BUY/NEGOTIATE/AVOID) — not an abstract score. Score is still computed backend-
            side for analytics/PDF-internal/saved-deal ranking, just not shown here. */}

        {/* Deal Explanation - Hidden in valuation mode */}
        {!isValuationMode && (
        <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">
            {lang === 'en' ? 'Deal Analysis' : 'Análisis del Trato'}
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-slate-700 mb-6">
            {analysis.deal_explanation}
          </p>
          
          {analysis.opportunities && analysis.opportunities.length > 0 && (
            <div className="mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-success mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                {lang === 'en' ? 'Opportunities' : 'Oportunidades'}
              </h3>
              <ul className="space-y-2">
                {analysis.opportunities.map((opp, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-slate-700">
                    <span className="text-success mt-1">•</span>
                    {opp}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.risk_factors && analysis.risk_factors.length > 0 && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-warning mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {lang === 'en' ? 'Risk Factors' : 'Factores de Riesgo'}
              </h3>
              <ul className="space-y-2">
                {analysis.risk_factors.map((risk, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-slate-700">
                    <span className="text-warning mt-1">•</span>
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        )}

        {/* Flip Analysis - FLIP ONLY - Hidden in valuation mode */}
        {!isValuationMode && analysis.strategy === 'flip' && (
        <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4 sm:mb-6">
            {lang === 'en' ? 'Fix & Flip Analysis' : 'Análisis de Reventa'}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                {/* nosemgrep: purchase-price-must-be-refinance-aware -- FLIP-only section (strategy === 'flip') */}
                {lang === 'en' ? 'Purchase Price' : 'Precio de Compra'}
              </div>
              <div className="text-lg sm:text-xl font-bold">${formatWholeDollars(analysis.purchase_price)}</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                {lang === 'en' ? 'Rehab Cost' : 'Costo Remodelación'}
              </div>
              <div className="text-lg sm:text-xl font-bold">${formatWholeDollars(analysis.flip_analysis?.rehab_cost)}</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                {lang === 'en' ? 'Loan Costs' : 'Costos de Préstamo'}
              </div>
              <div className="text-lg sm:text-xl font-bold">${formatWholeDollars(ssotLoanCosts)}</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                {lang === 'en' ? 'Holding Costs' : 'Costos de Mantenimiento'}
              </div>
              <div className="text-lg sm:text-xl font-bold">${formatWholeDollars(analysis.flip_analysis?.holding_costs)}</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                {lang === 'en' ? 'Agent Commission' : 'Comisión Agente'}
              </div>
              <div className="text-lg sm:text-xl font-bold">${formatWholeDollars(analysis.flip_analysis?.agent_commission)}</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                {lang === 'en' ? 'Closing Costs' : 'Costos de Cierre'}
              </div>
              <div className="text-lg sm:text-xl font-bold">${formatWholeDollars(analysis.flip_analysis?.closing_costs)}</div>
            </div>
          </div>
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <div>
                <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                  {lang === 'en' ? 'Total Project Cost' : 'Costo Total del Proyecto'}
                </div>
                <div className="text-xl sm:text-2xl font-bold">${formatWholeDollars(analysis.flip_analysis?.total_project_cost)}</div>
              </div>
              <div>
                <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                  {lang === 'en' ? 'Net Profit' : 'Ganancia Neta'}
                </div>
                <div className={`text-xl sm:text-2xl font-bold ${financialTextColor(ssotNetProfit)}`}>${formatWholeDollars(ssotNetProfit)}</div>
              </div>
            </div>
          </div>
          
          {/* Fix & Flip Cash Invested Breakdown */}
          {analysis.flip_analysis && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-sm font-medium text-muted-foreground mb-2">
                {lang === 'en' ? 'Cash Invested Breakdown' : 'Desglose de Efectivo Invertido'}
              </div>
              {/* Every figure here is READ from flip_analysis. It used to be rebuilt in the
                  browser, and drifted four separate ways from the backend that computes it:
                  the full rehab was charged to the investor even when financed (+$105,582),
                  SALE-side closing costs were added although they come out of proceeds and
                  never out of pocket (+$6,109), holding costs were left out (-$4,626), and
                  the loan legs only appeared when the PURCHASE was financed, so a cash
                  purchase with a financed rehab hid $9,502 of real loan cost. On that
                  combination the card read $200,691 against a computed $103,128 — and printed
                  a Cash-on-Cash of -22.7% beside it, which is the ratio over the OTHER
                  number. Reading one source removes the whole class. */}
              <div className="flex flex-wrap gap-4 text-sm" data-testid="flip-cash-breakdown">
                <div>
                  <span className="text-muted-foreground">
                    {ssotFlipAnalysis.financing_structure?.purchase === 'cash'
                      ? (lang === 'en' ? 'Purchase (Cash):' : 'Compra (Efectivo):')
                      : (lang === 'en' ? 'Down Payment:' : 'Enganche:')}
                  </span>
                  <span className="ml-1 font-semibold" data-testid="cash-in-purchase">
                    ${formatWholeDollars(ssotFlipAnalysis.purchase_cash_required)}
                  </span>
                </div>
                {/* Absent, not zero, when the rehab is fully financed: nothing is paid for it
                    out of pocket, and a "$0" line invites the reader to look for the money. */}
                {ssotFlipAnalysis.rehab_cash_required > 0 && (
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Rehab (Cash):' : 'Remodelación (Efectivo):'}</span>
                    <span className="ml-1 font-semibold" data-testid="cash-in-rehab">
                      ${formatWholeDollars(ssotFlipAnalysis.rehab_cash_required)}
                    </span>
                  </div>
                )}
                {ssotFlipAnalysis.buyer_closing_costs > 0 && (
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Buyer Closing:' : 'Costos Cierre Compra:'}</span>
                    <span className="ml-1 font-semibold" data-testid="cash-in-buyer-closing">
                      ${formatWholeDollars(ssotFlipAnalysis.buyer_closing_costs)}
                    </span>
                  </div>
                )}
                {ssotFlipAnalysis.holding_costs > 0 && (
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Holding Costs:' : 'Costos de Mantenimiento:'}</span>
                    <span className="ml-1 font-semibold" data-testid="cash-in-holding">
                      ${formatWholeDollars(ssotFlipAnalysis.holding_costs)}
                    </span>
                  </div>
                )}
                {/* Not gated on the PURCHASE leg: a cash purchase with a financed rehab still
                    carries loan costs, and gating hid them entirely. */}
                {ssotLoanCosts > 0 && (
                  <div>
                    <span className="text-muted-foreground">{lang === 'en' ? 'Loan Costs:' : 'Costos de Préstamo:'}</span>
                    <span className="ml-1 font-semibold" data-testid="cash-in-loan">
                      ${formatWholeDollars(ssotLoanCosts)}
                    </span>
                  </div>
                )}
                <div className="font-semibold">
                  <span className="text-muted-foreground">{lang === 'en' ? 'Total Cash In:' : 'Total Efectivo:'}</span>
                  <span className="ml-1 text-primary" data-testid="cash-in-total">
                    ${formatWholeDollars(ssotFlipAnalysis.cash_invested)}
                  </span>
                </div>
                {/* Cash-on-Cash Return = net profit / cash invested — the LEVERAGED return,
                    shown separately from the headline Margin (which is profit/ARV, stable).
                    In financed deals this is legitimately high; labeled so it's never confused
                    with margin. */}
                {analysis.flip_analysis?.cash_on_cash != null && (
                  <div className="font-semibold" data-testid="flip-cash-on-cash">
                    <span className="text-muted-foreground">{lang === 'en' ? 'Cash-on-Cash Return:' : 'Retorno Efectivo:'}</span>
                    <span className="ml-1 text-emerald-600">{analysis.flip_analysis.cash_on_cash.toFixed(1)}%</span>
                    <span className="ml-1 text-xs text-slate-400">
                      {lang === 'en' ? '(profit ÷ cash in)' : '(ganancia ÷ efectivo)'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Rental Analysis - Hidden in valuation mode */}
        {!isValuationMode && analysis.strategy === 'rental' && (
        <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4 sm:mb-6 flex items-center gap-2">
            {lang === 'en' ? 'Rental Analysis' : 'Análisis de Renta'}
            {/* Show unit count badge for multi-family */}
            {analysis.rental_analysis?.unit_count > 1 && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                {analysis.rental_analysis.unit_count} {lang === 'en' ? 'Units' : 'Unidades'}
              </span>
            )}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                {analysis.rental_analysis?.unit_count > 1 
                  ? (lang === 'en' ? 'Total Monthly Rent' : 'Renta Mensual Total')
                  : (lang === 'en' ? 'Monthly Rent' : 'Renta Mensual')}
              </div>
              <div className="text-lg sm:text-xl font-bold">${formatWholeDollars(analysis.rental_analysis?.monthly_rent)}</div>
              {analysis.rental_analysis?.unit_count > 1 && analysis.rental_analysis?.rent_per_unit > 0 && (
                <div className="text-xs text-muted-foreground">
                  ${formatWholeDollars(analysis.rental_analysis?.rent_per_unit)}/{lang === 'en' ? 'unit' : 'unidad'}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                {lang === 'en' ? 'Monthly Cashflow' : 'Flujo Mensual'}
              </div>
              <div className={`text-lg sm:text-xl font-bold ${financialTextColor(liveRental.monthlyCashFlow)}`}>
                ${formatWholeDollars(liveRental.monthlyCashFlow)}
              </div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">Cap Rate</div>
              <div className="text-lg sm:text-xl font-bold">{liveRental.capRate?.toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground mb-1">Cash-on-Cash</div>
              <div className={`text-lg sm:text-xl font-bold ${liveRental.cashOnCash >= 8 ? 'text-success' : liveRental.cashOnCash >= 0 ? 'text-primary' : 'text-red-600'}`}>
                {liveRental.cashOnCash?.toFixed(1)}%
              </div>
            </div>
          </div>
          
          {/* Cash Invested Breakdown */}
          {analysis.rental_analysis?.cash_invested > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-sm font-medium text-muted-foreground mb-2">
                {lang === 'en' ? 'Cash Invested Breakdown' : 'Desglose de Efectivo Invertido'}
              </div>
              {/* Every line below reads the SAME backend result the total comes from, so the
                  pieces add up to the total by construction rather than by luck.
                  Three of them did not:
                    - Rehab printed analysis.rehab_estimate, the FULL estimate. What is
                      actually inside cash_invested is the condition-factored repair
                      allowance: 0 for turnkey/heavy (and 0 outright on the /analyze path),
                      30% for light, 60% for moderate. On a rental with a rehab estimate the
                      line therefore claimed money the total did not contain.
                    - Closing Costs read liveRental, the FRONTEND preview engine, which
                      reflects edits that are still staged and not yet recalculated. Beside
                      an applied total, that is a guaranteed disagreement whenever a draft
                      is open.
                    - Down Payment fell back to purchase_price x rentalDownPaymentPct when
                      the backend field was falsy — rebuilding backend arithmetic in the
                      view, which is the pattern this card is being cured of. */}
              <div className="flex flex-wrap gap-4 text-sm" data-testid="rental-cash-breakdown">
                <div>
                  {analysis.rental_analysis?.rental_purchase_type === 'refinancing' ? (
                    <>
                      <span className="text-muted-foreground">{lang === 'en' ? 'Cash Out:' : 'Cash Out:'}</span>
                      <span className="ml-1 font-semibold text-green-600">${formatWholeDollars(liveRental.refiCashOut)}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-muted-foreground">{lang === 'en' ? 'Down Payment:' : 'Enganche:'}</span>
                      <span className="ml-1 font-semibold" data-testid="rental-cash-down">${formatWholeDollars(analysis.rental_analysis?.down_payment_amount)}</span>
                    </>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">{lang === 'en' ? 'Closing Costs:' : 'Costos de Cierre:'}</span>
                  <span className="ml-1 font-semibold" data-testid="rental-cash-closing">${formatWholeDollars(analysis.rental_analysis?.closing_costs)}</span>
                </div>
                {analysis.rental_analysis?.repair_allowance > 0 && (
                <div>
                  <span className="text-muted-foreground">{lang === 'en' ? 'Rehab:' : 'Remodelación:'}</span>
                  <span className="ml-1 font-semibold" data-testid="rental-cash-rehab">${formatWholeDollars(analysis.rental_analysis?.repair_allowance)}</span>
                </div>
                )}
                <div className="font-semibold">
                  <span className="text-muted-foreground">{lang === 'en' ? 'Total Cash In:' : 'Total Efectivo:'}</span>
                  <span className="ml-1 text-primary" data-testid="rental-cash-total">${formatWholeDollars(analysis.rental_analysis?.cash_invested)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* ============ USAGE DISPLAY + ACTION BUTTONS (Moved to bottom) ============ */}
        <div className="bg-white rounded-2xl shadow-sm border border-border p-4 sm:p-6 mb-6">
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <Save className="w-5 h-5 text-secondary" />
            {lang === 'en' ? 'Actions' : 'Acciones'}
          </h3>
          
          {/* Usage Display */}
          {analysis.usage_info && (
            <div className="mb-4">
              <UsageDisplay usageInfo={analysis.usage_info} lang={lang} />
            </div>
          )}
          
          {/* Action buttons - with feature gating for Free users */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {/* SAVE - requires paid plan */}
            <button
              onClick={() => canUseFeature(user, 'save_deals') ? handleSaveWithAuth() : handleLockedFeature('save_deals', lang === 'en' ? 'Save Deals' : 'Guardar Tratos')}
              disabled={saving}
              className={`relative flex-1 sm:flex-none px-4 py-2 border rounded-lg font-medium flex items-center justify-center gap-2 transition-all text-sm ${
                canUseFeature(user, 'save_deals')
                  ? (hasUnsavedChanges ? 'bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-800' : 'bg-white border-border hover:bg-slate-50')
                  : 'bg-slate-100 border-slate-200 text-slate-500'
              }`}
              data-testid="save-btn"
            >
              {hasUnsavedChanges && canUseFeature(user, 'save_deals') && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500" />
              )}
              {canUseFeature(user, 'save_deals') ? <Save className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              {hasUnsavedChanges ? (lang === 'en' ? 'Save changes' : 'Guardar cambios') : t('action_save')}
            </button>
            
            {/* PDF PREVIEW - free for all */}
            <button
              onClick={handlePreviewPDFWithAuth}
              className="flex-1 sm:flex-none px-4 py-2 bg-gradient-to-r from-slate-700 to-slate-600 text-white rounded-lg font-medium hover:from-slate-800 hover:to-slate-700 flex items-center justify-center gap-2 transition-all text-sm shadow-sm"
              data-testid="preview-pdf-btn"
            >
              <FileText className="w-4 h-4" />
              {lang === 'en' ? 'Preview PDF' : 'Vista Previa PDF'}
            </button>
            
            {/* PDF DOWNLOAD - free for all */}
            <button
              onClick={handleDownloadPDFWithAuth}
              disabled={generatingPDF}
              className="flex-1 sm:flex-none px-4 py-2 bg-white border border-secondary text-secondary rounded-lg font-medium hover:bg-blue-50 disabled:opacity-50 flex items-center justify-center gap-2 transition-all text-sm"
              data-testid="download-pdf-btn"
            >
              {generatingPDF ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {lang === 'en' ? 'Download PDF' : 'Descargar PDF'}
            </button>
            
            {/* SEND REPORT - requires paid plan */}
            <button
              onClick={() => canUseFeature(user, 'save_deals') ? handleSendEmailWithAuth() : handleLockedFeature('save_deals', lang === 'en' ? 'Send Report' : 'Enviar Reporte')}
              className={`flex-1 sm:flex-none px-4 py-2 border rounded-lg font-medium flex items-center justify-center gap-2 transition-all text-sm ${
                canUseFeature(user, 'save_deals')
                  ? 'bg-white border-green-500 text-green-600 hover:bg-green-50'
                  : 'bg-slate-100 border-slate-200 text-slate-500'
              }`}
              data-testid="send-report-btn"
            >
              {canUseFeature(user, 'save_deals') ? <Mail className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              {lang === 'en' ? 'Send Report' : 'Enviar Reporte'}
            </button>
            
            {/* SHARE - requires paid plan */}
            <button
              onClick={() => canUseFeature(user, 'save_deals') ? handleShareWithAuth() : handleLockedFeature('save_deals', lang === 'en' ? 'Share' : 'Compartir')}
              className={`flex-1 sm:flex-none px-4 py-2 border rounded-lg font-medium flex items-center justify-center gap-2 transition-all text-sm ${
                canUseFeature(user, 'save_deals')
                  ? 'bg-white border-border hover:bg-slate-50'
                  : 'bg-slate-100 border-slate-200 text-slate-500'
              }`}
              data-testid="share-analysis-btn"
            >
              {canUseFeature(user, 'save_deals') ? <Link className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              {lang === 'en' ? 'Share' : 'Compartir'}
            </button>
            
            {/* COMPARE - requires paid plan */}
            <button
              onClick={() => {
                if (!canUseFeature(user, 'compare_deals')) {
                  handleLockedFeature('compare_deals', lang === 'en' ? 'Compare Deals' : 'Comparar Tratos');
                  return;
                }
                // CRITICAL: Save FULL current analysis state for return navigation
                const stateToSave = currentAnalysisState || analysis;
                if (stateToSave) {
                  sessionStorage.setItem('chekdeal_compare_return_state', JSON.stringify(stateToSave));
                  sessionStorage.setItem('chekdeal_last_results_id', stateToSave.analysis_id || 'unsaved');
                }
                navigate('/compare');
              }}
              className={`flex-1 sm:flex-none px-4 py-2 border rounded-lg font-medium flex items-center justify-center gap-2 transition-all text-sm ${
                canUseFeature(user, 'compare_deals')
                  ? 'bg-white border-border hover:bg-slate-50'
                  : 'bg-slate-100 border-slate-200 text-slate-500'
              }`}
            >
              {canUseFeature(user, 'compare_deals') ? <Share2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              {t('action_compare')}
            </button>
          </div>
          
          {/* Feature Locked Modal */}
          {showFeatureLockedModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-amber-100 rounded-full">
                    <Lock className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    {lang === 'en' ? 'Upgrade Required' : 'Actualización Requerida'}
                  </h3>
                </div>
                <p className="text-slate-600 mb-6">
                  {lang === 'en' 
                    ? `${lockedFeature?.displayName} is available on the Investor plan and above. Upgrade to unlock this feature.`
                    : `${lockedFeature?.displayName} está disponible en el plan Investor y superiores. Actualiza para desbloquear.`
                  }
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowFeatureLockedModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg font-medium hover:bg-slate-50"
                  >
                    {lang === 'en' ? 'Cancel' : 'Cancelar'}
                  </button>
                  <button
                    onClick={() => {
                      setShowFeatureLockedModal(false);
                      navigate('/pricing');
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                  >
                    {lang === 'en' ? 'View Plans' : 'Ver Planes'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CONVERSION: Contextual Upgrade Prompt */}
        {showUpgradePrompt && user && (
          <UpgradePrompt
            variant={upgradePromptVariant}
            usage={analysis?.usage_info}
            dealStats={dealStats}
            userId={user?.user_id}
            showValue={true}
            showDismiss={true}
            onDismiss={handleDismissUpgradePrompt}
            lang={lang}
          />
        )}

        {/* CTA - Strong conversion-focused call to action */}
        <div className="bg-gradient-to-r from-secondary to-blue-700 rounded-2xl shadow-lg p-6 sm:p-8 text-center text-white">
          <h3 className="text-xl sm:text-2xl font-bold mb-3">
            {lang === 'en' ? 'Find Your Next Profitable Deal' : 'Encuentra Tu Próximo Trato Rentable'}
          </h3>
          <p className="text-blue-100 mb-6 text-sm sm:text-base max-w-lg mx-auto">
            {lang === 'en' 
              ? 'Analyze another property before you risk your next investment. Every deal deserves this level of scrutiny.'
              : 'Analiza otra propiedad antes de arriesgar tu próxima inversión. Cada trato merece este nivel de escrutinio.'}
          </p>
          <button
            onClick={() => navigate('/analyze')}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-secondary rounded-lg font-bold hover:bg-slate-50 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2 text-lg"
            data-testid="analyze-another-btn"
          >
            {lang === 'en' ? 'Analyze Another Deal' : 'Analizar Otro Trato'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
        
        {/* V5.0: End of Full Analysis Section */}
        </>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in" data-testid="email-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-primary flex items-center gap-2">
                <Mail className="w-5 h-5 text-secondary" />
                {lang === 'en' ? 'Send Report via Email' : 'Enviar Reporte por Correo'}
              </h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-1 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              {lang === 'en' 
                ? 'Enter email addresses to send the PDF report. Separate multiple emails with commas.'
                : 'Ingresa direcciones de correo para enviar el reporte PDF. Separa múltiples correos con comas.'}
            </p>
            
            <div className="mb-4">
              <label className="text-sm font-medium text-primary mb-2 block">
                {lang === 'en' ? 'Email Address(es)' : 'Dirección(es) de Correo'}
              </label>
              <textarea
                value={emailAddresses}
                onChange={(e) => setEmailAddresses(e.target.value)}
                placeholder={lang === 'en' 
                  ? 'partner@example.com, lender@bank.com'
                  : 'socio@ejemplo.com, prestamista@banco.com'}
                className="w-full px-4 py-3 border-2 border-border rounded-lg focus:outline-none focus:border-secondary transition-colors resize-none"
                rows={3}
                data-testid="email-input"
              />
            </div>
            
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <div className="text-sm text-muted-foreground">
                <strong>{lang === 'en' ? 'Property:' : 'Propiedad:'}</strong> {analysis.address}
              </div>
              <div className="text-sm text-muted-foreground">
                {/* BUG 2 FIX: Show Estimated Value for valuation strategy instead of Decision.
                    GAP #2: when the ARV is honest-blocked, don't present a confident value. */}
                {analysis.strategy === 'valuation' ? (
                  <>
                    {/* The value is stated here too, with the caveat appended rather than
                        substituted for it — same rule as the ARV card and the hero. */}
                    <strong>{lang === 'en' ? 'Estimated Value:' : 'Valor Estimado:'}</strong>{' '}
                    {`$${(SSOT?.arv || analysis.arv || 0).toLocaleString()}`}
                    {arvBlocked && (
                      <span className="text-amber-700">
                        {lang === 'en'
                          ? ' — low confidence, add your comps for a stronger ARV'
                          : ' — baja confianza, agrega tus comps para un ARV más preciso'}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <strong>{lang === 'en' ? 'Decision:' : 'Decisión:'}</strong> {SSOT?.deal_classification?.decision_label || 'N/A'}
                  </>
                )}
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 px-4 py-2 border border-border rounded-lg font-medium hover:bg-slate-50 transition-all"
              >
                {lang === 'en' ? 'Cancel' : 'Cancelar'}
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailAddresses.trim()}
                className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                data-testid="send-email-btn"
              >
                {sendingEmail ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {lang === 'en' ? 'Send Report' : 'Enviar Reporte'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Investment Criteria Onboarding Modal */}
      <InvestmentCriteriaOnboarding
        isOpen={showCriteriaOnboarding}
        onClose={() => setShowCriteriaOnboarding(false)}
        onComplete={() => {
          // Reload criteria after completion
          investmentCriteriaAPI.getCriteria().then(response => {
            setInvestmentCriteria(response.data.criteria);
          });
        }}
        lang={lang}
      />
      
      {/* Share Analysis Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={lang === 'en' ? 'Share This Analysis' : 'Compartir Este Análisis'}
        subtitle={lang === 'en' 
          ? 'Share this property analysis with partners or investors'
          : 'Comparte este análisis de propiedad con socios o inversores'}
        shareUrl={currentShareUrl}
        lang={lang}
        // P0 FIX: Use getCanonicalDecision() for SINGLE SOURCE OF TRUTH
        currentState={{
          strategy: analysis.strategy,
          dealDecision: getCanonicalDecision(SSOT, lang).label,
          dealScore: ssotDealScore,
          netProfit: ssotNetProfit,
          loanCosts: ssotLoanCosts,
          totalProjectCost: ssotFlipAnalysis.total_project_cost || 0,
          arv: SSOT?.arv || analysis.arv,
          arvConfidence: SSOT?.arv_confidence || analysis.arv_confidence,
          potentialEquity: (SSOT?.arv || analysis.arv || 0) - (SSOT?.purchase_price || analysis.purchase_price || 0),
          purchasePrice: SSOT?.purchase_price || analysis.purchase_price,
          propertyType: SSOT?.property_type || analysis.property_type,
          beds: SSOT?.beds || analysis.beds,
          baths: SSOT?.baths || analysis.baths,
          financingMode: assumptions.purchaseFinancing,
          // Rental fields (0 for non-rental strategies, read by rental branch only)
          monthlyCashflow: analysis?.rental_analysis?.monthly_cashflow || 0,
          capRate: analysis?.rental_analysis?.cap_rate || 0,
          dscr: analysis?.rental_analysis?.dscr || 0,
          cashInvested: analysis?.rental_analysis?.cash_invested || 0,
        }}
        onSendEmail={async (email, message) => {
          // CRITICAL: Use share snapshot ID, NOT original analysis_id
          if (!currentShareId) {
            toast.error(lang === 'en' ? 'Share link not ready. Please try again.' : 'Enlace no listo. Inténtalo de nuevo.');
            return;
          }
          await shareAPI.shareAnalysis({
            recipient_email: email,
            analysis_id: currentShareId, // FIXED: Use share snapshot ID
            property_address: SSOT?.address || analysis.address,
            personal_message: message,
            language: lang,
            analysis_summary: {
              arv: SSOT?.arv || analysis.arv,
              purchase_price: SSOT?.purchase_price || analysis.purchase_price,
              net_profit: ssotNetProfit,
              loan_costs: ssotLoanCosts,
              deal_rating: ssotDealRating,
              deal_score: ssotDealScore,
              property_type: SSOT?.property_type || analysis.property_type,
              beds: SSOT?.beds || analysis.beds,
              baths: SSOT?.baths || analysis.baths,
              sqft: SSOT?.sqft || analysis.sqft,
              financing_mode: assumptions.purchaseFinancing,
              total_project_cost: ssotFlipAnalysis.total_project_cost || 0,
            }
          });
        }}
      />
      
      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={showPDFPreview}
        onClose={() => setShowPDFPreview(false)}
        analysis={currentAnalysisState || analysis}  // SINGLE SOURCE OF TRUTH
        lang={lang}
        maoRule={assumptions.maoRule}
        onDownloadPDF={handleDownloadPDF}
        onSendEmail={() => {
          setShowPDFPreview(false);
          setShowEmailModal(true);
        }}
      />
      
      {/* Auth Prompt Modal for Gated Features */}
      {showAuthPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="auth-prompt-modal">
          <div className="relative">
            <button
              onClick={() => {
                setShowAuthPrompt(false);
                setPendingAction(null);
              }}
              className="absolute -top-2 -right-2 p-2 bg-white rounded-full shadow-lg text-slate-500 hover:text-slate-700 z-10"
              data-testid="close-auth-prompt"
            >
              <X className="w-5 h-5" />
            </button>
            <AuthPrompt
              icon={Save}
              title={lang === 'en' ? 'Sign In to Continue' : 'Inicia Sesión para Continuar'}
              description={
                lang === 'en'
                  ? 'Create a free account or sign in to save deals, generate reports, and share analyses.'
                  : 'Crea una cuenta gratuita o inicia sesión para guardar tratos, generar reportes y compartir análisis.'
              }
              lang={lang}
              redirectAfterAuth={`/results`}
            />
          </div>
        </div>
      )}
      
      {/* Feedback Prompt Modal */}
      {showFeedbackPrompt && (
        <FeedbackPrompt
          userId={user?.user_id}
          lang={lang}
          onDismiss={() => {
            setShowFeedbackPrompt(false);
            localStorage.setItem('chekdeal_feedback_dismissed', 'true');
          }}
          onSubmit={(response) => {
            setShowFeedbackPrompt(false);
            localStorage.setItem('chekdeal_feedback_submitted', 'true');
          }}
        />
      )}
    </div>
  );
};

export default Results;
